import {
  useMutation,
  useQuery,
  useSuspenseQuery,
  useInfiniteQuery,
  useSuspenseInfiniteQuery,
  UseMutationOptions,
  UseQueryOptions,
  UseSuspenseQueryOptions,
  UseInfiniteQueryOptions,
  InfiniteData,
  UseSuspenseInfiniteQueryOptions,
} from "@tanstack/react-query"

import * as Types from "./admin-types"
import { adminFetcher } from "./fetcher"

export const AiExtractionResultFragmentDoc = `
    fragment AIExtractionResult on AIExtractionResult {
  id
  extractionKind
  modelName
  schemaVersion
  dateCreated
  analysis {
    name
    description
    features {
      name
      description
      dataFields {
        id
        label
        value
        type
      }
      boundingBoxes {
        pageNumber
        left
        top
        right
        bottom
      }
    }
  }
}
    `
export const SiteRefFragmentDoc = `
    fragment SiteRef on SiteRef {
  pid
  name
  streetAddress
}
    `
export const ScenarioRefFragmentDoc = `
    fragment ScenarioRef on ScenarioRef {
  pid
  name
}
    `
export const FinmodelSourceFragmentFragmentDoc = `
    fragment FinmodelSourceFragment on FinmodelSource {
  __typename
  ... on PVoptRef {
    id
  }
  ... on LuxRef {
    id
  }
  ... on ReoptRef {
    pid
  }
  ... on ETBReportRef {
    id
    projectName
  }
}
    `
export const FinmodelFailureDetailFragmentDoc = `
    fragment FinmodelFailureDetail on FinmodelFailure {
  id
  createdAt
  fmv
  name
  message
  isPvopt
  isLux
  site {
    ...SiteRef
  }
  scenario {
    ...ScenarioRef
  }
  source {
    ...FinmodelSourceFragment
  }
}
    `
export const ResultRefFragmentDoc = `
    fragment ResultRef on Result {
  id
  pid
  createdAt
  scenario {
    pid
    name
    solarLocation
    loadCategory
    includeStorage
  }
  financing {
    id
    name
    type
    financierName
    financierPid
    financierLogo
    financierLogomark
    isThirdParty
    status
  }
}
    `
export const KwhFlowFragmentDoc = `
    fragment KwhFlow on KwhFlow {
  bauLoadKwh
  importsKwh
  exportsKwh
  pvProductionKwh
  pvToLoadKwh
  batteryChargeKwh
  batteryDischargeKwh
}
    `
export const RevampSystemFragmentDoc = `
    fragment RevampSystem on RevampSystem {
  __typename
  ... on PVSystem {
    type
    sizeKw
    bauMeterId
    sitePid
  }
  ... on BatterySystem {
    vendor
    sizeKw
    sizeKwh
    assumptions {
      battChemName
    }
  }
}
    `
export const FinmodelCostFragmentDoc = `
    fragment FinmodelCost on FinmodelCost {
  totalKwh
  totalCostUsd
  usdPerKwh
  items {
    name
    usd
  }
  tariffs {
    utility {
      id
      name
      shortName
    }
    id
    name
    code
    startDate
    endDate
  }
}
    `
export const RevampFinmodelFragmentDoc = `
    fragment RevampFinmodel on RevampFinmodel {
  __typename
  id
  pid
  createdAt
  type
  intervalsAndCostsXlsxHref
  buildingOwnerXlsxHref
  offerStatus
  fmvStr
  totalPvProductionKwh
  totalPvExportKwh
  bauCostPerYear
  derCostPerYear
  goal
  isVnem
  isGreenLease
  thirdPartySupplier {
    pid
    name
  }
  buildingOwner {
    noiYearOne
    noiLifetime
    noiAnnual
  }
  ppa {
    __typename
    ppaType
    ppaCostPerKwh
    ppaTermYears
    ppaEsc
    srecToInvestor
    offtakerXlsxHref
    product {
      productName
      financierName
    }
    roofRent {
      lifetimeUsd
      annualUsd
      usdPerSqft
      roofRentPerYear
    }
    tenantFlows {
      npv
      discountRate
      totalUtilitySavings
      totalCapex
      totalOpex
      totalNet
      netCashflowPerYear
    }
  }
  loan {
    __typename
    loanRecvrXlsxHref
    loanTermYears
    ownershipTermYears
    interestPct
    srecToInvestor
    product {
      productName
      financierName
      loanDealerFeePct
    }
    flows {
      monthlyPayment
      totalRevenue
      totalUtilitySavings
      totalOpex
      totalCapex
      totalItc
      totalLoanFunding
      totalLoanPayments
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalCapex
        totalOpex
        totalUtilitySavings
        npv
        discountRate
      }
    }
  }
  selfInvest {
    __typename
    npv
    discountRate
    selfInvestXlsxHref
    termYears
    srecToInvestor
    irrs {
      postTaxItcDepr
      postTaxItcDeprMultiple
    }
    product {
      productName
      financierName
    }
    flows {
      totalRevenue
      totalUtilitySavings
      totalOpex
      totalCapex
      totalItc
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalCapex
        totalOpex
        totalUtilitySavings
        npv
        discountRate
      }
    }
  }
  meters {
    name
    loadCategory
    meterSource
    bau {
      totalKwhFlow {
        ...KwhFlow
      }
    }
    der {
      totalKwhFlow {
        ...KwhFlow
      }
      averageDayKwhFlow {
        ...KwhFlow
      }
    }
  }
  systems {
    ...RevampSystem
  }
  utilityCosts {
    bau {
      ...FinmodelCost
    }
    der {
      ...FinmodelCost
    }
  }
}
    `
export const RevampCsFragmentDoc = `
    fragment RevampCS on RevampCS {
  __typename
  id
  termYears
  createdAt
  goal
  systems {
    ...RevampSystem
  }
  financing {
    id
    name
    type
    financierName
    financierPid
    financierLogo
    financierLogomark
    isThirdParty
  }
  buildingOwner {
    noiYearOne
    noiLifetime
    noiAnnual
  }
  roofRent {
    lifetimeUsd
    annualUsd
    usdPerSqft
    npv
    discountRate
    roofRentPerYear
  }
  pricing {
    utility {
      id
      name
    }
    usdPerKwPerYear
    escalator
    minPvKw
    maxPvKw
  }
  leaseProjections {
    systemSizeKw
    annualRentMinimumUsd
    annualRentLowerRangeUsd
    annualRentMedianUsd
    annualRentUpperRangeUsd
    annualRentMaximumUsd
    annualRentP10Usd
    annualRentP90Usd
    avgAwardedAnnualRentUsd
    methodology
  }
}
    `
export const HelioscopeFragmentDoc = `
    fragment Helioscope on Helioscope {
  pid
  roofPvKw
  carportPvKw
  groundPvKw
  systemProductivityKwhPerKw
  roofImage {
    id
    name
    downloadHref
  }
  pdf {
    downloadHref
    contentType
    name
    id
  }
  roofImageHref
  roofImageName
  pdfHref
  helioscopeKw
  dcNameplateKw
  acNameplateKw
  loadRatio
  moduleType
  inverterType
  rackingTiltDegrees
  rackingManufacturer
  rackingConnectionType
  notes
}
    `
export const ResultFragmentDoc = `
    fragment Result on Result {
  id
  pid
  createdAt
  salesPdfReportHref
  scenario {
    pid
    name
    solarLocation
    loadCategory
    includeStorage
  }
  financing {
    id
    name
    type
    financierName
    financierPid
    financierLogo
    financierLogomark
    isThirdParty
    status
  }
  result {
    __typename
    ... on PhysicalCheck {
      createdAt
      roofIsGood
      carportIsGood
      existingSolar
      badRoof
      groundmountIsGood
    }
    ... on PolicyCheck {
      createdAt
      productType
      isGood
      disqualReason
    }
    ... on RevampFinmodelFailure {
      id
      createdAt
      fmvStr
      details {
        label
        description
      }
    }
    ... on RevampFinmodel {
      ...RevampFinmodel
      iterations {
        cashPurchaseNpv
        pvKw
        marginalCo2Averted
        storagePvKw
        storagePvKwh
      }
      meters {
        loadCategory
        meterSource
        bau {
          totalKwhFlow {
            ...KwhFlow
          }
        }
        der {
          totalKwhFlow {
            ...KwhFlow
          }
          averageDayKwhFlow {
            ...KwhFlow
          }
        }
      }
      emissions {
        bauCo2Tons
        derCo2Tons
        bauAverageDayCo2Tons
        derAverageDayCo2Tons
      }
    }
    ... on RevampCS {
      ...RevampCS
    }
    ... on Viability {
      isAllowed
      viabilityType: type
      reason
      level
    }
  }
  helioscope {
    ...Helioscope
  }
}
    `
export const ResultWithEnergyDataFragmentDoc = `
    fragment ResultWithEnergyData on Result {
  id
  result {
    ... on RevampFinmodel {
      meters {
        loadCategory
        meterSource
        bau {
          totalKwhFlow {
            ...KwhFlow
          }
        }
        der {
          totalKwhFlow {
            ...KwhFlow
          }
          averageDayKwhFlow {
            ...KwhFlow
          }
        }
      }
      emissions {
        bauCo2Tons
        derCo2Tons
        bauAverageDayCo2Tons
        derAverageDayCo2Tons
      }
    }
  }
}
    `
export const DecodedRateFragmentDoc = `
    fragment DecodedRate on DecodedRate {
  id
  name
  chargeClass
}
    `
export const CostOrTaxItemFragmentDoc = `
    fragment CostOrTaxItem on CostItemTaxItem {
  __typename
  ... on CostItem {
    rateObj {
      ...DecodedRate
    }
    name
    rateId
    chargeType
    chargeCategory
    volume
    rate
    cost
  }
  ... on TaxItem {
    taxId
    subtotal
    cost
    percentage
  }
}
    `
export const BillSectionFragmentDoc = `
    fragment BillSection on BillSection {
  name
  rates {
    ...DecodedRate
  }
  costs {
    ...CostOrTaxItem
  }
}
    `
export const FormattedBillFragmentDoc = `
    fragment FormattedBill on FormattedBills {
  rate {
    tariffId
    startDate
    endDate
  }
  bills {
    month
    charges {
      ...BillSection
    }
    exports {
      ...BillSection
    }
    kwhSupplyCharge
    kwhCharge
  }
}
    `
export const BidRevampSiteFragmentDoc = `
    fragment BidRevampSite on RevampSite {
  pid
  name
  streetAddress
  city
  state
  zipCode
  county
  fullAddress
  assetManager
  buildingType
  meterSet {
    totalCost
    loadKwh
  }
  yearBuilt
  numStories
  sqft
  roofSqft
  siteDetails {
    salePlans
    roofCondition
    roofReplacedYr
  }
  roofMaxPvKw
  buildingClass
  targetUtility {
    name
    pid
    id
  }
  ppaType
}
    `
export const UserRefFragmentDoc = `
    fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
}
    `
export const SiteConfigFragmentDoc = `
    fragment SiteConfig on SiteConfig {
  createdAt
  createdBy {
    ...UserRef
  }
  scenario {
    pid
    name
    loadCategory
    solarLocation
    includeStorage
  }
  financing {
    id
    name
    type
    financierName
    financierPid
    financierLogo
    financierLogomark
    isThirdParty
  }
}
    `
export const FootprintFragmentDoc = `
    fragment Footprint on Footprint {
  latitude
  longitude
  bounds
  obstructionShape
  roofShape
  roofShapeGeojson
  roofShapeUpdatedAt
  roofShapeUpdatedBy {
    ...UserRef
  }
  roofSetbackShape
  roofSetbackShapeGeojson
  roofSetbackShapeUpdatedAt
  roofSetbackShapeUpdatedBy {
    ...UserRef
  }
  carportShape
  carportShapeGeojson
  carportShapeUpdatedAt
  carportShapeUpdatedBy {
    ...UserRef
  }
  groundmountShape
  groundmountShapeGeojson
  groundmountShapeUpdatedAt
  groundmountShapeUpdatedBy {
    ...UserRef
  }
  keepoutsShape
  keepoutsShapeGeojson
  keepoutsShapeUpdatedAt
  keepoutsShapeUpdatedBy {
    ...UserRef
  }
  batteryPadShape
  batteryPadShapeGeojson
  batteryPadShapeUpdatedAt
  batteryPadShapeUpdatedBy {
    ...UserRef
  }
}
    `
export const RevampSiteBasicsFragmentDoc = `
    fragment RevampSiteBasics on RevampSite {
  pid
  name
  isBehindTheMeter
  marketplaceSolarLocations
  marketplaceIncludeStorage
  isEnergyCommunity
  isLowIncomeCommunity
  isMultifamily
  streetAddress
  city
  state
  zipCode
  county
  fullAddress
  metering
  assetManager
  vnemAllowed
  buildingType
  usingBillingMeters
  numParkingSpaces
  selectedConfig {
    ...SiteConfig
  }
  documents {
    pid
    downloadHref
    fileName
    tag
  }
  energyThings {
    canRooftopSolar
    canCarportSolar
    canGroundmountSolar
    canStorage
  }
  financingThings {
    canSelfInvest
    canLoan
    canPpa
    canCommunitySolar
  }
  physicalCheck {
    existingSolar
    unobstructedFlatRoof
    badRoof
  }
  siteDetails {
    leaseApprovers
    externalLeaseApprovers
    needsTenantApproval
    roofRightsHolder
    propertyLenders
    propertyInsurer
    constructionEmail
    propertySiteVisitContacts
    leasePropertyLegalDescription
    leaseLegalOwnerName
    signatureBlocks
    isFannieMaeLender
    isFmGlobalInsurer
    creditRating
    roofReplacedYr
    roofCondition
    salePlans
    buildingLegalName
    propertyFundName
    leaseSignatory
    counselApproval
  }
  yearBuilt
  numStories
  sqft
  roofSqft
  usableRoofSqft
  usableCarportFt
  impliedCarportLinearFt
  impliedUsableRoofSqft
  impliedUsableRoofSqftExplanation
  groundAvailableSqft
  hideBuildingFootprint
  keepoutsFootprintSqft
  batteryPadFootprintSqft
  roofMaxPvKw
  carportMaxPvKw
  groundmountMaxPvKw
  maxPvKwPolicyLimited
  isMultifamily
  occupancyType
  leaseType
  tenantLeaseTerm
  tenantLeaseEnds
  tenantInterest
  mortgageLender
  notes {
    text
  }
  buildingClass
  buildingData {
    numStories
    numUnits
    yearBuilt
    sqft
    roofSqft
    numParkingSpaces
    occupancyRate
    totalBuildings
    yearRenovated
    parentCompany
    lastSalePrice
    leedCertified
    energyStar
    buildingOpex
    capRate
  }
  footprint {
    ...Footprint
  }
  thirdPartySupplier {
    pid
    name
  }
  meterSet {
    loadOrigin
  }
  targetUtility {
    name
    pid
    id
    eiaGridEsc {
      cagrBlended
      fromYear
      toYear
    }
    hostingCapacityMaps {
      url
      requiresLogin
      types
    }
  }
  hasSubmittedEnergyData
}
    `
export const AdminUserRefFragmentDoc = `
    fragment AdminUserRef on AdminUser {
  id
  pid
  email
  name
  isAdmin
  avatarUrl
}
    `
export const SiteVisualsFragmentDoc = `
    fragment SiteVisuals on SiteVisuals {
  solarPanels {
    latitude
    longitude
    angle
  }
}
    `
export const BasicEvCalcFragmentDoc = `
    fragment BasicEVCalc on EVCalc {
  id
  createdAt
  projectInputs {
    projectStartDate
    installType
    termYears
    chargerKw
    yr0NumTenantEvs
    discountRate
  }
  outputs {
    irr
    npv
    yr0NumTenantEvs
    breakevenYear
    numChargersDemandedSeries
    cashflowSeries
    revenueSeries
    incentiveUsd
  }
  numChargers
  optimalNumChargers
  npv
  markupUsdPerKwh
  tariffRate {
    parent {
      name
      code
    }
  }
  utility {
    shortName
  }
  timeZone
  dayRates
  seasonalDayRates {
    name
    startMonth
    endMonth
    dayRates
  }
  downloadHourlyRatesHref
  xlsx
  downloadXlsxHref
  optIters {
    numChargers
    npv
  }
  yr3Revenue
  yr3Cashflow
  yr3NumEvs
  parkingRatio
  year3Expenses {
    label
    value
  }
  installCosts {
    label
    value
  }
}
    `
export const DocumentFragmentDoc = `
    fragment Document on Document {
  id
  pid
  contentType
  dateCreated
  fileName
  ownerName
  ownerEmail
  downloadHref
  viewHref
  tag
}
    `
export const DocumentAnalysisRequestFragmentDoc = `
    fragment DocumentAnalysisRequest on DocumentAnalysisRequest {
  id
  createdAt
  status
  note
  errorClass
  errorMessage
}
    `
export const BauTariffFragmentDoc = `
    fragment BauTariff on Tariff {
  id
  name
  code
  masterTariffId
}
    `
export const BauMeterFragmentDoc = `
    fragment BauMeter on BauMeter {
  id
  createdAt
  createdBy {
    email
  }
  label
  loadOrigin
  loadCategory
  meterSource
  utility {
    id
    name
  }
  tariff {
    ...BauTariff
  }
  tariffSetBy {
    pid
    email
  }
  underHumanControl
  tariffsUnderHumanControl
  postSolarTariff {
    ...BauTariff
  }
  postSolarTariffSetBy {
    pid
    email
  }
  postBatteryTariff {
    ...BauTariff
  }
  postBatteryTariffSetBy {
    pid
    email
  }
  archivedAt
  archivedBy {
    pid
    email
  }
  archivedReason
  rollup {
    season
    days
    numbers
  }
}
    `
export const BauMeterIntervalsFragmentDoc = `
    fragment BauMeterIntervals on BauMeter {
  id
  tariffUsdPerKwh
  rollup {
    season
    days
    numbers
  }
  intervals {
    units
    year
    timeZone
    maxKw
    totalKwh
    kwh8760
  }
}
    `
export const SitesTableFragmentDoc = `
    fragment SitesTable on Site {
  pid
  propertyName
  fullAddress
  state
  buildingType
  sqft
  roofSqft
  occupancyType
  leaseType
  metering
  mortgageLender
  roofReplacedYr
  hiddenFromCustomers
  portfolio {
    pid
    title
    org {
      slug
    }
  }
  roofImage {
    id
  }
  roofMaxPvKw
  carportMaxPvKw
  primaryScenario {
    pid
    solarLocation
    includeStorage
  }
  currentStatus {
    pid
    status
  }
  createdAt
}
    `
export const ReonomyContactFragmentDoc = `
    fragment ReonomyContact on ReonomyContact {
  firstName
  lastName
  company
  jobTitle
  emailAddresses
  phoneNumbers
  fullAddresses
}
    `
export const UserListUserFragmentDoc = `
    fragment UserListUser on AdminUser {
  id
  pid
  email
  name
  affiliation
  isAdmin
  isEnabled
  avatarUrl
  meetingSchedulingUrl
  portfoliosWithRole {
    pid
    title
  }
  unclaimedInvitations {
    isClaimed
    isExpired
    expiresAt
    portfolio {
      pid
      title
    }
  }
  licenseAgreements {
    forOrg {
      pid
      name
    }
    acceptedAt
  }
  org {
    pid
    name
    slug
  }
  lastSeenAt
}
    `
export const ChangelogFragmentDoc = `
    fragment Changelog on Changelog {
  field
  updatedAt
  updatedBy {
    ...UserRef
  }
}
    `
export const RuleValueFragmentDoc = `
    fragment RuleValue on RuleValue {
  __typename
  ... on YesNoValue {
    yes
  }
  ... on NumberValue {
    number
  }
  ... on CurrencyValue {
    usd
  }
  ... on IntegerValue {
    integer
  }
  ... on TextValue {
    text
  }
}
    `
export const PolicyConditionFragmentDoc = `
    fragment PolicyCondition on PolicyCondition {
  __typename
  ... on BuildingSuperTypeCondition {
    buildingSuperType
  }
  ... on VnemEligibilityCondition {
    vnemEligibilityRequired
  }
}
    `
export const RuleFragmentDoc = `
    fragment Rule on Rule {
  id
  description
  units
  value {
    ...RuleValue
  }
  application {
    status
    detail
  }
  conditions {
    ...PolicyCondition
  }
}
    `
export const PolicyListItemFragmentDoc = `
    fragment PolicyListItem on Policy {
  pid
  name
  jurisdiction {
    scope
    name
  }
  status
  startDate
  endDate
  userVisible
  application {
    status
  }
  rules {
    ...Rule
  }
}
    `
export const OverrideFragmentDoc = `
    fragment Override on FinmodelInputOverride {
  id
  dateCreated
  dateTrashed
  inputName
  inputValue
  source
  createdBy
}
    `
export const FinRelFragmentDoc = `
    fragment FinRel on AdminFinRel {
  disabledAt
  finproduct {
    id
    type
    name
    financier {
      name
      pid
    }
  }
  recvr {
    pid
  }
}
    `
export const AttachmentFragmentFragmentDoc = `
    fragment AttachmentFragment on Attachment {
  id
  size
  name
  contentType
  downloadHref
  createdAt
  trashedAt
  isTrashable
  isRoofImageCandidate
  isEtb
  createdBy {
    ...UserRef
  }
}
    `
export const ScenarioHelioscopeFragmentFragmentDoc = `
    fragment ScenarioHelioscopeFragment on Helioscope {
  id
  pid
  createdAt
  isTrashed
  roofPvKw
  carportPvKw
  pdf {
    ...AttachmentFragment
  }
  roofImage {
    ...AttachmentFragment
  }
}
    `
export const ScenarioEtbFragmentFragmentDoc = `
    fragment ScenarioEtbFragment on ETBReport {
  id
  dateCreated
  projectName
  roofPvKw
  carportPvKw
  groundPvKw
  batteryKw
  batteryKwh
}
    `
export const TariffDetailFragmentFragmentDoc = `
    fragment TariffDetailFragment on TariffDetails {
  tariff {
    id
    code
    name
    customerClass
  }
  version {
    tariffId
    effectiveDate
    endDate
  }
  properties {
    keyName
    dataType
    propertyValue
    formulaDetail
  }
  rawJson
  rates {
    tariffRateId
    name
    classes
    chargeClass
    chargeType
    chargePeriod
    transactionType
    applicabilityKey
    quantityKey
    isDemandCharge
    isFixedPrice
    isQuantity
    isTou
    variableLimitValue {
      keyName
      formulaDetail
    }
    bands {
      tariffRateBandId
      rateUnit
      price
      isCredit
      hasConsumptionLimit
      propertyUpperLimit
      consumptionUpperLimit
      demandUpperLimit
      calculationFactor
      applicabilityValue
    }
    season {
      seasonFromMonth
      seasonToMonth
      seasonFromDay
      seasonToDay
    }
    tou {
      touName
      season {
        seasonFromMonth
        seasonToMonth
        seasonFromDay
        seasonToDay
      }
      touPeriods {
        fromDayOfWeek
        toDayOfWeek
        fromHour
        toHour
        fromMinute
        toMinute
      }
    }
  }
}
    `
export const MeteringFragmentDoc = `
    fragment Metering on Site {
  metering
  meteringSetDate
  meteringSetBy {
    ...UserRef
  }
}
    `
export const EtbScenarioRefFragmentDoc = `
    fragment ETBScenarioRef on ETBScenarioRef {
  pid
  name
  isPrimary
  solarLocation
  includeStorage
  loadCategory
}
    `
export const CostsFragmentFragmentDoc = `
    fragment CostsFragment on ETBCosts {
  totalCost
  totalIncentiveSavings
  netCost
  energyConsumptionKwhYear1
  netEnergyConsumptionKwhYear1
  utilityCostPerKwh
  averageLoadFactor
  energyCostBefore
  energyCostAfter
  energyCostSavings
  demandCostBefore
  demandCostAfter
  demandCostSavings
  totalCostBefore
  totalCostAfter
  totalCostSavings
  pvTotalCost
  pvNetCost
  pvPowerRatingKwDc
  pvPowerRatingKwAc
  pvDegradationRate
  pvGenerationKwhYear1
  pvKwhPerKwDc
  pvSavingsPerKwh
  pvGenerationPercentExported
  storageTotalCost
  storageNetCost
  storageCapacityKwh
  storageMaxDischargePowerKw
  storageTwoHourPowerRatingKw
  storageDegradationRate
  storageDischargeYear1
  storageConsumptionKwhYear1
  storageSavingsPerKw
  storageSavingsPerKwh
}
    `
export const PvConfigFragmentDoc = `
    fragment PVConfig on PVConfig {
  pvType
  origin
  sizeKw
  productionKwh
}
    `
export const BatteryConfigFragmentDoc = `
    fragment BatteryConfig on BatteryConfig {
  vendor
  sizeKwh
  sizeKw
  chargeStrategy
  dischargeStrategy
}
    `
export const SlimLuxFragmentDoc = `
    fragment SlimLux on Lux {
  id
  etbReport {
    id
  }
  createdAt
  fmvStr
  goal
  maxOffsetPct
  pvKw
  roofPvKw
  carportPvThreeHighKw
  carportPvSixHighKw
  battKw
  battKwh
  meterSet {
    id
  }
  meters {
    bauMeter {
      id
    }
    postSolarMeter {
      pv {
        ...PVConfig
      }
    }
    postBatteryMeter {
      batt {
        ...BatteryConfig
      }
    }
  }
}
    `
export const CostSummaryFragmentDoc = `
    fragment CostSummary on CostSummary {
  totalCost
  totalEnergyUsage
  energyCost
  demandCost
  fixedCost
  nbcCost
  exportBenefit
}
    `
export const LuxMeterFragmentDoc = `
    fragment LuxMeter on LuxMeter {
  id
  gridMeter {
    preSolar {
      costSummary {
        ...CostSummary
      }
    }
    postSolar {
      costSummary {
        ...CostSummary
      }
    }
    postBattery {
      costSummary {
        ...CostSummary
      }
    }
  }
  dayIntervals {
    pvToBldg
    battDischarge
    gridToBldg
  }
  bauMeter {
    id
    label
    importsKwh
    tariff {
      name
      code
    }
  }
  postSolarMeter {
    pv {
      ...PVConfig
    }
    tariff {
      parent {
        name
        code
      }
    }
  }
  postBatteryMeter {
    batt {
      ...BatteryConfig
    }
    tariff {
      parent {
        name
        code
      }
    }
  }
}
    `
export const LuxFragmentDoc = `
    fragment Lux on Lux {
  ...SlimLux
  meters {
    ...LuxMeter
  }
  iterations {
    meters {
      bauMeterId
      totalPvKw
      pv {
        pvType
        sizeKw
      }
    }
    metric
    postTaxItcDeprIrr
  }
}
    `
export const DeltaFragmentDoc = `
    fragment Delta on Delta {
  a
  b
  diff
  pct
}
    `
export const NameThatTariffResultFragmentDoc = `
    fragment NameThatTariffResult on NameThatTariffResult {
  notTheCorrectRateVersion
  rate {
    startDate
    endDate
    tariffId
  }
  expected {
    start
    end
    totalCostUsd
    totalKwh
    peakKw
    lineItems {
      chargeType
      costUsd
    }
  }
  actual {
    start
    end
    totalCostUsd
    totalKwh
    peakKw
    lineItems {
      chargeType
      costUsd
    }
  }
  delta {
    usd {
      ...Delta
    }
    kwh {
      ...Delta
    }
    kw {
      ...Delta
    }
  }
  document {
    pid
    contentType
    viewHref
  }
}
    `
export const CreatePortfolioDocument = `
    mutation CreatePortfolio($title: String!, $logo: Upload!, $orgPid: String, $createEponymousOrg: Boolean!) {
  portfolio {
    new(
      title: $title
      upload: $logo
      orgPid: $orgPid
      createEponymousOrg: $createEponymousOrg
    ) {
      pid
      title
    }
  }
}
    `

export const useCreatePortfolioMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CreatePortfolioMutation,
    TError,
    Types.CreatePortfolioMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreatePortfolioMutation,
    TError,
    Types.CreatePortfolioMutationVariables,
    TContext
  >({
    mutationKey: ["CreatePortfolio"],
    mutationFn: (variables?: Types.CreatePortfolioMutationVariables) =>
      adminFetcher<
        Types.CreatePortfolioMutation,
        Types.CreatePortfolioMutationVariables
      >(CreatePortfolioDocument, variables)(),
    ...options,
  })
}

useCreatePortfolioMutation.fetcher = (
  variables: Types.CreatePortfolioMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.CreatePortfolioMutation, Types.CreatePortfolioMutationVariables>(
    CreatePortfolioDocument,
    variables,
    options,
  )

export const SetSitePortfolioDocument = `
    mutation SetSitePortfolio($sitePid: String!, $portfolioPid: String) {
  site {
    setPortfolio(sitePid: $sitePid, portfolioPid: $portfolioPid) {
      pid
      portfolio {
        pid
        title
      }
    }
  }
}
    `

export const useSetSitePortfolioMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetSitePortfolioMutation,
    TError,
    Types.SetSitePortfolioMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetSitePortfolioMutation,
    TError,
    Types.SetSitePortfolioMutationVariables,
    TContext
  >({
    mutationKey: ["SetSitePortfolio"],
    mutationFn: (variables?: Types.SetSitePortfolioMutationVariables) =>
      adminFetcher<
        Types.SetSitePortfolioMutation,
        Types.SetSitePortfolioMutationVariables
      >(SetSitePortfolioDocument, variables)(),
    ...options,
  })
}

useSetSitePortfolioMutation.fetcher = (
  variables: Types.SetSitePortfolioMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetSitePortfolioMutation, Types.SetSitePortfolioMutationVariables>(
    SetSitePortfolioDocument,
    variables,
    options,
  )

export const SetBuildingTypeDocument = `
    mutation SetBuildingType($sitePid: String!, $value: BuildingType) {
  site {
    setBuildingType(sitePid: $sitePid, value: $value) {
      pid
      buildingType
    }
  }
}
    `

export const useSetBuildingTypeMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetBuildingTypeMutation,
    TError,
    Types.SetBuildingTypeMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBuildingTypeMutation,
    TError,
    Types.SetBuildingTypeMutationVariables,
    TContext
  >({
    mutationKey: ["SetBuildingType"],
    mutationFn: (variables?: Types.SetBuildingTypeMutationVariables) =>
      adminFetcher<
        Types.SetBuildingTypeMutation,
        Types.SetBuildingTypeMutationVariables
      >(SetBuildingTypeDocument, variables)(),
    ...options,
  })
}

useSetBuildingTypeMutation.fetcher = (
  variables: Types.SetBuildingTypeMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetBuildingTypeMutation, Types.SetBuildingTypeMutationVariables>(
    SetBuildingTypeDocument,
    variables,
    options,
  )

export const SetCostarPropertyTypeDocument = `
    mutation SetCostarPropertyType($sitePid: String!, $value: CoStarPropertyType!) {
  site {
    setCostarPropertyType(sitePid: $sitePid, value: $value) {
      pid
      costarPropertyType
    }
  }
}
    `

export const useSetCostarPropertyTypeMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetCostarPropertyTypeMutation,
    TError,
    Types.SetCostarPropertyTypeMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetCostarPropertyTypeMutation,
    TError,
    Types.SetCostarPropertyTypeMutationVariables,
    TContext
  >({
    mutationKey: ["SetCostarPropertyType"],
    mutationFn: (variables?: Types.SetCostarPropertyTypeMutationVariables) =>
      adminFetcher<
        Types.SetCostarPropertyTypeMutation,
        Types.SetCostarPropertyTypeMutationVariables
      >(SetCostarPropertyTypeDocument, variables)(),
    ...options,
  })
}

useSetCostarPropertyTypeMutation.fetcher = (
  variables: Types.SetCostarPropertyTypeMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.SetCostarPropertyTypeMutation,
    Types.SetCostarPropertyTypeMutationVariables
  >(SetCostarPropertyTypeDocument, variables, options)

export const SetCostarSecondaryTypeDocument = `
    mutation SetCostarSecondaryType($sitePid: String!, $value: CoStarSecondaryType) {
  site {
    setCostarSecondaryType(sitePid: $sitePid, value: $value) {
      pid
      costarSecondaryType
    }
  }
}
    `

export const useSetCostarSecondaryTypeMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetCostarSecondaryTypeMutation,
    TError,
    Types.SetCostarSecondaryTypeMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetCostarSecondaryTypeMutation,
    TError,
    Types.SetCostarSecondaryTypeMutationVariables,
    TContext
  >({
    mutationKey: ["SetCostarSecondaryType"],
    mutationFn: (variables?: Types.SetCostarSecondaryTypeMutationVariables) =>
      adminFetcher<
        Types.SetCostarSecondaryTypeMutation,
        Types.SetCostarSecondaryTypeMutationVariables
      >(SetCostarSecondaryTypeDocument, variables)(),
    ...options,
  })
}

useSetCostarSecondaryTypeMutation.fetcher = (
  variables: Types.SetCostarSecondaryTypeMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.SetCostarSecondaryTypeMutation,
    Types.SetCostarSecondaryTypeMutationVariables
  >(SetCostarSecondaryTypeDocument, variables, options)

export const SetCapRateDocument = `
    mutation SetCapRate($sitePid: String!, $value: Float) {
  site {
    setCapRate(sitePid: $sitePid, value: $value) {
      pid
      capRate
    }
  }
}
    `

export const useSetCapRateMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetCapRateMutation,
    TError,
    Types.SetCapRateMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetCapRateMutation,
    TError,
    Types.SetCapRateMutationVariables,
    TContext
  >({
    mutationKey: ["SetCapRate"],
    mutationFn: (variables?: Types.SetCapRateMutationVariables) =>
      adminFetcher<Types.SetCapRateMutation, Types.SetCapRateMutationVariables>(
        SetCapRateDocument,
        variables,
      )(),
    ...options,
  })
}

useSetCapRateMutation.fetcher = (
  variables: Types.SetCapRateMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetCapRateMutation, Types.SetCapRateMutationVariables>(
    SetCapRateDocument,
    variables,
    options,
  )

export const SetZipCodeDocument = `
    mutation SetZipCode($sitePid: String!, $value: String!) {
  site {
    setZipCode(sitePid: $sitePid, value: $value) {
      pid
      zipCode
    }
  }
}
    `

export const useSetZipCodeMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetZipCodeMutation,
    TError,
    Types.SetZipCodeMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetZipCodeMutation,
    TError,
    Types.SetZipCodeMutationVariables,
    TContext
  >({
    mutationKey: ["SetZipCode"],
    mutationFn: (variables?: Types.SetZipCodeMutationVariables) =>
      adminFetcher<Types.SetZipCodeMutation, Types.SetZipCodeMutationVariables>(
        SetZipCodeDocument,
        variables,
      )(),
    ...options,
  })
}

useSetZipCodeMutation.fetcher = (
  variables: Types.SetZipCodeMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetZipCodeMutation, Types.SetZipCodeMutationVariables>(
    SetZipCodeDocument,
    variables,
    options,
  )

export const SetBuildingClassDocument = `
    mutation SetBuildingClass($sitePid: String!, $buildingClass: String) {
  site {
    setBuildingClass(sitePid: $sitePid, buildingClass: $buildingClass)
  }
}
    `

export const useSetBuildingClassMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetBuildingClassMutation,
    TError,
    Types.SetBuildingClassMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBuildingClassMutation,
    TError,
    Types.SetBuildingClassMutationVariables,
    TContext
  >({
    mutationKey: ["SetBuildingClass"],
    mutationFn: (variables?: Types.SetBuildingClassMutationVariables) =>
      adminFetcher<
        Types.SetBuildingClassMutation,
        Types.SetBuildingClassMutationVariables
      >(SetBuildingClassDocument, variables)(),
    ...options,
  })
}

useSetBuildingClassMutation.fetcher = (
  variables: Types.SetBuildingClassMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetBuildingClassMutation, Types.SetBuildingClassMutationVariables>(
    SetBuildingClassDocument,
    variables,
    options,
  )

export const SetSqftDocument = `
    mutation SetSqft($sitePid: String!, $value: Float!) {
  site {
    setSqft(sitePid: $sitePid, value: $value) {
      pid
      sqft
    }
  }
}
    `

export const useSetSqftMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetSqftMutation,
    TError,
    Types.SetSqftMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetSqftMutation,
    TError,
    Types.SetSqftMutationVariables,
    TContext
  >({
    mutationKey: ["SetSqft"],
    mutationFn: (variables?: Types.SetSqftMutationVariables) =>
      adminFetcher<Types.SetSqftMutation, Types.SetSqftMutationVariables>(
        SetSqftDocument,
        variables,
      )(),
    ...options,
  })
}

useSetSqftMutation.fetcher = (
  variables: Types.SetSqftMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetSqftMutation, Types.SetSqftMutationVariables>(
    SetSqftDocument,
    variables,
    options,
  )

export const SetRoofSqftDocument = `
    mutation SetRoofSqft($sitePid: String!, $value: Float!, $sourceType: ChangeSourceType, $sourceId: String) {
  site {
    setRoofSqft(
      sitePid: $sitePid
      value: $value
      sourceType: $sourceType
      sourceId: $sourceId
    ) {
      pid
      roofSqft
    }
  }
}
    `

export const useSetRoofSqftMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetRoofSqftMutation,
    TError,
    Types.SetRoofSqftMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetRoofSqftMutation,
    TError,
    Types.SetRoofSqftMutationVariables,
    TContext
  >({
    mutationKey: ["SetRoofSqft"],
    mutationFn: (variables?: Types.SetRoofSqftMutationVariables) =>
      adminFetcher<Types.SetRoofSqftMutation, Types.SetRoofSqftMutationVariables>(
        SetRoofSqftDocument,
        variables,
      )(),
    ...options,
  })
}

useSetRoofSqftMutation.fetcher = (
  variables: Types.SetRoofSqftMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetRoofSqftMutation, Types.SetRoofSqftMutationVariables>(
    SetRoofSqftDocument,
    variables,
    options,
  )

export const SetYearBuiltDocument = `
    mutation SetYearBuilt($sitePid: String!, $value: Int) {
  site {
    setYearBuilt(sitePid: $sitePid, yearBuilt: $value) {
      pid
    }
  }
}
    `

export const useSetYearBuiltMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetYearBuiltMutation,
    TError,
    Types.SetYearBuiltMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetYearBuiltMutation,
    TError,
    Types.SetYearBuiltMutationVariables,
    TContext
  >({
    mutationKey: ["SetYearBuilt"],
    mutationFn: (variables?: Types.SetYearBuiltMutationVariables) =>
      adminFetcher<Types.SetYearBuiltMutation, Types.SetYearBuiltMutationVariables>(
        SetYearBuiltDocument,
        variables,
      )(),
    ...options,
  })
}

useSetYearBuiltMutation.fetcher = (
  variables: Types.SetYearBuiltMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetYearBuiltMutation, Types.SetYearBuiltMutationVariables>(
    SetYearBuiltDocument,
    variables,
    options,
  )

export const SetNumStoriesDocument = `
    mutation SetNumStories($sitePid: String!, $value: Int) {
  site {
    setNumStories(sitePid: $sitePid, numStories: $value) {
      pid
    }
  }
}
    `

export const useSetNumStoriesMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetNumStoriesMutation,
    TError,
    Types.SetNumStoriesMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetNumStoriesMutation,
    TError,
    Types.SetNumStoriesMutationVariables,
    TContext
  >({
    mutationKey: ["SetNumStories"],
    mutationFn: (variables?: Types.SetNumStoriesMutationVariables) =>
      adminFetcher<Types.SetNumStoriesMutation, Types.SetNumStoriesMutationVariables>(
        SetNumStoriesDocument,
        variables,
      )(),
    ...options,
  })
}

useSetNumStoriesMutation.fetcher = (
  variables: Types.SetNumStoriesMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetNumStoriesMutation, Types.SetNumStoriesMutationVariables>(
    SetNumStoriesDocument,
    variables,
    options,
  )

export const SetNumParkingSpacesDocument = `
    mutation SetNumParkingSpaces($sitePid: String!, $value: Int) {
  site {
    setNumParkingSpaces(sitePid: $sitePid, numParkingSpaces: $value) {
      pid
    }
  }
}
    `

export const useSetNumParkingSpacesMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetNumParkingSpacesMutation,
    TError,
    Types.SetNumParkingSpacesMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetNumParkingSpacesMutation,
    TError,
    Types.SetNumParkingSpacesMutationVariables,
    TContext
  >({
    mutationKey: ["SetNumParkingSpaces"],
    mutationFn: (variables?: Types.SetNumParkingSpacesMutationVariables) =>
      adminFetcher<
        Types.SetNumParkingSpacesMutation,
        Types.SetNumParkingSpacesMutationVariables
      >(SetNumParkingSpacesDocument, variables)(),
    ...options,
  })
}

useSetNumParkingSpacesMutation.fetcher = (
  variables: Types.SetNumParkingSpacesMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.SetNumParkingSpacesMutation,
    Types.SetNumParkingSpacesMutationVariables
  >(SetNumParkingSpacesDocument, variables, options)

export const SetExistingSolarDocument = `
    mutation SetExistingSolar($sitePid: String!, $value: Boolean, $source: PhysicalCheckSource!) {
  site {
    setExistingSolar(sitePid: $sitePid, value: $value, source: $source) {
      pid
      physicalCheck {
        existingSolar
      }
    }
  }
}
    `

export const useSetExistingSolarMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetExistingSolarMutation,
    TError,
    Types.SetExistingSolarMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetExistingSolarMutation,
    TError,
    Types.SetExistingSolarMutationVariables,
    TContext
  >({
    mutationKey: ["SetExistingSolar"],
    mutationFn: (variables?: Types.SetExistingSolarMutationVariables) =>
      adminFetcher<
        Types.SetExistingSolarMutation,
        Types.SetExistingSolarMutationVariables
      >(SetExistingSolarDocument, variables)(),
    ...options,
  })
}

useSetExistingSolarMutation.fetcher = (
  variables: Types.SetExistingSolarMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetExistingSolarMutation, Types.SetExistingSolarMutationVariables>(
    SetExistingSolarDocument,
    variables,
    options,
  )

export const SetCustomerIdDocument = `
    mutation SetCustomerId($sitePid: String!, $customerId: String) {
  site {
    setCustomerId(sitePid: $sitePid, customerId: $customerId)
  }
}
    `

export const useSetCustomerIdMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetCustomerIdMutation,
    TError,
    Types.SetCustomerIdMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetCustomerIdMutation,
    TError,
    Types.SetCustomerIdMutationVariables,
    TContext
  >({
    mutationKey: ["SetCustomerId"],
    mutationFn: (variables?: Types.SetCustomerIdMutationVariables) =>
      adminFetcher<Types.SetCustomerIdMutation, Types.SetCustomerIdMutationVariables>(
        SetCustomerIdDocument,
        variables,
      )(),
    ...options,
  })
}

useSetCustomerIdMutation.fetcher = (
  variables: Types.SetCustomerIdMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetCustomerIdMutation, Types.SetCustomerIdMutationVariables>(
    SetCustomerIdDocument,
    variables,
    options,
  )

export const SetAssetManagerDocument = `
    mutation SetAssetManager($sitePid: String!, $assetManager: String) {
  site {
    setAssetManager(sitePid: $sitePid, assetManager: $assetManager)
  }
}
    `

export const useSetAssetManagerMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetAssetManagerMutation,
    TError,
    Types.SetAssetManagerMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetAssetManagerMutation,
    TError,
    Types.SetAssetManagerMutationVariables,
    TContext
  >({
    mutationKey: ["SetAssetManager"],
    mutationFn: (variables?: Types.SetAssetManagerMutationVariables) =>
      adminFetcher<
        Types.SetAssetManagerMutation,
        Types.SetAssetManagerMutationVariables
      >(SetAssetManagerDocument, variables)(),
    ...options,
  })
}

useSetAssetManagerMutation.fetcher = (
  variables: Types.SetAssetManagerMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetAssetManagerMutation, Types.SetAssetManagerMutationVariables>(
    SetAssetManagerDocument,
    variables,
    options,
  )

export const TrashAttachmentDocument = `
    mutation TrashAttachment($pid: String!, $attachmentId: Int!) {
  site {
    trashAttachment(pid: $pid, attachmentId: $attachmentId) {
      pid
      roofImage {
        id
        name
      }
      attachments {
        id
        trashedAt
        isTrashable
        isRoofImageCandidate
      }
    }
  }
}
    `

export const useTrashAttachmentMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.TrashAttachmentMutation,
    TError,
    Types.TrashAttachmentMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.TrashAttachmentMutation,
    TError,
    Types.TrashAttachmentMutationVariables,
    TContext
  >({
    mutationKey: ["TrashAttachment"],
    mutationFn: (variables?: Types.TrashAttachmentMutationVariables) =>
      adminFetcher<
        Types.TrashAttachmentMutation,
        Types.TrashAttachmentMutationVariables
      >(TrashAttachmentDocument, variables)(),
    ...options,
  })
}

useTrashAttachmentMutation.fetcher = (
  variables: Types.TrashAttachmentMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.TrashAttachmentMutation, Types.TrashAttachmentMutationVariables>(
    TrashAttachmentDocument,
    variables,
    options,
  )

export const SetSiteHiddenFromCustomersDocument = `
    mutation SetSiteHiddenFromCustomers($sitePid: String!, $hiddenFromCustomers: Boolean!) {
  site {
    setSiteHiddenFromCustomers(
      sitePid: $sitePid
      hiddenFromCustomers: $hiddenFromCustomers
    ) {
      pid
      hiddenFromCustomers
    }
  }
}
    `

export const useSetSiteHiddenFromCustomersMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetSiteHiddenFromCustomersMutation,
    TError,
    Types.SetSiteHiddenFromCustomersMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetSiteHiddenFromCustomersMutation,
    TError,
    Types.SetSiteHiddenFromCustomersMutationVariables,
    TContext
  >({
    mutationKey: ["SetSiteHiddenFromCustomers"],
    mutationFn: (variables?: Types.SetSiteHiddenFromCustomersMutationVariables) =>
      adminFetcher<
        Types.SetSiteHiddenFromCustomersMutation,
        Types.SetSiteHiddenFromCustomersMutationVariables
      >(SetSiteHiddenFromCustomersDocument, variables)(),
    ...options,
  })
}

useSetSiteHiddenFromCustomersMutation.fetcher = (
  variables: Types.SetSiteHiddenFromCustomersMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.SetSiteHiddenFromCustomersMutation,
    Types.SetSiteHiddenFromCustomersMutationVariables
  >(SetSiteHiddenFromCustomersDocument, variables, options)

export const AttachFileDocument = `
    mutation AttachFile($pid: String!, $file: Upload!, $tag: String) {
  site {
    attachFile(pid: $pid, upload: $file, tag: $tag) {
      __typename
      ... on Site {
        pid
        attachments {
          ...AttachmentFragment
        }
      }
      ... on DuplicatesAttachment {
        attachment {
          name
        }
      }
      ... on Error {
        message
      }
    }
  }
}
    ${AttachmentFragmentFragmentDoc}
${UserRefFragmentDoc}`

export const useAttachFileMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AttachFileMutation,
    TError,
    Types.AttachFileMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AttachFileMutation,
    TError,
    Types.AttachFileMutationVariables,
    TContext
  >({
    mutationKey: ["AttachFile"],
    mutationFn: (variables?: Types.AttachFileMutationVariables) =>
      adminFetcher<Types.AttachFileMutation, Types.AttachFileMutationVariables>(
        AttachFileDocument,
        variables,
      )(),
    ...options,
  })
}

useAttachFileMutation.fetcher = (
  variables: Types.AttachFileMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.AttachFileMutation, Types.AttachFileMutationVariables>(
    AttachFileDocument,
    variables,
    options,
  )

export const CreateUserDocument = `
    mutation CreateUser($email: String!, $name: String, $orgPid: String!, $portfolioPids: [String!]!, $sendInvite: Boolean!) {
  user {
    new(
      email: $email
      name: $name
      orgPid: $orgPid
      portfolioPids: $portfolioPids
      sendInvite: $sendInvite
    ) {
      __typename
      ... on AdminUser {
        id
        email
        name
      }
      ... on Error {
        message
      }
    }
  }
}
    `

export const useCreateUserMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CreateUserMutation,
    TError,
    Types.CreateUserMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateUserMutation,
    TError,
    Types.CreateUserMutationVariables,
    TContext
  >({
    mutationKey: ["CreateUser"],
    mutationFn: (variables?: Types.CreateUserMutationVariables) =>
      adminFetcher<Types.CreateUserMutation, Types.CreateUserMutationVariables>(
        CreateUserDocument,
        variables,
      )(),
    ...options,
  })
}

useCreateUserMutation.fetcher = (
  variables: Types.CreateUserMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.CreateUserMutation, Types.CreateUserMutationVariables>(
    CreateUserDocument,
    variables,
    options,
  )

export const CreateAdminUserDocument = `
    mutation CreateAdminUser($email: String!, $name: String) {
  user {
    createAdmin(email: $email, name: $name) {
      __typename
      ... on AdminUser {
        id
        email
        name
      }
      ... on Error {
        message
      }
    }
  }
}
    `

export const useCreateAdminUserMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CreateAdminUserMutation,
    TError,
    Types.CreateAdminUserMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateAdminUserMutation,
    TError,
    Types.CreateAdminUserMutationVariables,
    TContext
  >({
    mutationKey: ["CreateAdminUser"],
    mutationFn: (variables?: Types.CreateAdminUserMutationVariables) =>
      adminFetcher<
        Types.CreateAdminUserMutation,
        Types.CreateAdminUserMutationVariables
      >(CreateAdminUserDocument, variables)(),
    ...options,
  })
}

useCreateAdminUserMutation.fetcher = (
  variables: Types.CreateAdminUserMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.CreateAdminUserMutation, Types.CreateAdminUserMutationVariables>(
    CreateAdminUserDocument,
    variables,
    options,
  )

export const ChangeUserNameDocument = `
    mutation ChangeUserName($id: Int!, $name: String!) {
  user {
    setName(id: $id, name: $name) {
      id
      name
    }
  }
}
    `

export const useChangeUserNameMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.ChangeUserNameMutation,
    TError,
    Types.ChangeUserNameMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.ChangeUserNameMutation,
    TError,
    Types.ChangeUserNameMutationVariables,
    TContext
  >({
    mutationKey: ["ChangeUserName"],
    mutationFn: (variables?: Types.ChangeUserNameMutationVariables) =>
      adminFetcher<Types.ChangeUserNameMutation, Types.ChangeUserNameMutationVariables>(
        ChangeUserNameDocument,
        variables,
      )(),
    ...options,
  })
}

useChangeUserNameMutation.fetcher = (
  variables: Types.ChangeUserNameMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.ChangeUserNameMutation, Types.ChangeUserNameMutationVariables>(
    ChangeUserNameDocument,
    variables,
    options,
  )

export const SetUserMeetingUrlDocument = `
    mutation SetUserMeetingUrl($id: Int!, $url: String!) {
  user {
    setMeetingSchedulingUrl(userId: $id, url: $url) {
      __typename
      ... on Error {
        message
      }
      ... on AdminUser {
        id
        name
        email
        meetingSchedulingUrl
      }
    }
  }
}
    `

export const useSetUserMeetingUrlMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetUserMeetingUrlMutation,
    TError,
    Types.SetUserMeetingUrlMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetUserMeetingUrlMutation,
    TError,
    Types.SetUserMeetingUrlMutationVariables,
    TContext
  >({
    mutationKey: ["SetUserMeetingUrl"],
    mutationFn: (variables?: Types.SetUserMeetingUrlMutationVariables) =>
      adminFetcher<
        Types.SetUserMeetingUrlMutation,
        Types.SetUserMeetingUrlMutationVariables
      >(SetUserMeetingUrlDocument, variables)(),
    ...options,
  })
}

useSetUserMeetingUrlMutation.fetcher = (
  variables: Types.SetUserMeetingUrlMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.SetUserMeetingUrlMutation,
    Types.SetUserMeetingUrlMutationVariables
  >(SetUserMeetingUrlDocument, variables, options)

export const InviteToPortfolioDocument = `
    mutation InviteToPortfolio($email: String!, $orgPid: String!, $portfolioPid: String!) {
  user {
    inviteToPortfolio(email: $email, orgPid: $orgPid, portfolioPid: $portfolioPid)
  }
}
    `

export const useInviteToPortfolioMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.InviteToPortfolioMutation,
    TError,
    Types.InviteToPortfolioMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.InviteToPortfolioMutation,
    TError,
    Types.InviteToPortfolioMutationVariables,
    TContext
  >({
    mutationKey: ["InviteToPortfolio"],
    mutationFn: (variables?: Types.InviteToPortfolioMutationVariables) =>
      adminFetcher<
        Types.InviteToPortfolioMutation,
        Types.InviteToPortfolioMutationVariables
      >(InviteToPortfolioDocument, variables)(),
    ...options,
  })
}

useInviteToPortfolioMutation.fetcher = (
  variables: Types.InviteToPortfolioMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.InviteToPortfolioMutation,
    Types.InviteToPortfolioMutationVariables
  >(InviteToPortfolioDocument, variables, options)

export const GiveMeAMagicLinkDocument = `
    mutation GiveMeAMagicLink($email: String!) {
  user {
    giveMeAMagicLink(email: $email) {
      href
      expiresAt
    }
  }
}
    `

export const useGiveMeAMagicLinkMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.GiveMeAMagicLinkMutation,
    TError,
    Types.GiveMeAMagicLinkMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.GiveMeAMagicLinkMutation,
    TError,
    Types.GiveMeAMagicLinkMutationVariables,
    TContext
  >({
    mutationKey: ["GiveMeAMagicLink"],
    mutationFn: (variables?: Types.GiveMeAMagicLinkMutationVariables) =>
      adminFetcher<
        Types.GiveMeAMagicLinkMutation,
        Types.GiveMeAMagicLinkMutationVariables
      >(GiveMeAMagicLinkDocument, variables)(),
    ...options,
  })
}

useGiveMeAMagicLinkMutation.fetcher = (
  variables: Types.GiveMeAMagicLinkMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.GiveMeAMagicLinkMutation, Types.GiveMeAMagicLinkMutationVariables>(
    GiveMeAMagicLinkDocument,
    variables,
    options,
  )

export const AddUserToPortfolioDocument = `
    mutation AddUserToPortfolio($userId: Int!, $portfolioPid: String!, $sendEmail: Boolean!) {
  user {
    addToPortfolio(
      userId: $userId
      portfolioPid: $portfolioPid
      sendEmail: $sendEmail
    ) {
      id
      pid
    }
  }
}
    `

export const useAddUserToPortfolioMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AddUserToPortfolioMutation,
    TError,
    Types.AddUserToPortfolioMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddUserToPortfolioMutation,
    TError,
    Types.AddUserToPortfolioMutationVariables,
    TContext
  >({
    mutationKey: ["AddUserToPortfolio"],
    mutationFn: (variables?: Types.AddUserToPortfolioMutationVariables) =>
      adminFetcher<
        Types.AddUserToPortfolioMutation,
        Types.AddUserToPortfolioMutationVariables
      >(AddUserToPortfolioDocument, variables)(),
    ...options,
  })
}

useAddUserToPortfolioMutation.fetcher = (
  variables: Types.AddUserToPortfolioMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.AddUserToPortfolioMutation,
    Types.AddUserToPortfolioMutationVariables
  >(AddUserToPortfolioDocument, variables, options)

export const RemoveFromPortfolioDocument = `
    mutation RemoveFromPortfolio($userId: Int!, $portfolioPid: String!) {
  user {
    removeFromPortfolio(userId: $userId, portfolioPid: $portfolioPid) {
      id
      pid
    }
  }
}
    `

export const useRemoveFromPortfolioMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.RemoveFromPortfolioMutation,
    TError,
    Types.RemoveFromPortfolioMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RemoveFromPortfolioMutation,
    TError,
    Types.RemoveFromPortfolioMutationVariables,
    TContext
  >({
    mutationKey: ["RemoveFromPortfolio"],
    mutationFn: (variables?: Types.RemoveFromPortfolioMutationVariables) =>
      adminFetcher<
        Types.RemoveFromPortfolioMutation,
        Types.RemoveFromPortfolioMutationVariables
      >(RemoveFromPortfolioDocument, variables)(),
    ...options,
  })
}

useRemoveFromPortfolioMutation.fetcher = (
  variables: Types.RemoveFromPortfolioMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.RemoveFromPortfolioMutation,
    Types.RemoveFromPortfolioMutationVariables
  >(RemoveFromPortfolioDocument, variables, options)

export const SetRoofMaxPvKwDocument = `
    mutation SetRoofMaxPvKw($sitePid: String!, $value: Float) {
  site {
    setRoofMaxPvKw(sitePid: $sitePid, value: $value) {
      pid
      roofMaxPvKw
    }
  }
}
    `

export const useSetRoofMaxPvKwMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetRoofMaxPvKwMutation,
    TError,
    Types.SetRoofMaxPvKwMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetRoofMaxPvKwMutation,
    TError,
    Types.SetRoofMaxPvKwMutationVariables,
    TContext
  >({
    mutationKey: ["SetRoofMaxPvKw"],
    mutationFn: (variables?: Types.SetRoofMaxPvKwMutationVariables) =>
      adminFetcher<Types.SetRoofMaxPvKwMutation, Types.SetRoofMaxPvKwMutationVariables>(
        SetRoofMaxPvKwDocument,
        variables,
      )(),
    ...options,
  })
}

useSetRoofMaxPvKwMutation.fetcher = (
  variables: Types.SetRoofMaxPvKwMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetRoofMaxPvKwMutation, Types.SetRoofMaxPvKwMutationVariables>(
    SetRoofMaxPvKwDocument,
    variables,
    options,
  )

export const SetCarportMaxPvKwDocument = `
    mutation SetCarportMaxPvKw($sitePid: String!, $value: Float) {
  site {
    setCarportMaxPvKw(sitePid: $sitePid, value: $value) {
      pid
      carportMaxPvKw
    }
  }
}
    `

export const useSetCarportMaxPvKwMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetCarportMaxPvKwMutation,
    TError,
    Types.SetCarportMaxPvKwMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetCarportMaxPvKwMutation,
    TError,
    Types.SetCarportMaxPvKwMutationVariables,
    TContext
  >({
    mutationKey: ["SetCarportMaxPvKw"],
    mutationFn: (variables?: Types.SetCarportMaxPvKwMutationVariables) =>
      adminFetcher<
        Types.SetCarportMaxPvKwMutation,
        Types.SetCarportMaxPvKwMutationVariables
      >(SetCarportMaxPvKwDocument, variables)(),
    ...options,
  })
}

useSetCarportMaxPvKwMutation.fetcher = (
  variables: Types.SetCarportMaxPvKwMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.SetCarportMaxPvKwMutation,
    Types.SetCarportMaxPvKwMutationVariables
  >(SetCarportMaxPvKwDocument, variables, options)

export const SetGroundMaxPvKwDocument = `
    mutation SetGroundMaxPvKw($sitePid: String!, $value: Float) {
  site {
    setGroundMaxPvKw(sitePid: $sitePid, value: $value) {
      pid
      groundMaxPvKw
    }
  }
}
    `

export const useSetGroundMaxPvKwMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetGroundMaxPvKwMutation,
    TError,
    Types.SetGroundMaxPvKwMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetGroundMaxPvKwMutation,
    TError,
    Types.SetGroundMaxPvKwMutationVariables,
    TContext
  >({
    mutationKey: ["SetGroundMaxPvKw"],
    mutationFn: (variables?: Types.SetGroundMaxPvKwMutationVariables) =>
      adminFetcher<
        Types.SetGroundMaxPvKwMutation,
        Types.SetGroundMaxPvKwMutationVariables
      >(SetGroundMaxPvKwDocument, variables)(),
    ...options,
  })
}

useSetGroundMaxPvKwMutation.fetcher = (
  variables: Types.SetGroundMaxPvKwMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetGroundMaxPvKwMutation, Types.SetGroundMaxPvKwMutationVariables>(
    SetGroundMaxPvKwDocument,
    variables,
    options,
  )

export const UpdateTariffDocument = `
    mutation UpdateTariff($id: Int!, $isPreferred: Boolean, $requiresPv: Boolean, $requiresStorage: Boolean) {
  tariff {
    setFields(
      id: $id
      isPreferred: $isPreferred
      requiresPv: $requiresPv
      requiresStorage: $requiresStorage
    ) {
      id
      isPreferred
      requiresPv
      requiresStorage
    }
  }
}
    `

export const useUpdateTariffMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UpdateTariffMutation,
    TError,
    Types.UpdateTariffMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UpdateTariffMutation,
    TError,
    Types.UpdateTariffMutationVariables,
    TContext
  >({
    mutationKey: ["UpdateTariff"],
    mutationFn: (variables?: Types.UpdateTariffMutationVariables) =>
      adminFetcher<Types.UpdateTariffMutation, Types.UpdateTariffMutationVariables>(
        UpdateTariffDocument,
        variables,
      )(),
    ...options,
  })
}

useUpdateTariffMutation.fetcher = (
  variables: Types.UpdateTariffMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.UpdateTariffMutation, Types.UpdateTariffMutationVariables>(
    UpdateTariffDocument,
    variables,
    options,
  )

export const AddOverrideDocument = `
    mutation AddOverride($sitePid: String, $portfolioPid: String, $orgPid: String, $finProductId: Int, $inputName: String!, $inputValue: String!) {
  finmodel {
    addOverride(
      sitePid: $sitePid
      portfolioPid: $portfolioPid
      orgPid: $orgPid
      finproductId: $finProductId
      inputName: $inputName
      inputValue: $inputValue
    ) {
      __typename
      ... on Error {
        message
      }
      ... on FinmodelInputOverride {
        inputName
        inputValue
      }
    }
  }
}
    `

export const useAddOverrideMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AddOverrideMutation,
    TError,
    Types.AddOverrideMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddOverrideMutation,
    TError,
    Types.AddOverrideMutationVariables,
    TContext
  >({
    mutationKey: ["AddOverride"],
    mutationFn: (variables?: Types.AddOverrideMutationVariables) =>
      adminFetcher<Types.AddOverrideMutation, Types.AddOverrideMutationVariables>(
        AddOverrideDocument,
        variables,
      )(),
    ...options,
  })
}

useAddOverrideMutation.fetcher = (
  variables: Types.AddOverrideMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.AddOverrideMutation, Types.AddOverrideMutationVariables>(
    AddOverrideDocument,
    variables,
    options,
  )

export const DeleteOverrideDocument = `
    mutation DeleteOverride($sitePid: String, $portfolioPid: String, $orgPid: String, $finProductId: Int, $inputName: String!) {
  finmodel {
    deleteOverride(
      sitePid: $sitePid
      portfolioPid: $portfolioPid
      orgPid: $orgPid
      finproductId: $finProductId
      inputName: $inputName
    ) {
      inputName
      inputValue
    }
  }
}
    `

export const useDeleteOverrideMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.DeleteOverrideMutation,
    TError,
    Types.DeleteOverrideMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.DeleteOverrideMutation,
    TError,
    Types.DeleteOverrideMutationVariables,
    TContext
  >({
    mutationKey: ["DeleteOverride"],
    mutationFn: (variables?: Types.DeleteOverrideMutationVariables) =>
      adminFetcher<Types.DeleteOverrideMutation, Types.DeleteOverrideMutationVariables>(
        DeleteOverrideDocument,
        variables,
      )(),
    ...options,
  })
}

useDeleteOverrideMutation.fetcher = (
  variables: Types.DeleteOverrideMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.DeleteOverrideMutation, Types.DeleteOverrideMutationVariables>(
    DeleteOverrideDocument,
    variables,
    options,
  )

export const SetPortfolioMinKwDocument = `
    mutation SetPortfolioMinKw($portfolioPid: String!, $minKw: Float) {
  portfolio {
    setPortfolioMinKw(portfolioPid: $portfolioPid, minKw: $minKw) {
      pid
      minKw
      minKwSetBy
      minKwSetDate
    }
  }
}
    `

export const useSetPortfolioMinKwMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetPortfolioMinKwMutation,
    TError,
    Types.SetPortfolioMinKwMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetPortfolioMinKwMutation,
    TError,
    Types.SetPortfolioMinKwMutationVariables,
    TContext
  >({
    mutationKey: ["SetPortfolioMinKw"],
    mutationFn: (variables?: Types.SetPortfolioMinKwMutationVariables) =>
      adminFetcher<
        Types.SetPortfolioMinKwMutation,
        Types.SetPortfolioMinKwMutationVariables
      >(SetPortfolioMinKwDocument, variables)(),
    ...options,
  })
}

useSetPortfolioMinKwMutation.fetcher = (
  variables: Types.SetPortfolioMinKwMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.SetPortfolioMinKwMutation,
    Types.SetPortfolioMinKwMutationVariables
  >(SetPortfolioMinKwDocument, variables, options)

export const SetEtbProjectNameDocument = `
    mutation SetEtbProjectName($etbId: Int!, $name: String!) {
  etb(etbId: $etbId) {
    setProjectName(name: $name) {
      id
      projectName
    }
  }
}
    `

export const useSetEtbProjectNameMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetEtbProjectNameMutation,
    TError,
    Types.SetEtbProjectNameMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetEtbProjectNameMutation,
    TError,
    Types.SetEtbProjectNameMutationVariables,
    TContext
  >({
    mutationKey: ["SetEtbProjectName"],
    mutationFn: (variables?: Types.SetEtbProjectNameMutationVariables) =>
      adminFetcher<
        Types.SetEtbProjectNameMutation,
        Types.SetEtbProjectNameMutationVariables
      >(SetEtbProjectNameDocument, variables)(),
    ...options,
  })
}

useSetEtbProjectNameMutation.fetcher = (
  variables: Types.SetEtbProjectNameMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.SetEtbProjectNameMutation,
    Types.SetEtbProjectNameMutationVariables
  >(SetEtbProjectNameDocument, variables, options)

export const SetEtbMeterTariffBeforeDocument = `
    mutation SetEtbMeterTariffBefore($etbMeterId: Int!, $tariffId: Int) {
  etbMeter {
    setTariffBefore(id: $etbMeterId, tariffBeforeId: $tariffId) {
      id
      genabilityTariffBefore: tariffBefore {
        id
        name
        code
      }
    }
  }
}
    `

export const useSetEtbMeterTariffBeforeMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetEtbMeterTariffBeforeMutation,
    TError,
    Types.SetEtbMeterTariffBeforeMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetEtbMeterTariffBeforeMutation,
    TError,
    Types.SetEtbMeterTariffBeforeMutationVariables,
    TContext
  >({
    mutationKey: ["SetEtbMeterTariffBefore"],
    mutationFn: (variables?: Types.SetEtbMeterTariffBeforeMutationVariables) =>
      adminFetcher<
        Types.SetEtbMeterTariffBeforeMutation,
        Types.SetEtbMeterTariffBeforeMutationVariables
      >(SetEtbMeterTariffBeforeDocument, variables)(),
    ...options,
  })
}

useSetEtbMeterTariffBeforeMutation.fetcher = (
  variables: Types.SetEtbMeterTariffBeforeMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.SetEtbMeterTariffBeforeMutation,
    Types.SetEtbMeterTariffBeforeMutationVariables
  >(SetEtbMeterTariffBeforeDocument, variables, options)

export const SetEtbMeterTariffAfterDocument = `
    mutation SetEtbMeterTariffAfter($etbMeterId: Int!, $tariffId: Int) {
  etbMeter {
    setTariffAfter(id: $etbMeterId, tariffAfterId: $tariffId) {
      id
      genabilityTariffAfter: tariffAfter {
        id
        name
        code
      }
    }
  }
}
    `

export const useSetEtbMeterTariffAfterMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetEtbMeterTariffAfterMutation,
    TError,
    Types.SetEtbMeterTariffAfterMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetEtbMeterTariffAfterMutation,
    TError,
    Types.SetEtbMeterTariffAfterMutationVariables,
    TContext
  >({
    mutationKey: ["SetEtbMeterTariffAfter"],
    mutationFn: (variables?: Types.SetEtbMeterTariffAfterMutationVariables) =>
      adminFetcher<
        Types.SetEtbMeterTariffAfterMutation,
        Types.SetEtbMeterTariffAfterMutationVariables
      >(SetEtbMeterTariffAfterDocument, variables)(),
    ...options,
  })
}

useSetEtbMeterTariffAfterMutation.fetcher = (
  variables: Types.SetEtbMeterTariffAfterMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.SetEtbMeterTariffAfterMutation,
    Types.SetEtbMeterTariffAfterMutationVariables
  >(SetEtbMeterTariffAfterDocument, variables, options)

export const SetEtbMeterBauMeterDocument = `
    mutation SetEtbMeterBauMeter($etbMeterId: Int!, $bauMeterId: Int!) {
  etbMeter {
    setBauMeter(id: $etbMeterId, bauMeterId: $bauMeterId) {
      id
    }
  }
}
    `

export const useSetEtbMeterBauMeterMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetEtbMeterBauMeterMutation,
    TError,
    Types.SetEtbMeterBauMeterMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetEtbMeterBauMeterMutation,
    TError,
    Types.SetEtbMeterBauMeterMutationVariables,
    TContext
  >({
    mutationKey: ["SetEtbMeterBauMeter"],
    mutationFn: (variables?: Types.SetEtbMeterBauMeterMutationVariables) =>
      adminFetcher<
        Types.SetEtbMeterBauMeterMutation,
        Types.SetEtbMeterBauMeterMutationVariables
      >(SetEtbMeterBauMeterDocument, variables)(),
    ...options,
  })
}

useSetEtbMeterBauMeterMutation.fetcher = (
  variables: Types.SetEtbMeterBauMeterMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.SetEtbMeterBauMeterMutation,
    Types.SetEtbMeterBauMeterMutationVariables
  >(SetEtbMeterBauMeterDocument, variables, options)

export const SetETbMeterPvTypeDocument = `
    mutation SetETbMeterPvType($etbMeterId: Int!, $pvType: PVType) {
  etbMeter {
    setPvType(id: $etbMeterId, pvType: $pvType) {
      id
    }
  }
}
    `

export const useSetETbMeterPvTypeMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetETbMeterPvTypeMutation,
    TError,
    Types.SetETbMeterPvTypeMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetETbMeterPvTypeMutation,
    TError,
    Types.SetETbMeterPvTypeMutationVariables,
    TContext
  >({
    mutationKey: ["SetETbMeterPvType"],
    mutationFn: (variables?: Types.SetETbMeterPvTypeMutationVariables) =>
      adminFetcher<
        Types.SetETbMeterPvTypeMutation,
        Types.SetETbMeterPvTypeMutationVariables
      >(SetETbMeterPvTypeDocument, variables)(),
    ...options,
  })
}

useSetETbMeterPvTypeMutation.fetcher = (
  variables: Types.SetETbMeterPvTypeMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.SetETbMeterPvTypeMutation,
    Types.SetETbMeterPvTypeMutationVariables
  >(SetETbMeterPvTypeDocument, variables, options)

export const SetSiteStatusDocument = `
    mutation SetSiteStatus($sitePid: String!, $status: SiteStatus!, $reason: StatusReason, $customReason: String, $unixtimeSec: Int) {
  site {
    setStatus(
      status: $status
      sitePid: $sitePid
      reason: $reason
      customReason: $customReason
      unixtimeSec: $unixtimeSec
    ) {
      pid
      currentStatus {
        pid
      }
    }
  }
}
    `

export const useSetSiteStatusMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetSiteStatusMutation,
    TError,
    Types.SetSiteStatusMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetSiteStatusMutation,
    TError,
    Types.SetSiteStatusMutationVariables,
    TContext
  >({
    mutationKey: ["SetSiteStatus"],
    mutationFn: (variables?: Types.SetSiteStatusMutationVariables) =>
      adminFetcher<Types.SetSiteStatusMutation, Types.SetSiteStatusMutationVariables>(
        SetSiteStatusDocument,
        variables,
      )(),
    ...options,
  })
}

useSetSiteStatusMutation.fetcher = (
  variables: Types.SetSiteStatusMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetSiteStatusMutation, Types.SetSiteStatusMutationVariables>(
    SetSiteStatusDocument,
    variables,
    options,
  )

export const SetPrimaryScenarioDocument = `
    mutation SetPrimaryScenario($scenarioPid: String!) {
  scenario(scenarioPid: $scenarioPid) {
    setPrimary {
      pid
      scenarios {
        pid
        isPrimary
      }
    }
  }
}
    `

export const useSetPrimaryScenarioMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetPrimaryScenarioMutation,
    TError,
    Types.SetPrimaryScenarioMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetPrimaryScenarioMutation,
    TError,
    Types.SetPrimaryScenarioMutationVariables,
    TContext
  >({
    mutationKey: ["SetPrimaryScenario"],
    mutationFn: (variables?: Types.SetPrimaryScenarioMutationVariables) =>
      adminFetcher<
        Types.SetPrimaryScenarioMutation,
        Types.SetPrimaryScenarioMutationVariables
      >(SetPrimaryScenarioDocument, variables)(),
    ...options,
  })
}

useSetPrimaryScenarioMutation.fetcher = (
  variables: Types.SetPrimaryScenarioMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.SetPrimaryScenarioMutation,
    Types.SetPrimaryScenarioMutationVariables
  >(SetPrimaryScenarioDocument, variables, options)

export const SetScenarioNameDocument = `
    mutation SetScenarioName($scenarioPid: String!, $name: String!) {
  scenario(scenarioPid: $scenarioPid) {
    setName(name: $name) {
      pid
      scenarios {
        pid
        name
      }
    }
  }
}
    `

export const useSetScenarioNameMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetScenarioNameMutation,
    TError,
    Types.SetScenarioNameMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetScenarioNameMutation,
    TError,
    Types.SetScenarioNameMutationVariables,
    TContext
  >({
    mutationKey: ["SetScenarioName"],
    mutationFn: (variables?: Types.SetScenarioNameMutationVariables) =>
      adminFetcher<
        Types.SetScenarioNameMutation,
        Types.SetScenarioNameMutationVariables
      >(SetScenarioNameDocument, variables)(),
    ...options,
  })
}

useSetScenarioNameMutation.fetcher = (
  variables: Types.SetScenarioNameMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetScenarioNameMutation, Types.SetScenarioNameMutationVariables>(
    SetScenarioNameDocument,
    variables,
    options,
  )

export const SetScenarioEtbDocument = `
    mutation SetScenarioETB($scenarioPid: String!, $etbId: Int) {
  scenario(scenarioPid: $scenarioPid) {
    setEtb(etbId: $etbId) {
      pid
      activeEtbReport {
        id
        projectName
      }
    }
  }
}
    `

export const useSetScenarioEtbMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetScenarioEtbMutation,
    TError,
    Types.SetScenarioEtbMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetScenarioEtbMutation,
    TError,
    Types.SetScenarioEtbMutationVariables,
    TContext
  >({
    mutationKey: ["SetScenarioETB"],
    mutationFn: (variables?: Types.SetScenarioEtbMutationVariables) =>
      adminFetcher<Types.SetScenarioEtbMutation, Types.SetScenarioEtbMutationVariables>(
        SetScenarioEtbDocument,
        variables,
      )(),
    ...options,
  })
}

useSetScenarioEtbMutation.fetcher = (
  variables: Types.SetScenarioEtbMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetScenarioEtbMutation, Types.SetScenarioEtbMutationVariables>(
    SetScenarioEtbDocument,
    variables,
    options,
  )

export const AddScenarioDocument = `
    mutation AddScenario($sitePid: String!, $solarLocation: SolarLocation!, $includeStorage: Boolean!, $loadCategory: LoadCategory!, $name: String) {
  site {
    addScenario(
      sitePid: $sitePid
      solarLocation: $solarLocation
      includeStorage: $includeStorage
      loadCategory: $loadCategory
      name: $name
    ) {
      __typename
      ... on ScenarioRef {
        pid
      }
      ... on Error {
        message
      }
    }
  }
}
    `

export const useAddScenarioMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AddScenarioMutation,
    TError,
    Types.AddScenarioMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddScenarioMutation,
    TError,
    Types.AddScenarioMutationVariables,
    TContext
  >({
    mutationKey: ["AddScenario"],
    mutationFn: (variables?: Types.AddScenarioMutationVariables) =>
      adminFetcher<Types.AddScenarioMutation, Types.AddScenarioMutationVariables>(
        AddScenarioDocument,
        variables,
      )(),
    ...options,
  })
}

useAddScenarioMutation.fetcher = (
  variables: Types.AddScenarioMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.AddScenarioMutation, Types.AddScenarioMutationVariables>(
    AddScenarioDocument,
    variables,
    options,
  )

export const ToggleScenarioVisibilityDocument = `
    mutation ToggleScenarioVisibility($scenarioPid: String!, $hidden: Boolean!) {
  scenario(scenarioPid: $scenarioPid) {
    toggleVisibility(hidden: $hidden) {
      pid
      hiddenFromCustomers
    }
  }
}
    `

export const useToggleScenarioVisibilityMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.ToggleScenarioVisibilityMutation,
    TError,
    Types.ToggleScenarioVisibilityMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.ToggleScenarioVisibilityMutation,
    TError,
    Types.ToggleScenarioVisibilityMutationVariables,
    TContext
  >({
    mutationKey: ["ToggleScenarioVisibility"],
    mutationFn: (variables?: Types.ToggleScenarioVisibilityMutationVariables) =>
      adminFetcher<
        Types.ToggleScenarioVisibilityMutation,
        Types.ToggleScenarioVisibilityMutationVariables
      >(ToggleScenarioVisibilityDocument, variables)(),
    ...options,
  })
}

useToggleScenarioVisibilityMutation.fetcher = (
  variables: Types.ToggleScenarioVisibilityMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.ToggleScenarioVisibilityMutation,
    Types.ToggleScenarioVisibilityMutationVariables
  >(ToggleScenarioVisibilityDocument, variables, options)

export const ToggleMulticonnectDocument = `
    mutation ToggleMulticonnect($scenarioPid: String!, $allow: Boolean!) {
  scenario(scenarioPid: $scenarioPid) {
    toggleMulticonnect(allow: $allow) {
      pid
      allowMulticonnect
    }
  }
}
    `

export const useToggleMulticonnectMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.ToggleMulticonnectMutation,
    TError,
    Types.ToggleMulticonnectMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.ToggleMulticonnectMutation,
    TError,
    Types.ToggleMulticonnectMutationVariables,
    TContext
  >({
    mutationKey: ["ToggleMulticonnect"],
    mutationFn: (variables?: Types.ToggleMulticonnectMutationVariables) =>
      adminFetcher<
        Types.ToggleMulticonnectMutation,
        Types.ToggleMulticonnectMutationVariables
      >(ToggleMulticonnectDocument, variables)(),
    ...options,
  })
}

useToggleMulticonnectMutation.fetcher = (
  variables: Types.ToggleMulticonnectMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.ToggleMulticonnectMutation,
    Types.ToggleMulticonnectMutationVariables
  >(ToggleMulticonnectDocument, variables, options)

export const AddEtbDocument = `
    mutation AddETB($sitePid: String!, $scenarioPid: String!, $loadOrigin: LoadOrigin!, $upload: Upload!, $roofPvKw: Float, $carportPvKw: Float, $makeActive: Boolean!) {
  site {
    addEtb(
      sitePid: $sitePid
      scenarioPid: $scenarioPid
      loadOrigin: $loadOrigin
      upload: $upload
      roofPvKw: $roofPvKw
      carportPvKw: $carportPvKw
      makeActive: $makeActive
    ) {
      pid
    }
  }
}
    `

export const useAddEtbMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AddEtbMutation,
    TError,
    Types.AddEtbMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddEtbMutation,
    TError,
    Types.AddEtbMutationVariables,
    TContext
  >({
    mutationKey: ["AddETB"],
    mutationFn: (variables?: Types.AddEtbMutationVariables) =>
      adminFetcher<Types.AddEtbMutation, Types.AddEtbMutationVariables>(
        AddEtbDocument,
        variables,
      )(),
    ...options,
  })
}

useAddEtbMutation.fetcher = (
  variables: Types.AddEtbMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.AddEtbMutation, Types.AddEtbMutationVariables>(
    AddEtbDocument,
    variables,
    options,
  )

export const AddSitesToPortfolioByOwnerNameDocument = `
    mutation AddSitesToPortfolioByOwnerName($portfolioPid: String!, $names: [String!]!) {
  portfolio {
    addSitesByOwnerName(portfolioPid: $portfolioPid, names: $names)
  }
}
    `

export const useAddSitesToPortfolioByOwnerNameMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.AddSitesToPortfolioByOwnerNameMutation,
    TError,
    Types.AddSitesToPortfolioByOwnerNameMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddSitesToPortfolioByOwnerNameMutation,
    TError,
    Types.AddSitesToPortfolioByOwnerNameMutationVariables,
    TContext
  >({
    mutationKey: ["AddSitesToPortfolioByOwnerName"],
    mutationFn: (variables?: Types.AddSitesToPortfolioByOwnerNameMutationVariables) =>
      adminFetcher<
        Types.AddSitesToPortfolioByOwnerNameMutation,
        Types.AddSitesToPortfolioByOwnerNameMutationVariables
      >(AddSitesToPortfolioByOwnerNameDocument, variables)(),
    ...options,
  })
}

useAddSitesToPortfolioByOwnerNameMutation.fetcher = (
  variables: Types.AddSitesToPortfolioByOwnerNameMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.AddSitesToPortfolioByOwnerNameMutation,
    Types.AddSitesToPortfolioByOwnerNameMutationVariables
  >(AddSitesToPortfolioByOwnerNameDocument, variables, options)

export const SetTargetUtilityDocument = `
    mutation SetTargetUtility($sitePid: String!, $utilityId: Int!) {
  site {
    setTargetUtility(sitePid: $sitePid, utilityId: $utilityId) {
      pid
      targetUtility {
        id
        name
      }
      targetUtilitySetBy {
        ...UserRef
      }
      dateTargetUtilitySet
    }
  }
}
    ${UserRefFragmentDoc}`

export const useSetTargetUtilityMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetTargetUtilityMutation,
    TError,
    Types.SetTargetUtilityMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetTargetUtilityMutation,
    TError,
    Types.SetTargetUtilityMutationVariables,
    TContext
  >({
    mutationKey: ["SetTargetUtility"],
    mutationFn: (variables?: Types.SetTargetUtilityMutationVariables) =>
      adminFetcher<
        Types.SetTargetUtilityMutation,
        Types.SetTargetUtilityMutationVariables
      >(SetTargetUtilityDocument, variables)(),
    ...options,
  })
}

useSetTargetUtilityMutation.fetcher = (
  variables: Types.SetTargetUtilityMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetTargetUtilityMutation, Types.SetTargetUtilityMutationVariables>(
    SetTargetUtilityDocument,
    variables,
    options,
  )

export const SetThirdPartySupplierDocument = `
    mutation SetThirdPartySupplier($sitePid: String!, $thirdPartySupplierId: Int) {
  site {
    setThirdPartySupplier(
      sitePid: $sitePid
      thirdPartySupplierId: $thirdPartySupplierId
    ) {
      pid
      thirdPartySupplier {
        id
        name
      }
    }
  }
}
    `

export const useSetThirdPartySupplierMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetThirdPartySupplierMutation,
    TError,
    Types.SetThirdPartySupplierMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetThirdPartySupplierMutation,
    TError,
    Types.SetThirdPartySupplierMutationVariables,
    TContext
  >({
    mutationKey: ["SetThirdPartySupplier"],
    mutationFn: (variables?: Types.SetThirdPartySupplierMutationVariables) =>
      adminFetcher<
        Types.SetThirdPartySupplierMutation,
        Types.SetThirdPartySupplierMutationVariables
      >(SetThirdPartySupplierDocument, variables)(),
    ...options,
  })
}

useSetThirdPartySupplierMutation.fetcher = (
  variables: Types.SetThirdPartySupplierMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.SetThirdPartySupplierMutation,
    Types.SetThirdPartySupplierMutationVariables
  >(SetThirdPartySupplierDocument, variables, options)

export const SetMeteringDocument = `
    mutation SetMetering($sitePids: [String!]!, $metering: Metering) {
  site {
    setMetering(sitePids: $sitePids, metering: $metering)
  }
}
    `

export const useSetMeteringMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetMeteringMutation,
    TError,
    Types.SetMeteringMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetMeteringMutation,
    TError,
    Types.SetMeteringMutationVariables,
    TContext
  >({
    mutationKey: ["SetMetering"],
    mutationFn: (variables?: Types.SetMeteringMutationVariables) =>
      adminFetcher<Types.SetMeteringMutation, Types.SetMeteringMutationVariables>(
        SetMeteringDocument,
        variables,
      )(),
    ...options,
  })
}

useSetMeteringMutation.fetcher = (
  variables: Types.SetMeteringMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetMeteringMutation, Types.SetMeteringMutationVariables>(
    SetMeteringDocument,
    variables,
    options,
  )

export const SetLeaseTypeDocument = `
    mutation SetLeaseType($sitePids: [String!]!, $leaseType: LeaseType) {
  site {
    setLeaseType(sitePids: $sitePids, leaseType: $leaseType)
  }
}
    `

export const useSetLeaseTypeMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetLeaseTypeMutation,
    TError,
    Types.SetLeaseTypeMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetLeaseTypeMutation,
    TError,
    Types.SetLeaseTypeMutationVariables,
    TContext
  >({
    mutationKey: ["SetLeaseType"],
    mutationFn: (variables?: Types.SetLeaseTypeMutationVariables) =>
      adminFetcher<Types.SetLeaseTypeMutation, Types.SetLeaseTypeMutationVariables>(
        SetLeaseTypeDocument,
        variables,
      )(),
    ...options,
  })
}

useSetLeaseTypeMutation.fetcher = (
  variables: Types.SetLeaseTypeMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetLeaseTypeMutation, Types.SetLeaseTypeMutationVariables>(
    SetLeaseTypeDocument,
    variables,
    options,
  )

export const SetOccupancyTypeDocument = `
    mutation SetOccupancyType($sitePids: [String!]!, $occupancyType: BuildingOccupancyType) {
  site {
    setOccupancyType(sitePids: $sitePids, occupancyType: $occupancyType)
  }
}
    `

export const useSetOccupancyTypeMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetOccupancyTypeMutation,
    TError,
    Types.SetOccupancyTypeMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetOccupancyTypeMutation,
    TError,
    Types.SetOccupancyTypeMutationVariables,
    TContext
  >({
    mutationKey: ["SetOccupancyType"],
    mutationFn: (variables?: Types.SetOccupancyTypeMutationVariables) =>
      adminFetcher<
        Types.SetOccupancyTypeMutation,
        Types.SetOccupancyTypeMutationVariables
      >(SetOccupancyTypeDocument, variables)(),
    ...options,
  })
}

useSetOccupancyTypeMutation.fetcher = (
  variables: Types.SetOccupancyTypeMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetOccupancyTypeMutation, Types.SetOccupancyTypeMutationVariables>(
    SetOccupancyTypeDocument,
    variables,
    options,
  )

export const RemoveUserFromPortfolioDocument = `
    mutation RemoveUserFromPortfolio($portfolioPid: String!, $userId: Int!) {
  portfolio {
    removeUserFromPortfolio(portfolioPid: $portfolioPid, userId: $userId) {
      pid
      users {
        id
        email
      }
    }
  }
}
    `

export const useRemoveUserFromPortfolioMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.RemoveUserFromPortfolioMutation,
    TError,
    Types.RemoveUserFromPortfolioMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RemoveUserFromPortfolioMutation,
    TError,
    Types.RemoveUserFromPortfolioMutationVariables,
    TContext
  >({
    mutationKey: ["RemoveUserFromPortfolio"],
    mutationFn: (variables?: Types.RemoveUserFromPortfolioMutationVariables) =>
      adminFetcher<
        Types.RemoveUserFromPortfolioMutation,
        Types.RemoveUserFromPortfolioMutationVariables
      >(RemoveUserFromPortfolioDocument, variables)(),
    ...options,
  })
}

useRemoveUserFromPortfolioMutation.fetcher = (
  variables: Types.RemoveUserFromPortfolioMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.RemoveUserFromPortfolioMutation,
    Types.RemoveUserFromPortfolioMutationVariables
  >(RemoveUserFromPortfolioDocument, variables, options)

export const RenamePortfolioDocument = `
    mutation RenamePortfolio($portfolioPid: String!, $newName: String!) {
  portfolio {
    rename(portfolioPid: $portfolioPid, newName: $newName) {
      pid
      title
    }
  }
}
    `

export const useRenamePortfolioMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.RenamePortfolioMutation,
    TError,
    Types.RenamePortfolioMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RenamePortfolioMutation,
    TError,
    Types.RenamePortfolioMutationVariables,
    TContext
  >({
    mutationKey: ["RenamePortfolio"],
    mutationFn: (variables?: Types.RenamePortfolioMutationVariables) =>
      adminFetcher<
        Types.RenamePortfolioMutation,
        Types.RenamePortfolioMutationVariables
      >(RenamePortfolioDocument, variables)(),
    ...options,
  })
}

useRenamePortfolioMutation.fetcher = (
  variables: Types.RenamePortfolioMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.RenamePortfolioMutation, Types.RenamePortfolioMutationVariables>(
    RenamePortfolioDocument,
    variables,
    options,
  )

export const UtilityApiNameDocument = `
    mutation UtilityApiName($utilityId: Int!, $utilityApiName: String) {
  utility {
    setUtilityApiName(id: $utilityId, utilityApiName: $utilityApiName) {
      id
      utilityApiName
    }
  }
}
    `

export const useUtilityApiNameMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UtilityApiNameMutation,
    TError,
    Types.UtilityApiNameMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UtilityApiNameMutation,
    TError,
    Types.UtilityApiNameMutationVariables,
    TContext
  >({
    mutationKey: ["UtilityApiName"],
    mutationFn: (variables?: Types.UtilityApiNameMutationVariables) =>
      adminFetcher<Types.UtilityApiNameMutation, Types.UtilityApiNameMutationVariables>(
        UtilityApiNameDocument,
        variables,
      )(),
    ...options,
  })
}

useUtilityApiNameMutation.fetcher = (
  variables: Types.UtilityApiNameMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.UtilityApiNameMutation, Types.UtilityApiNameMutationVariables>(
    UtilityApiNameDocument,
    variables,
    options,
  )

export const UtilityIsPreferredDocument = `
    mutation UtilityIsPreferred($utilityId: Int!, $isPreferred: Boolean) {
  utility {
    setIsPreferred(id: $utilityId, isPreferred: $isPreferred) {
      id
      isPreferred
    }
  }
}
    `

export const useUtilityIsPreferredMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UtilityIsPreferredMutation,
    TError,
    Types.UtilityIsPreferredMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UtilityIsPreferredMutation,
    TError,
    Types.UtilityIsPreferredMutationVariables,
    TContext
  >({
    mutationKey: ["UtilityIsPreferred"],
    mutationFn: (variables?: Types.UtilityIsPreferredMutationVariables) =>
      adminFetcher<
        Types.UtilityIsPreferredMutation,
        Types.UtilityIsPreferredMutationVariables
      >(UtilityIsPreferredDocument, variables)(),
    ...options,
  })
}

useUtilityIsPreferredMutation.fetcher = (
  variables: Types.UtilityIsPreferredMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.UtilityIsPreferredMutation,
    Types.UtilityIsPreferredMutationVariables
  >(UtilityIsPreferredDocument, variables, options)

export const UtilityNotesDocument = `
    mutation UtilityNotes($utilityId: Int!, $notes: String) {
  utility {
    setNotes(id: $utilityId, notes: $notes) {
      id
      notes
    }
  }
}
    `

export const useUtilityNotesMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UtilityNotesMutation,
    TError,
    Types.UtilityNotesMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UtilityNotesMutation,
    TError,
    Types.UtilityNotesMutationVariables,
    TContext
  >({
    mutationKey: ["UtilityNotes"],
    mutationFn: (variables?: Types.UtilityNotesMutationVariables) =>
      adminFetcher<Types.UtilityNotesMutation, Types.UtilityNotesMutationVariables>(
        UtilityNotesDocument,
        variables,
      )(),
    ...options,
  })
}

useUtilityNotesMutation.fetcher = (
  variables: Types.UtilityNotesMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.UtilityNotesMutation, Types.UtilityNotesMutationVariables>(
    UtilityNotesDocument,
    variables,
    options,
  )

export const CreatePropertyNoteDocument = `
    mutation CreatePropertyNote($propertyPid: String!, $text: String!, $hiddenFromCustomers: Boolean!) {
  note {
    new(
      sitePid: $propertyPid
      text: $text
      hiddenFromCustomers: $hiddenFromCustomers
    ) {
      pid
    }
  }
}
    `

export const useCreatePropertyNoteMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CreatePropertyNoteMutation,
    TError,
    Types.CreatePropertyNoteMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreatePropertyNoteMutation,
    TError,
    Types.CreatePropertyNoteMutationVariables,
    TContext
  >({
    mutationKey: ["CreatePropertyNote"],
    mutationFn: (variables?: Types.CreatePropertyNoteMutationVariables) =>
      adminFetcher<
        Types.CreatePropertyNoteMutation,
        Types.CreatePropertyNoteMutationVariables
      >(CreatePropertyNoteDocument, variables)(),
    ...options,
  })
}

useCreatePropertyNoteMutation.fetcher = (
  variables: Types.CreatePropertyNoteMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.CreatePropertyNoteMutation,
    Types.CreatePropertyNoteMutationVariables
  >(CreatePropertyNoteDocument, variables, options)

export const EditPropertyNoteDocument = `
    mutation EditPropertyNote($notePid: String!, $text: String!, $hiddenFromCustomers: Boolean!) {
  note {
    edit(notePid: $notePid, text: $text, hiddenFromCustomers: $hiddenFromCustomers) {
      pid
    }
  }
}
    `

export const useEditPropertyNoteMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.EditPropertyNoteMutation,
    TError,
    Types.EditPropertyNoteMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.EditPropertyNoteMutation,
    TError,
    Types.EditPropertyNoteMutationVariables,
    TContext
  >({
    mutationKey: ["EditPropertyNote"],
    mutationFn: (variables?: Types.EditPropertyNoteMutationVariables) =>
      adminFetcher<
        Types.EditPropertyNoteMutation,
        Types.EditPropertyNoteMutationVariables
      >(EditPropertyNoteDocument, variables)(),
    ...options,
  })
}

useEditPropertyNoteMutation.fetcher = (
  variables: Types.EditPropertyNoteMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.EditPropertyNoteMutation, Types.EditPropertyNoteMutationVariables>(
    EditPropertyNoteDocument,
    variables,
    options,
  )

export const TrashPropertyNoteDocument = `
    mutation TrashPropertyNote($notePid: String!) {
  note {
    trash(notePid: $notePid) {
      pid
    }
  }
}
    `

export const useTrashPropertyNoteMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.TrashPropertyNoteMutation,
    TError,
    Types.TrashPropertyNoteMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.TrashPropertyNoteMutation,
    TError,
    Types.TrashPropertyNoteMutationVariables,
    TContext
  >({
    mutationKey: ["TrashPropertyNote"],
    mutationFn: (variables?: Types.TrashPropertyNoteMutationVariables) =>
      adminFetcher<
        Types.TrashPropertyNoteMutation,
        Types.TrashPropertyNoteMutationVariables
      >(TrashPropertyNoteDocument, variables)(),
    ...options,
  })
}

useTrashPropertyNoteMutation.fetcher = (
  variables: Types.TrashPropertyNoteMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.TrashPropertyNoteMutation,
    Types.TrashPropertyNoteMutationVariables
  >(TrashPropertyNoteDocument, variables, options)

export const SetScenarioHelioscopeDocument = `
    mutation SetScenarioHelioscope($scenarioPid: String!, $helioscopePid: String, $finproductId: Int, $finproductType: FinProductType) {
  scenario(scenarioPid: $scenarioPid) {
    setHelioscope(
      helioscopePid: $helioscopePid
      finproductId: $finproductId
      finproductType: $finproductType
    ) {
      __typename
      pid
    }
  }
}
    `

export const useSetScenarioHelioscopeMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetScenarioHelioscopeMutation,
    TError,
    Types.SetScenarioHelioscopeMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetScenarioHelioscopeMutation,
    TError,
    Types.SetScenarioHelioscopeMutationVariables,
    TContext
  >({
    mutationKey: ["SetScenarioHelioscope"],
    mutationFn: (variables?: Types.SetScenarioHelioscopeMutationVariables) =>
      adminFetcher<
        Types.SetScenarioHelioscopeMutation,
        Types.SetScenarioHelioscopeMutationVariables
      >(SetScenarioHelioscopeDocument, variables)(),
    ...options,
  })
}

useSetScenarioHelioscopeMutation.fetcher = (
  variables: Types.SetScenarioHelioscopeMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.SetScenarioHelioscopeMutation,
    Types.SetScenarioHelioscopeMutationVariables
  >(SetScenarioHelioscopeDocument, variables, options)

export const TrashHelioscopeDocument = `
    mutation TrashHelioscope($sitePid: String!, $helioscopePid: String!) {
  site {
    trashHelioscope(sitePid: $sitePid, helioscopePid: $helioscopePid) {
      pid
    }
  }
}
    `

export const useTrashHelioscopeMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.TrashHelioscopeMutation,
    TError,
    Types.TrashHelioscopeMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.TrashHelioscopeMutation,
    TError,
    Types.TrashHelioscopeMutationVariables,
    TContext
  >({
    mutationKey: ["TrashHelioscope"],
    mutationFn: (variables?: Types.TrashHelioscopeMutationVariables) =>
      adminFetcher<
        Types.TrashHelioscopeMutation,
        Types.TrashHelioscopeMutationVariables
      >(TrashHelioscopeDocument, variables)(),
    ...options,
  })
}

useTrashHelioscopeMutation.fetcher = (
  variables: Types.TrashHelioscopeMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.TrashHelioscopeMutation, Types.TrashHelioscopeMutationVariables>(
    TrashHelioscopeDocument,
    variables,
    options,
  )

export const SetOrgNameDocument = `
    mutation setOrgName($pid: String!, $value: String!) {
  orgs {
    setName(pid: $pid, name: $value) {
      pid
      name
    }
  }
}
    `

export const useSetOrgNameMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetOrgNameMutation,
    TError,
    Types.SetOrgNameMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetOrgNameMutation,
    TError,
    Types.SetOrgNameMutationVariables,
    TContext
  >({
    mutationKey: ["setOrgName"],
    mutationFn: (variables?: Types.SetOrgNameMutationVariables) =>
      adminFetcher<Types.SetOrgNameMutation, Types.SetOrgNameMutationVariables>(
        SetOrgNameDocument,
        variables,
      )(),
    ...options,
  })
}

useSetOrgNameMutation.fetcher = (
  variables: Types.SetOrgNameMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetOrgNameMutation, Types.SetOrgNameMutationVariables>(
    SetOrgNameDocument,
    variables,
    options,
  )

export const SetOrgSlugDocument = `
    mutation setOrgSlug($pid: String!, $value: String!) {
  orgs {
    setSlug(pid: $pid, slug: $value) {
      pid
      slug
    }
  }
}
    `

export const useSetOrgSlugMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetOrgSlugMutation,
    TError,
    Types.SetOrgSlugMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetOrgSlugMutation,
    TError,
    Types.SetOrgSlugMutationVariables,
    TContext
  >({
    mutationKey: ["setOrgSlug"],
    mutationFn: (variables?: Types.SetOrgSlugMutationVariables) =>
      adminFetcher<Types.SetOrgSlugMutation, Types.SetOrgSlugMutationVariables>(
        SetOrgSlugDocument,
        variables,
      )(),
    ...options,
  })
}

useSetOrgSlugMutation.fetcher = (
  variables: Types.SetOrgSlugMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetOrgSlugMutation, Types.SetOrgSlugMutationVariables>(
    SetOrgSlugDocument,
    variables,
    options,
  )

export const SetOrgLogoDocument = `
    mutation setOrgLogo($pid: String!, $upload: Upload!) {
  orgs {
    setLogo(pid: $pid, upload: $upload) {
      pid
      slug
      logoUrl
    }
  }
}
    `

export const useSetOrgLogoMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetOrgLogoMutation,
    TError,
    Types.SetOrgLogoMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetOrgLogoMutation,
    TError,
    Types.SetOrgLogoMutationVariables,
    TContext
  >({
    mutationKey: ["setOrgLogo"],
    mutationFn: (variables?: Types.SetOrgLogoMutationVariables) =>
      adminFetcher<Types.SetOrgLogoMutation, Types.SetOrgLogoMutationVariables>(
        SetOrgLogoDocument,
        variables,
      )(),
    ...options,
  })
}

useSetOrgLogoMutation.fetcher = (
  variables: Types.SetOrgLogoMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetOrgLogoMutation, Types.SetOrgLogoMutationVariables>(
    SetOrgLogoDocument,
    variables,
    options,
  )

export const SetOrgLogomarkDocument = `
    mutation setOrgLogomark($pid: String!, $upload: Upload!) {
  orgs {
    setLogomark(pid: $pid, upload: $upload) {
      pid
      slug
      logoUrl
    }
  }
}
    `

export const useSetOrgLogomarkMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetOrgLogomarkMutation,
    TError,
    Types.SetOrgLogomarkMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetOrgLogomarkMutation,
    TError,
    Types.SetOrgLogomarkMutationVariables,
    TContext
  >({
    mutationKey: ["setOrgLogomark"],
    mutationFn: (variables?: Types.SetOrgLogomarkMutationVariables) =>
      adminFetcher<Types.SetOrgLogomarkMutation, Types.SetOrgLogomarkMutationVariables>(
        SetOrgLogomarkDocument,
        variables,
      )(),
    ...options,
  })
}

useSetOrgLogomarkMutation.fetcher = (
  variables: Types.SetOrgLogomarkMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetOrgLogomarkMutation, Types.SetOrgLogomarkMutationVariables>(
    SetOrgLogomarkDocument,
    variables,
    options,
  )

export const SetOrgAllowlistDocument = `
    mutation SetOrgAllowlist($orgPid: String!, $domains: [String!]) {
  orgs {
    setAllowedEmailDomains(pid: $orgPid, allowedEmailDomains: $domains) {
      pid
      allowedEmailDomains
    }
  }
}
    `

export const useSetOrgAllowlistMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetOrgAllowlistMutation,
    TError,
    Types.SetOrgAllowlistMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetOrgAllowlistMutation,
    TError,
    Types.SetOrgAllowlistMutationVariables,
    TContext
  >({
    mutationKey: ["SetOrgAllowlist"],
    mutationFn: (variables?: Types.SetOrgAllowlistMutationVariables) =>
      adminFetcher<
        Types.SetOrgAllowlistMutation,
        Types.SetOrgAllowlistMutationVariables
      >(SetOrgAllowlistDocument, variables)(),
    ...options,
  })
}

useSetOrgAllowlistMutation.fetcher = (
  variables: Types.SetOrgAllowlistMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetOrgAllowlistMutation, Types.SetOrgAllowlistMutationVariables>(
    SetOrgAllowlistDocument,
    variables,
    options,
  )

export const SetUserAvatarDocument = `
    mutation setUserAvatar($userId: Int!, $upload: Upload!) {
  user {
    setAvatar(userId: $userId, upload: $upload) {
      avatarUrl
    }
  }
}
    `

export const useSetUserAvatarMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetUserAvatarMutation,
    TError,
    Types.SetUserAvatarMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetUserAvatarMutation,
    TError,
    Types.SetUserAvatarMutationVariables,
    TContext
  >({
    mutationKey: ["setUserAvatar"],
    mutationFn: (variables?: Types.SetUserAvatarMutationVariables) =>
      adminFetcher<Types.SetUserAvatarMutation, Types.SetUserAvatarMutationVariables>(
        SetUserAvatarDocument,
        variables,
      )(),
    ...options,
  })
}

useSetUserAvatarMutation.fetcher = (
  variables: Types.SetUserAvatarMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetUserAvatarMutation, Types.SetUserAvatarMutationVariables>(
    SetUserAvatarDocument,
    variables,
    options,
  )

export const UpdateUserOrgMembershipDocument = `
    mutation updateUserOrgMembership($userId: Int!, $orgPid: String!) {
  user {
    updateOrgMembership(userId: $userId, orgPid: $orgPid) {
      ...UserListUser
    }
  }
}
    ${UserListUserFragmentDoc}`

export const useUpdateUserOrgMembershipMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.UpdateUserOrgMembershipMutation,
    TError,
    Types.UpdateUserOrgMembershipMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UpdateUserOrgMembershipMutation,
    TError,
    Types.UpdateUserOrgMembershipMutationVariables,
    TContext
  >({
    mutationKey: ["updateUserOrgMembership"],
    mutationFn: (variables?: Types.UpdateUserOrgMembershipMutationVariables) =>
      adminFetcher<
        Types.UpdateUserOrgMembershipMutation,
        Types.UpdateUserOrgMembershipMutationVariables
      >(UpdateUserOrgMembershipDocument, variables)(),
    ...options,
  })
}

useUpdateUserOrgMembershipMutation.fetcher = (
  variables: Types.UpdateUserOrgMembershipMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.UpdateUserOrgMembershipMutation,
    Types.UpdateUserOrgMembershipMutationVariables
  >(UpdateUserOrgMembershipDocument, variables, options)

export const RemoveUserOrgMembershipDocument = `
    mutation removeUserOrgMembership($userId: Int!) {
  user {
    removeOrgMembership(userId: $userId) {
      ...UserListUser
    }
  }
}
    ${UserListUserFragmentDoc}`

export const useRemoveUserOrgMembershipMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.RemoveUserOrgMembershipMutation,
    TError,
    Types.RemoveUserOrgMembershipMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RemoveUserOrgMembershipMutation,
    TError,
    Types.RemoveUserOrgMembershipMutationVariables,
    TContext
  >({
    mutationKey: ["removeUserOrgMembership"],
    mutationFn: (variables?: Types.RemoveUserOrgMembershipMutationVariables) =>
      adminFetcher<
        Types.RemoveUserOrgMembershipMutation,
        Types.RemoveUserOrgMembershipMutationVariables
      >(RemoveUserOrgMembershipDocument, variables)(),
    ...options,
  })
}

useRemoveUserOrgMembershipMutation.fetcher = (
  variables: Types.RemoveUserOrgMembershipMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.RemoveUserOrgMembershipMutation,
    Types.RemoveUserOrgMembershipMutationVariables
  >(RemoveUserOrgMembershipDocument, variables, options)

export const CreateCustomTariffDocument = `
    mutation CreateCustomTariff($sitePid: String!, $utilityId: Int!, $cfg: String!) {
  tariff {
    createCustomTariff(sitePid: $sitePid, utilityId: $utilityId, cfg: $cfg) {
      id
      name
      code
      isCustom
      utility {
        id
        name
      }
    }
  }
}
    `

export const useCreateCustomTariffMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CreateCustomTariffMutation,
    TError,
    Types.CreateCustomTariffMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateCustomTariffMutation,
    TError,
    Types.CreateCustomTariffMutationVariables,
    TContext
  >({
    mutationKey: ["CreateCustomTariff"],
    mutationFn: (variables?: Types.CreateCustomTariffMutationVariables) =>
      adminFetcher<
        Types.CreateCustomTariffMutation,
        Types.CreateCustomTariffMutationVariables
      >(CreateCustomTariffDocument, variables)(),
    ...options,
  })
}

useCreateCustomTariffMutation.fetcher = (
  variables: Types.CreateCustomTariffMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.CreateCustomTariffMutation,
    Types.CreateCustomTariffMutationVariables
  >(CreateCustomTariffDocument, variables, options)

export const SetScenarioConfigDocument = `
    mutation SetScenarioConfig($scenarioPid: String!, $finproductId: Int, $finproductType: FinProductType, $helioscopePid: String, $goal: PVoptGoal, $roofKw: Float, $carportKw: Float, $groundKw: Float, $batteryProduct: BatteryProductInput) {
  scenario(scenarioPid: $scenarioPid) {
    setConfig(
      finproductId: $finproductId
      finproductType: $finproductType
      goal: $goal
      roofKw: $roofKw
      carportKw: $carportKw
      groundKw: $groundKw
      helioscopePid: $helioscopePid
      batteryProduct: $batteryProduct
    ) {
      pid
    }
  }
}
    `

export const useSetScenarioConfigMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetScenarioConfigMutation,
    TError,
    Types.SetScenarioConfigMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetScenarioConfigMutation,
    TError,
    Types.SetScenarioConfigMutationVariables,
    TContext
  >({
    mutationKey: ["SetScenarioConfig"],
    mutationFn: (variables?: Types.SetScenarioConfigMutationVariables) =>
      adminFetcher<
        Types.SetScenarioConfigMutation,
        Types.SetScenarioConfigMutationVariables
      >(SetScenarioConfigDocument, variables)(),
    ...options,
  })
}

useSetScenarioConfigMutation.fetcher = (
  variables: Types.SetScenarioConfigMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.SetScenarioConfigMutation,
    Types.SetScenarioConfigMutationVariables
  >(SetScenarioConfigDocument, variables, options)

export const RemoveScenarioConfigDocument = `
    mutation RemoveScenarioConfig($scenarioPid: String!, $scenarioConfigId: Int!) {
  scenario(scenarioPid: $scenarioPid) {
    removeConfig(scenarioConfigId: $scenarioConfigId)
  }
}
    `

export const useRemoveScenarioConfigMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.RemoveScenarioConfigMutation,
    TError,
    Types.RemoveScenarioConfigMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RemoveScenarioConfigMutation,
    TError,
    Types.RemoveScenarioConfigMutationVariables,
    TContext
  >({
    mutationKey: ["RemoveScenarioConfig"],
    mutationFn: (variables?: Types.RemoveScenarioConfigMutationVariables) =>
      adminFetcher<
        Types.RemoveScenarioConfigMutation,
        Types.RemoveScenarioConfigMutationVariables
      >(RemoveScenarioConfigDocument, variables)(),
    ...options,
  })
}

useRemoveScenarioConfigMutation.fetcher = (
  variables: Types.RemoveScenarioConfigMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.RemoveScenarioConfigMutation,
    Types.RemoveScenarioConfigMutationVariables
  >(RemoveScenarioConfigDocument, variables, options)

export const RunLuxScenariosDocument = `
    mutation RunLuxScenarios($sitePid: String!) {
  site {
    runLux(sitePid: $sitePid)
  }
}
    `

export const useRunLuxScenariosMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.RunLuxScenariosMutation,
    TError,
    Types.RunLuxScenariosMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RunLuxScenariosMutation,
    TError,
    Types.RunLuxScenariosMutationVariables,
    TContext
  >({
    mutationKey: ["RunLuxScenarios"],
    mutationFn: (variables?: Types.RunLuxScenariosMutationVariables) =>
      adminFetcher<
        Types.RunLuxScenariosMutation,
        Types.RunLuxScenariosMutationVariables
      >(RunLuxScenariosDocument, variables)(),
    ...options,
  })
}

useRunLuxScenariosMutation.fetcher = (
  variables: Types.RunLuxScenariosMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.RunLuxScenariosMutation, Types.RunLuxScenariosMutationVariables>(
    RunLuxScenariosDocument,
    variables,
    options,
  )

export const RunLuxOrgDocument = `
    mutation RunLuxOrg($pid: String!) {
  orgs {
    runLux(pid: $pid)
  }
}
    `

export const useRunLuxOrgMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.RunLuxOrgMutation,
    TError,
    Types.RunLuxOrgMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RunLuxOrgMutation,
    TError,
    Types.RunLuxOrgMutationVariables,
    TContext
  >({
    mutationKey: ["RunLuxOrg"],
    mutationFn: (variables?: Types.RunLuxOrgMutationVariables) =>
      adminFetcher<Types.RunLuxOrgMutation, Types.RunLuxOrgMutationVariables>(
        RunLuxOrgDocument,
        variables,
      )(),
    ...options,
  })
}

useRunLuxOrgMutation.fetcher = (
  variables: Types.RunLuxOrgMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.RunLuxOrgMutation, Types.RunLuxOrgMutationVariables>(
    RunLuxOrgDocument,
    variables,
    options,
  )

export const RunLuxPortfolioDocument = `
    mutation RunLuxPortfolio($portfolioPid: String!) {
  portfolio {
    runLux(portfolioPid: $portfolioPid)
  }
}
    `

export const useRunLuxPortfolioMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.RunLuxPortfolioMutation,
    TError,
    Types.RunLuxPortfolioMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RunLuxPortfolioMutation,
    TError,
    Types.RunLuxPortfolioMutationVariables,
    TContext
  >({
    mutationKey: ["RunLuxPortfolio"],
    mutationFn: (variables?: Types.RunLuxPortfolioMutationVariables) =>
      adminFetcher<
        Types.RunLuxPortfolioMutation,
        Types.RunLuxPortfolioMutationVariables
      >(RunLuxPortfolioDocument, variables)(),
    ...options,
  })
}

useRunLuxPortfolioMutation.fetcher = (
  variables: Types.RunLuxPortfolioMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.RunLuxPortfolioMutation, Types.RunLuxPortfolioMutationVariables>(
    RunLuxPortfolioDocument,
    variables,
    options,
  )

export const RunLuxFinproductDocument = `
    mutation RunLuxFinproduct($id: Int!) {
  finproduct {
    runLux(id: $id)
  }
}
    `

export const useRunLuxFinproductMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.RunLuxFinproductMutation,
    TError,
    Types.RunLuxFinproductMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RunLuxFinproductMutation,
    TError,
    Types.RunLuxFinproductMutationVariables,
    TContext
  >({
    mutationKey: ["RunLuxFinproduct"],
    mutationFn: (variables?: Types.RunLuxFinproductMutationVariables) =>
      adminFetcher<
        Types.RunLuxFinproductMutation,
        Types.RunLuxFinproductMutationVariables
      >(RunLuxFinproductDocument, variables)(),
    ...options,
  })
}

useRunLuxFinproductMutation.fetcher = (
  variables: Types.RunLuxFinproductMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.RunLuxFinproductMutation, Types.RunLuxFinproductMutationVariables>(
    RunLuxFinproductDocument,
    variables,
    options,
  )

export const SetBauMeterTariffDocument = `
    mutation SetBauMeterTariff($input: SetBauMeterTariffInput!) {
  site {
    setBauMeterTariff(input: $input) {
      pid
    }
  }
}
    `

export const useSetBauMeterTariffMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetBauMeterTariffMutation,
    TError,
    Types.SetBauMeterTariffMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBauMeterTariffMutation,
    TError,
    Types.SetBauMeterTariffMutationVariables,
    TContext
  >({
    mutationKey: ["SetBauMeterTariff"],
    mutationFn: (variables?: Types.SetBauMeterTariffMutationVariables) =>
      adminFetcher<
        Types.SetBauMeterTariffMutation,
        Types.SetBauMeterTariffMutationVariables
      >(SetBauMeterTariffDocument, variables)(),
    ...options,
  })
}

useSetBauMeterTariffMutation.fetcher = (
  variables: Types.SetBauMeterTariffMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.SetBauMeterTariffMutation,
    Types.SetBauMeterTariffMutationVariables
  >(SetBauMeterTariffDocument, variables, options)

export const SetBauMeterLabelDocument = `
    mutation SetBauMeterLabel($input: SetBauMeterLabelInput!) {
  site {
    setBauMeterLabel(input: $input) {
      pid
    }
  }
}
    `

export const useSetBauMeterLabelMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetBauMeterLabelMutation,
    TError,
    Types.SetBauMeterLabelMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBauMeterLabelMutation,
    TError,
    Types.SetBauMeterLabelMutationVariables,
    TContext
  >({
    mutationKey: ["SetBauMeterLabel"],
    mutationFn: (variables?: Types.SetBauMeterLabelMutationVariables) =>
      adminFetcher<
        Types.SetBauMeterLabelMutation,
        Types.SetBauMeterLabelMutationVariables
      >(SetBauMeterLabelDocument, variables)(),
    ...options,
  })
}

useSetBauMeterLabelMutation.fetcher = (
  variables: Types.SetBauMeterLabelMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetBauMeterLabelMutation, Types.SetBauMeterLabelMutationVariables>(
    SetBauMeterLabelDocument,
    variables,
    options,
  )

export const OrgFinRelToggleDocument = `
    mutation OrgFinRelToggle($orgPid: String!, $finproductId: Int!, $enable: Boolean!) {
  orgs {
    finrelToggle(orgPid: $orgPid, finproductId: $finproductId, enable: $enable) {
      ...FinRel
    }
  }
}
    ${FinRelFragmentDoc}`

export const useOrgFinRelToggleMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.OrgFinRelToggleMutation,
    TError,
    Types.OrgFinRelToggleMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.OrgFinRelToggleMutation,
    TError,
    Types.OrgFinRelToggleMutationVariables,
    TContext
  >({
    mutationKey: ["OrgFinRelToggle"],
    mutationFn: (variables?: Types.OrgFinRelToggleMutationVariables) =>
      adminFetcher<
        Types.OrgFinRelToggleMutation,
        Types.OrgFinRelToggleMutationVariables
      >(OrgFinRelToggleDocument, variables)(),
    ...options,
  })
}

useOrgFinRelToggleMutation.fetcher = (
  variables: Types.OrgFinRelToggleMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.OrgFinRelToggleMutation, Types.OrgFinRelToggleMutationVariables>(
    OrgFinRelToggleDocument,
    variables,
    options,
  )

export const CreateAndApplySiteLabelDocument = `
    mutation CreateAndApplySiteLabel($input: CreateAndApplySiteLabelInput!) {
  site {
    labels {
      createAndApplySiteLabel(input: $input) {
        __typename
        ... on Error {
          message
        }
        ... on CreateAndApplySiteLabelOutput {
          label {
            pid
          }
        }
      }
    }
  }
}
    `

export const useCreateAndApplySiteLabelMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.CreateAndApplySiteLabelMutation,
    TError,
    Types.CreateAndApplySiteLabelMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateAndApplySiteLabelMutation,
    TError,
    Types.CreateAndApplySiteLabelMutationVariables,
    TContext
  >({
    mutationKey: ["CreateAndApplySiteLabel"],
    mutationFn: (variables?: Types.CreateAndApplySiteLabelMutationVariables) =>
      adminFetcher<
        Types.CreateAndApplySiteLabelMutation,
        Types.CreateAndApplySiteLabelMutationVariables
      >(CreateAndApplySiteLabelDocument, variables)(),
    ...options,
  })
}

useCreateAndApplySiteLabelMutation.fetcher = (
  variables: Types.CreateAndApplySiteLabelMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.CreateAndApplySiteLabelMutation,
    Types.CreateAndApplySiteLabelMutationVariables
  >(CreateAndApplySiteLabelDocument, variables, options)

export const ApplySiteLabelDocument = `
    mutation ApplySiteLabel($input: ApplyLabelToSiteInput!) {
  site {
    labels {
      applyLabelToSite(input: $input) {
        __typename
        ... on Error {
          message
        }
      }
    }
  }
}
    `

export const useApplySiteLabelMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.ApplySiteLabelMutation,
    TError,
    Types.ApplySiteLabelMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.ApplySiteLabelMutation,
    TError,
    Types.ApplySiteLabelMutationVariables,
    TContext
  >({
    mutationKey: ["ApplySiteLabel"],
    mutationFn: (variables?: Types.ApplySiteLabelMutationVariables) =>
      adminFetcher<Types.ApplySiteLabelMutation, Types.ApplySiteLabelMutationVariables>(
        ApplySiteLabelDocument,
        variables,
      )(),
    ...options,
  })
}

useApplySiteLabelMutation.fetcher = (
  variables: Types.ApplySiteLabelMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.ApplySiteLabelMutation, Types.ApplySiteLabelMutationVariables>(
    ApplySiteLabelDocument,
    variables,
    options,
  )

export const DeleteSiteLabelDocument = `
    mutation DeleteSiteLabel($input: DeleteLabelInput!) {
  site {
    labels {
      deleteLabel(input: $input) {
        __typename
        ... on Error {
          message
        }
      }
    }
  }
}
    `

export const useDeleteSiteLabelMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.DeleteSiteLabelMutation,
    TError,
    Types.DeleteSiteLabelMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.DeleteSiteLabelMutation,
    TError,
    Types.DeleteSiteLabelMutationVariables,
    TContext
  >({
    mutationKey: ["DeleteSiteLabel"],
    mutationFn: (variables?: Types.DeleteSiteLabelMutationVariables) =>
      adminFetcher<
        Types.DeleteSiteLabelMutation,
        Types.DeleteSiteLabelMutationVariables
      >(DeleteSiteLabelDocument, variables)(),
    ...options,
  })
}

useDeleteSiteLabelMutation.fetcher = (
  variables: Types.DeleteSiteLabelMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.DeleteSiteLabelMutation, Types.DeleteSiteLabelMutationVariables>(
    DeleteSiteLabelDocument,
    variables,
    options,
  )

export const SetLatitudeDocument = `
    mutation SetLatitude($sitePid: String!, $value: Float!) {
  site {
    setLatitude(sitePid: $sitePid, value: $value)
  }
}
    `

export const useSetLatitudeMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetLatitudeMutation,
    TError,
    Types.SetLatitudeMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetLatitudeMutation,
    TError,
    Types.SetLatitudeMutationVariables,
    TContext
  >({
    mutationKey: ["SetLatitude"],
    mutationFn: (variables?: Types.SetLatitudeMutationVariables) =>
      adminFetcher<Types.SetLatitudeMutation, Types.SetLatitudeMutationVariables>(
        SetLatitudeDocument,
        variables,
      )(),
    ...options,
  })
}

useSetLatitudeMutation.fetcher = (
  variables: Types.SetLatitudeMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetLatitudeMutation, Types.SetLatitudeMutationVariables>(
    SetLatitudeDocument,
    variables,
    options,
  )

export const SetLongitudeDocument = `
    mutation SetLongitude($sitePid: String!, $value: Float!) {
  site {
    setLongitude(sitePid: $sitePid, value: $value)
  }
}
    `

export const useSetLongitudeMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetLongitudeMutation,
    TError,
    Types.SetLongitudeMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetLongitudeMutation,
    TError,
    Types.SetLongitudeMutationVariables,
    TContext
  >({
    mutationKey: ["SetLongitude"],
    mutationFn: (variables?: Types.SetLongitudeMutationVariables) =>
      adminFetcher<Types.SetLongitudeMutation, Types.SetLongitudeMutationVariables>(
        SetLongitudeDocument,
        variables,
      )(),
    ...options,
  })
}

useSetLongitudeMutation.fetcher = (
  variables: Types.SetLongitudeMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetLongitudeMutation, Types.SetLongitudeMutationVariables>(
    SetLongitudeDocument,
    variables,
    options,
  )

export const BoostDocument = `
    mutation Boost($orgPid: String!) {
  queue {
    boost(orgPid: $orgPid)
  }
}
    `

export const useBoostMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.BoostMutation,
    TError,
    Types.BoostMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.BoostMutation,
    TError,
    Types.BoostMutationVariables,
    TContext
  >({
    mutationKey: ["Boost"],
    mutationFn: (variables?: Types.BoostMutationVariables) =>
      adminFetcher<Types.BoostMutation, Types.BoostMutationVariables>(
        BoostDocument,
        variables,
      )(),
    ...options,
  })
}

useBoostMutation.fetcher = (
  variables: Types.BoostMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.BoostMutation, Types.BoostMutationVariables>(
    BoostDocument,
    variables,
    options,
  )

export const CancelTasksWithNameDocument = `
    mutation CancelTasksWithName($taskName: String!, $orgPid: String) {
  queue {
    cancelTasksWithName(taskName: $taskName, orgPid: $orgPid)
  }
}
    `

export const useCancelTasksWithNameMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CancelTasksWithNameMutation,
    TError,
    Types.CancelTasksWithNameMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CancelTasksWithNameMutation,
    TError,
    Types.CancelTasksWithNameMutationVariables,
    TContext
  >({
    mutationKey: ["CancelTasksWithName"],
    mutationFn: (variables?: Types.CancelTasksWithNameMutationVariables) =>
      adminFetcher<
        Types.CancelTasksWithNameMutation,
        Types.CancelTasksWithNameMutationVariables
      >(CancelTasksWithNameDocument, variables)(),
    ...options,
  })
}

useCancelTasksWithNameMutation.fetcher = (
  variables: Types.CancelTasksWithNameMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.CancelTasksWithNameMutation,
    Types.CancelTasksWithNameMutationVariables
  >(CancelTasksWithNameDocument, variables, options)

export const LinkConserviceAddressesDocument = `
    mutation LinkConserviceAddresses($orgPid: String!, $sitePid: String!, $conserviceAddresses: [String!]!) {
  site {
    linkConserviceAddresses(
      orgPid: $orgPid
      sitePid: $sitePid
      conserviceAddresses: $conserviceAddresses
    ) {
      ... on Error {
        __typename
        message
      }
    }
  }
}
    `

export const useLinkConserviceAddressesMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.LinkConserviceAddressesMutation,
    TError,
    Types.LinkConserviceAddressesMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.LinkConserviceAddressesMutation,
    TError,
    Types.LinkConserviceAddressesMutationVariables,
    TContext
  >({
    mutationKey: ["LinkConserviceAddresses"],
    mutationFn: (variables?: Types.LinkConserviceAddressesMutationVariables) =>
      adminFetcher<
        Types.LinkConserviceAddressesMutation,
        Types.LinkConserviceAddressesMutationVariables
      >(LinkConserviceAddressesDocument, variables)(),
    ...options,
  })
}

useLinkConserviceAddressesMutation.fetcher = (
  variables: Types.LinkConserviceAddressesMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.LinkConserviceAddressesMutation,
    Types.LinkConserviceAddressesMutationVariables
  >(LinkConserviceAddressesDocument, variables, options)

export const CreateCsFinProductDocument = `
    mutation CreateCsFinProduct($input: CSFinProductInput!) {
  finproduct {
    createCsFinproduct(input: $input) {
      id
    }
  }
}
    `

export const useCreateCsFinProductMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CreateCsFinProductMutation,
    TError,
    Types.CreateCsFinProductMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateCsFinProductMutation,
    TError,
    Types.CreateCsFinProductMutationVariables,
    TContext
  >({
    mutationKey: ["CreateCsFinProduct"],
    mutationFn: (variables?: Types.CreateCsFinProductMutationVariables) =>
      adminFetcher<
        Types.CreateCsFinProductMutation,
        Types.CreateCsFinProductMutationVariables
      >(CreateCsFinProductDocument, variables)(),
    ...options,
  })
}

useCreateCsFinProductMutation.fetcher = (
  variables: Types.CreateCsFinProductMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.CreateCsFinProductMutation,
    Types.CreateCsFinProductMutationVariables
  >(CreateCsFinProductDocument, variables, options)

export const EditCsFinProductPricesDocument = `
    mutation EditCSFinProductPrices($id: Int!, $input: [CSPriceInput!]!) {
  finproduct {
    editCsFinproductPrices(id: $id, input: $input) {
      id
    }
  }
}
    `

export const useEditCsFinProductPricesMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.EditCsFinProductPricesMutation,
    TError,
    Types.EditCsFinProductPricesMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.EditCsFinProductPricesMutation,
    TError,
    Types.EditCsFinProductPricesMutationVariables,
    TContext
  >({
    mutationKey: ["EditCSFinProductPrices"],
    mutationFn: (variables?: Types.EditCsFinProductPricesMutationVariables) =>
      adminFetcher<
        Types.EditCsFinProductPricesMutation,
        Types.EditCsFinProductPricesMutationVariables
      >(EditCsFinProductPricesDocument, variables)(),
    ...options,
  })
}

useEditCsFinProductPricesMutation.fetcher = (
  variables: Types.EditCsFinProductPricesMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.EditCsFinProductPricesMutation,
    Types.EditCsFinProductPricesMutationVariables
  >(EditCsFinProductPricesDocument, variables, options)

export const CreatePpaFinProductDocument = `
    mutation CreatePpaFinProduct($input: PPAFinProductInput!) {
  finproduct {
    createPpaFinproduct(input: $input) {
      id
    }
  }
}
    `

export const useCreatePpaFinProductMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CreatePpaFinProductMutation,
    TError,
    Types.CreatePpaFinProductMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreatePpaFinProductMutation,
    TError,
    Types.CreatePpaFinProductMutationVariables,
    TContext
  >({
    mutationKey: ["CreatePpaFinProduct"],
    mutationFn: (variables?: Types.CreatePpaFinProductMutationVariables) =>
      adminFetcher<
        Types.CreatePpaFinProductMutation,
        Types.CreatePpaFinProductMutationVariables
      >(CreatePpaFinProductDocument, variables)(),
    ...options,
  })
}

useCreatePpaFinProductMutation.fetcher = (
  variables: Types.CreatePpaFinProductMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.CreatePpaFinProductMutation,
    Types.CreatePpaFinProductMutationVariables
  >(CreatePpaFinProductDocument, variables, options)

export const CreateLoanFinProductDocument = `
    mutation CreateLoanFinProduct($input: LoanFinProductInput!) {
  finproduct {
    createLoanFinproduct(input: $input) {
      id
    }
  }
}
    `

export const useCreateLoanFinProductMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CreateLoanFinProductMutation,
    TError,
    Types.CreateLoanFinProductMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateLoanFinProductMutation,
    TError,
    Types.CreateLoanFinProductMutationVariables,
    TContext
  >({
    mutationKey: ["CreateLoanFinProduct"],
    mutationFn: (variables?: Types.CreateLoanFinProductMutationVariables) =>
      adminFetcher<
        Types.CreateLoanFinProductMutation,
        Types.CreateLoanFinProductMutationVariables
      >(CreateLoanFinProductDocument, variables)(),
    ...options,
  })
}

useCreateLoanFinProductMutation.fetcher = (
  variables: Types.CreateLoanFinProductMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.CreateLoanFinProductMutation,
    Types.CreateLoanFinProductMutationVariables
  >(CreateLoanFinProductDocument, variables, options)

export const FinProductSetNameDocument = `
    mutation FinProductSetName($id: Int!, $value: String!) {
  finproduct {
    setName(id: $id, name: $value) {
      id
    }
  }
}
    `

export const useFinProductSetNameMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.FinProductSetNameMutation,
    TError,
    Types.FinProductSetNameMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.FinProductSetNameMutation,
    TError,
    Types.FinProductSetNameMutationVariables,
    TContext
  >({
    mutationKey: ["FinProductSetName"],
    mutationFn: (variables?: Types.FinProductSetNameMutationVariables) =>
      adminFetcher<
        Types.FinProductSetNameMutation,
        Types.FinProductSetNameMutationVariables
      >(FinProductSetNameDocument, variables)(),
    ...options,
  })
}

useFinProductSetNameMutation.fetcher = (
  variables: Types.FinProductSetNameMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.FinProductSetNameMutation,
    Types.FinProductSetNameMutationVariables
  >(FinProductSetNameDocument, variables, options)

export const FinProductSetStatusDocument = `
    mutation FinProductSetStatus($id: Int!, $value: FinProductStatus) {
  finproduct {
    setStatus(id: $id, status: $value) {
      id
    }
  }
}
    `

export const useFinProductSetStatusMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.FinProductSetStatusMutation,
    TError,
    Types.FinProductSetStatusMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.FinProductSetStatusMutation,
    TError,
    Types.FinProductSetStatusMutationVariables,
    TContext
  >({
    mutationKey: ["FinProductSetStatus"],
    mutationFn: (variables?: Types.FinProductSetStatusMutationVariables) =>
      adminFetcher<
        Types.FinProductSetStatusMutation,
        Types.FinProductSetStatusMutationVariables
      >(FinProductSetStatusDocument, variables)(),
    ...options,
  })
}

useFinProductSetStatusMutation.fetcher = (
  variables: Types.FinProductSetStatusMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.FinProductSetStatusMutation,
    Types.FinProductSetStatusMutationVariables
  >(FinProductSetStatusDocument, variables, options)

export const FinProductSetDisabledDocument = `
    mutation FinProductSetDisabled($id: Int!, $value: Boolean!) {
  finproduct {
    setDisabled(id: $id, disabled: $value) {
      id
    }
  }
}
    `

export const useFinProductSetDisabledMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.FinProductSetDisabledMutation,
    TError,
    Types.FinProductSetDisabledMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.FinProductSetDisabledMutation,
    TError,
    Types.FinProductSetDisabledMutationVariables,
    TContext
  >({
    mutationKey: ["FinProductSetDisabled"],
    mutationFn: (variables?: Types.FinProductSetDisabledMutationVariables) =>
      adminFetcher<
        Types.FinProductSetDisabledMutation,
        Types.FinProductSetDisabledMutationVariables
      >(FinProductSetDisabledDocument, variables)(),
    ...options,
  })
}

useFinProductSetDisabledMutation.fetcher = (
  variables: Types.FinProductSetDisabledMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.FinProductSetDisabledMutation,
    Types.FinProductSetDisabledMutationVariables
  >(FinProductSetDisabledDocument, variables, options)

export const FinProductSetIncludeStorageDocument = `
    mutation FinProductSetIncludeStorage($finproductId: Int!, $enable: Boolean!) {
  finproduct {
    setIncludeStorage(id: $finproductId, includeStorage: $enable) {
      id
    }
  }
}
    `

export const useFinProductSetIncludeStorageMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.FinProductSetIncludeStorageMutation,
    TError,
    Types.FinProductSetIncludeStorageMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.FinProductSetIncludeStorageMutation,
    TError,
    Types.FinProductSetIncludeStorageMutationVariables,
    TContext
  >({
    mutationKey: ["FinProductSetIncludeStorage"],
    mutationFn: (variables?: Types.FinProductSetIncludeStorageMutationVariables) =>
      adminFetcher<
        Types.FinProductSetIncludeStorageMutation,
        Types.FinProductSetIncludeStorageMutationVariables
      >(FinProductSetIncludeStorageDocument, variables)(),
    ...options,
  })
}

useFinProductSetIncludeStorageMutation.fetcher = (
  variables: Types.FinProductSetIncludeStorageMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.FinProductSetIncludeStorageMutation,
    Types.FinProductSetIncludeStorageMutationVariables
  >(FinProductSetIncludeStorageDocument, variables, options)

export const SiteToggleFinProductDocument = `
    mutation SiteToggleFinProduct($sitePid: String!, $finproductId: Int!, $enabled: Boolean!) {
  site {
    toggleFinproduct(
      sitePid: $sitePid
      finproductId: $finproductId
      enabled: $enabled
    )
  }
}
    `

export const useSiteToggleFinProductMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SiteToggleFinProductMutation,
    TError,
    Types.SiteToggleFinProductMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SiteToggleFinProductMutation,
    TError,
    Types.SiteToggleFinProductMutationVariables,
    TContext
  >({
    mutationKey: ["SiteToggleFinProduct"],
    mutationFn: (variables?: Types.SiteToggleFinProductMutationVariables) =>
      adminFetcher<
        Types.SiteToggleFinProductMutation,
        Types.SiteToggleFinProductMutationVariables
      >(SiteToggleFinProductDocument, variables)(),
    ...options,
  })
}

useSiteToggleFinProductMutation.fetcher = (
  variables: Types.SiteToggleFinProductMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.SiteToggleFinProductMutation,
    Types.SiteToggleFinProductMutationVariables
  >(SiteToggleFinProductDocument, variables, options)

export const ToggleHideFakePanelsDocument = `
    mutation ToggleHideFakePanels($sitePid: String!, $hide: Boolean!) {
  site {
    toggleHideFakePanels(sitePid: $sitePid, hide: $hide)
  }
}
    `

export const useToggleHideFakePanelsMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.ToggleHideFakePanelsMutation,
    TError,
    Types.ToggleHideFakePanelsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.ToggleHideFakePanelsMutation,
    TError,
    Types.ToggleHideFakePanelsMutationVariables,
    TContext
  >({
    mutationKey: ["ToggleHideFakePanels"],
    mutationFn: (variables?: Types.ToggleHideFakePanelsMutationVariables) =>
      adminFetcher<
        Types.ToggleHideFakePanelsMutation,
        Types.ToggleHideFakePanelsMutationVariables
      >(ToggleHideFakePanelsDocument, variables)(),
    ...options,
  })
}

useToggleHideFakePanelsMutation.fetcher = (
  variables: Types.ToggleHideFakePanelsMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.ToggleHideFakePanelsMutation,
    Types.ToggleHideFakePanelsMutationVariables
  >(ToggleHideFakePanelsDocument, variables, options)

export const TodoItemAddDocument = `
    mutation TodoItemAdd($sitePid: String!, $content: String!, $sectionName: String!) {
  todos {
    add(sitePid: $sitePid, content: $content, sectionName: $sectionName)
  }
}
    `

export const useTodoItemAddMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.TodoItemAddMutation,
    TError,
    Types.TodoItemAddMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.TodoItemAddMutation,
    TError,
    Types.TodoItemAddMutationVariables,
    TContext
  >({
    mutationKey: ["TodoItemAdd"],
    mutationFn: (variables?: Types.TodoItemAddMutationVariables) =>
      adminFetcher<Types.TodoItemAddMutation, Types.TodoItemAddMutationVariables>(
        TodoItemAddDocument,
        variables,
      )(),
    ...options,
  })
}

useTodoItemAddMutation.fetcher = (
  variables: Types.TodoItemAddMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.TodoItemAddMutation, Types.TodoItemAddMutationVariables>(
    TodoItemAddDocument,
    variables,
    options,
  )

export const TodoItemDeleteDocument = `
    mutation TodoItemDelete($sitePid: String!, $todoItemId: Int!) {
  todos {
    delete(sitePid: $sitePid, todoItemId: $todoItemId)
  }
}
    `

export const useTodoItemDeleteMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.TodoItemDeleteMutation,
    TError,
    Types.TodoItemDeleteMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.TodoItemDeleteMutation,
    TError,
    Types.TodoItemDeleteMutationVariables,
    TContext
  >({
    mutationKey: ["TodoItemDelete"],
    mutationFn: (variables?: Types.TodoItemDeleteMutationVariables) =>
      adminFetcher<Types.TodoItemDeleteMutation, Types.TodoItemDeleteMutationVariables>(
        TodoItemDeleteDocument,
        variables,
      )(),
    ...options,
  })
}

useTodoItemDeleteMutation.fetcher = (
  variables: Types.TodoItemDeleteMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.TodoItemDeleteMutation, Types.TodoItemDeleteMutationVariables>(
    TodoItemDeleteDocument,
    variables,
    options,
  )

export const TodoItemSetContentDocument = `
    mutation TodoItemSetContent($sitePid: String!, $todoItemId: Int!, $content: String!) {
  todos {
    setContent(sitePid: $sitePid, todoItemId: $todoItemId, content: $content)
  }
}
    `

export const useTodoItemSetContentMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.TodoItemSetContentMutation,
    TError,
    Types.TodoItemSetContentMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.TodoItemSetContentMutation,
    TError,
    Types.TodoItemSetContentMutationVariables,
    TContext
  >({
    mutationKey: ["TodoItemSetContent"],
    mutationFn: (variables?: Types.TodoItemSetContentMutationVariables) =>
      adminFetcher<
        Types.TodoItemSetContentMutation,
        Types.TodoItemSetContentMutationVariables
      >(TodoItemSetContentDocument, variables)(),
    ...options,
  })
}

useTodoItemSetContentMutation.fetcher = (
  variables: Types.TodoItemSetContentMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.TodoItemSetContentMutation,
    Types.TodoItemSetContentMutationVariables
  >(TodoItemSetContentDocument, variables, options)

export const TodoItemSetCompleteDocument = `
    mutation TodoItemSetComplete($sitePid: String!, $todoItemId: Int!, $complete: Boolean!) {
  todos {
    setComplete(sitePid: $sitePid, todoItemId: $todoItemId, complete: $complete)
  }
}
    `

export const useTodoItemSetCompleteMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.TodoItemSetCompleteMutation,
    TError,
    Types.TodoItemSetCompleteMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.TodoItemSetCompleteMutation,
    TError,
    Types.TodoItemSetCompleteMutationVariables,
    TContext
  >({
    mutationKey: ["TodoItemSetComplete"],
    mutationFn: (variables?: Types.TodoItemSetCompleteMutationVariables) =>
      adminFetcher<
        Types.TodoItemSetCompleteMutation,
        Types.TodoItemSetCompleteMutationVariables
      >(TodoItemSetCompleteDocument, variables)(),
    ...options,
  })
}

useTodoItemSetCompleteMutation.fetcher = (
  variables: Types.TodoItemSetCompleteMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.TodoItemSetCompleteMutation,
    Types.TodoItemSetCompleteMutationVariables
  >(TodoItemSetCompleteDocument, variables, options)

export const TodoItemSetLocationDocument = `
    mutation TodoItemSetLocation($sitePid: String!, $todoItemId: Int!, $sectionName: String!, $index: Int!) {
  todos {
    setSection(
      sitePid: $sitePid
      todoItemId: $todoItemId
      sectionName: $sectionName
      index: $index
    )
  }
}
    `

export const useTodoItemSetLocationMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.TodoItemSetLocationMutation,
    TError,
    Types.TodoItemSetLocationMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.TodoItemSetLocationMutation,
    TError,
    Types.TodoItemSetLocationMutationVariables,
    TContext
  >({
    mutationKey: ["TodoItemSetLocation"],
    mutationFn: (variables?: Types.TodoItemSetLocationMutationVariables) =>
      adminFetcher<
        Types.TodoItemSetLocationMutation,
        Types.TodoItemSetLocationMutationVariables
      >(TodoItemSetLocationDocument, variables)(),
    ...options,
  })
}

useTodoItemSetLocationMutation.fetcher = (
  variables: Types.TodoItemSetLocationMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.TodoItemSetLocationMutation,
    Types.TodoItemSetLocationMutationVariables
  >(TodoItemSetLocationDocument, variables, options)

export const TodoSectionSetIndexDocument = `
    mutation TodoSectionSetIndex($sitePid: String!, $sectionName: String!, $index: Int!) {
  todos {
    setSectionIndex(sitePid: $sitePid, sectionName: $sectionName, index: $index)
  }
}
    `

export const useTodoSectionSetIndexMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.TodoSectionSetIndexMutation,
    TError,
    Types.TodoSectionSetIndexMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.TodoSectionSetIndexMutation,
    TError,
    Types.TodoSectionSetIndexMutationVariables,
    TContext
  >({
    mutationKey: ["TodoSectionSetIndex"],
    mutationFn: (variables?: Types.TodoSectionSetIndexMutationVariables) =>
      adminFetcher<
        Types.TodoSectionSetIndexMutation,
        Types.TodoSectionSetIndexMutationVariables
      >(TodoSectionSetIndexDocument, variables)(),
    ...options,
  })
}

useTodoSectionSetIndexMutation.fetcher = (
  variables: Types.TodoSectionSetIndexMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.TodoSectionSetIndexMutation,
    Types.TodoSectionSetIndexMutationVariables
  >(TodoSectionSetIndexDocument, variables, options)

export const TodoSectionSetNameDocument = `
    mutation TodoSectionSetName($sitePid: String!, $sectionName: String!, $newName: String!) {
  todos {
    setSectionName(sitePid: $sitePid, sectionName: $sectionName, newName: $newName)
  }
}
    `

export const useTodoSectionSetNameMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.TodoSectionSetNameMutation,
    TError,
    Types.TodoSectionSetNameMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.TodoSectionSetNameMutation,
    TError,
    Types.TodoSectionSetNameMutationVariables,
    TContext
  >({
    mutationKey: ["TodoSectionSetName"],
    mutationFn: (variables?: Types.TodoSectionSetNameMutationVariables) =>
      adminFetcher<
        Types.TodoSectionSetNameMutation,
        Types.TodoSectionSetNameMutationVariables
      >(TodoSectionSetNameDocument, variables)(),
    ...options,
  })
}

useTodoSectionSetNameMutation.fetcher = (
  variables: Types.TodoSectionSetNameMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.TodoSectionSetNameMutation,
    Types.TodoSectionSetNameMutationVariables
  >(TodoSectionSetNameDocument, variables, options)

export const SetHelioscopeAllocationDocument = `
    mutation SetHelioscopeAllocation($helioscopeId: Int!, $allocations: [Allocation!]!) {
  site {
    setHelioscopeAllocation(helioscopeId: $helioscopeId, allocations: $allocations)
  }
}
    `

export const useSetHelioscopeAllocationMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetHelioscopeAllocationMutation,
    TError,
    Types.SetHelioscopeAllocationMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetHelioscopeAllocationMutation,
    TError,
    Types.SetHelioscopeAllocationMutationVariables,
    TContext
  >({
    mutationKey: ["SetHelioscopeAllocation"],
    mutationFn: (variables?: Types.SetHelioscopeAllocationMutationVariables) =>
      adminFetcher<
        Types.SetHelioscopeAllocationMutation,
        Types.SetHelioscopeAllocationMutationVariables
      >(SetHelioscopeAllocationDocument, variables)(),
    ...options,
  })
}

useSetHelioscopeAllocationMutation.fetcher = (
  variables: Types.SetHelioscopeAllocationMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.SetHelioscopeAllocationMutation,
    Types.SetHelioscopeAllocationMutationVariables
  >(SetHelioscopeAllocationDocument, variables, options)

export const FetchTariffVersionDocument = `
    mutation FetchTariffVersion($masterTariffId: Int!, $effectiveOn: DateTime!) {
  tariff {
    fetchTariffVersion(masterTariffId: $masterTariffId, effectiveOn: $effectiveOn)
  }
}
    `

export const useFetchTariffVersionMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.FetchTariffVersionMutation,
    TError,
    Types.FetchTariffVersionMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.FetchTariffVersionMutation,
    TError,
    Types.FetchTariffVersionMutationVariables,
    TContext
  >({
    mutationKey: ["FetchTariffVersion"],
    mutationFn: (variables?: Types.FetchTariffVersionMutationVariables) =>
      adminFetcher<
        Types.FetchTariffVersionMutation,
        Types.FetchTariffVersionMutationVariables
      >(FetchTariffVersionDocument, variables)(),
    ...options,
  })
}

useFetchTariffVersionMutation.fetcher = (
  variables: Types.FetchTariffVersionMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.FetchTariffVersionMutation,
    Types.FetchTariffVersionMutationVariables
  >(FetchTariffVersionDocument, variables, options)

export const UpdateHelioscopeAssumptionsDocument = `
    mutation UpdateHelioscopeAssumptions($helioscopePid: String!, $assumptions: HelioscopeAssumptionsInput!) {
  helioscopes {
    updateHelioscopeAssumptions(
      helioscopePid: $helioscopePid
      assumptions: $assumptions
    ) {
      __typename
    }
  }
}
    `

export const useUpdateHelioscopeAssumptionsMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.UpdateHelioscopeAssumptionsMutation,
    TError,
    Types.UpdateHelioscopeAssumptionsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UpdateHelioscopeAssumptionsMutation,
    TError,
    Types.UpdateHelioscopeAssumptionsMutationVariables,
    TContext
  >({
    mutationKey: ["UpdateHelioscopeAssumptions"],
    mutationFn: (variables?: Types.UpdateHelioscopeAssumptionsMutationVariables) =>
      adminFetcher<
        Types.UpdateHelioscopeAssumptionsMutation,
        Types.UpdateHelioscopeAssumptionsMutationVariables
      >(UpdateHelioscopeAssumptionsDocument, variables)(),
    ...options,
  })
}

useUpdateHelioscopeAssumptionsMutation.fetcher = (
  variables: Types.UpdateHelioscopeAssumptionsMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.UpdateHelioscopeAssumptionsMutation,
    Types.UpdateHelioscopeAssumptionsMutationVariables
  >(UpdateHelioscopeAssumptionsDocument, variables, options)

export const UpdateMultifamilyUnitsDocument = `
    mutation UpdateMultifamilyUnits($sitePid: String!, $input: MFUnitBreakdownInput!) {
  updateMfUnits(sitePid: $sitePid, input: $input)
}
    `

export const useUpdateMultifamilyUnitsMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UpdateMultifamilyUnitsMutation,
    TError,
    Types.UpdateMultifamilyUnitsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UpdateMultifamilyUnitsMutation,
    TError,
    Types.UpdateMultifamilyUnitsMutationVariables,
    TContext
  >({
    mutationKey: ["UpdateMultifamilyUnits"],
    mutationFn: (variables?: Types.UpdateMultifamilyUnitsMutationVariables) =>
      adminFetcher<
        Types.UpdateMultifamilyUnitsMutation,
        Types.UpdateMultifamilyUnitsMutationVariables
      >(UpdateMultifamilyUnitsDocument, variables)(),
    ...options,
  })
}

useUpdateMultifamilyUnitsMutation.fetcher = (
  variables: Types.UpdateMultifamilyUnitsMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.UpdateMultifamilyUnitsMutation,
    Types.UpdateMultifamilyUnitsMutationVariables
  >(UpdateMultifamilyUnitsDocument, variables, options)

export const SetUserEnabledDocument = `
    mutation SetUserEnabled($userId: Int!, $isEnabled: Boolean!) {
  user {
    setIsEnabled(userId: $userId, isEnabled: $isEnabled) {
      __typename
      isEnabled
      lastSeenAt
    }
  }
}
    `

export const useSetUserEnabledMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetUserEnabledMutation,
    TError,
    Types.SetUserEnabledMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetUserEnabledMutation,
    TError,
    Types.SetUserEnabledMutationVariables,
    TContext
  >({
    mutationKey: ["SetUserEnabled"],
    mutationFn: (variables?: Types.SetUserEnabledMutationVariables) =>
      adminFetcher<Types.SetUserEnabledMutation, Types.SetUserEnabledMutationVariables>(
        SetUserEnabledDocument,
        variables,
      )(),
    ...options,
  })
}

useSetUserEnabledMutation.fetcher = (
  variables: Types.SetUserEnabledMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.SetUserEnabledMutation, Types.SetUserEnabledMutationVariables>(
    SetUserEnabledDocument,
    variables,
    options,
  )

export const ClearSiteDrawingsDocument = `
    mutation ClearSiteDrawings($input: ClearDrawingsInput!) {
  site {
    clearDrawings(input: $input)
  }
}
    `

export const useClearSiteDrawingsMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.ClearSiteDrawingsMutation,
    TError,
    Types.ClearSiteDrawingsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.ClearSiteDrawingsMutation,
    TError,
    Types.ClearSiteDrawingsMutationVariables,
    TContext
  >({
    mutationKey: ["ClearSiteDrawings"],
    mutationFn: (variables?: Types.ClearSiteDrawingsMutationVariables) =>
      adminFetcher<
        Types.ClearSiteDrawingsMutation,
        Types.ClearSiteDrawingsMutationVariables
      >(ClearSiteDrawingsDocument, variables)(),
    ...options,
  })
}

useClearSiteDrawingsMutation.fetcher = (
  variables: Types.ClearSiteDrawingsMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.ClearSiteDrawingsMutation,
    Types.ClearSiteDrawingsMutationVariables
  >(ClearSiteDrawingsDocument, variables, options)

export const RevertSiteToEstimatesDocument = `
    mutation RevertSiteToEstimates($input: RevertToEstimatesInput!) {
  site {
    revertToEstimates(input: $input)
  }
}
    `

export const useRevertSiteToEstimatesMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.RevertSiteToEstimatesMutation,
    TError,
    Types.RevertSiteToEstimatesMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RevertSiteToEstimatesMutation,
    TError,
    Types.RevertSiteToEstimatesMutationVariables,
    TContext
  >({
    mutationKey: ["RevertSiteToEstimates"],
    mutationFn: (variables?: Types.RevertSiteToEstimatesMutationVariables) =>
      adminFetcher<
        Types.RevertSiteToEstimatesMutation,
        Types.RevertSiteToEstimatesMutationVariables
      >(RevertSiteToEstimatesDocument, variables)(),
    ...options,
  })
}

useRevertSiteToEstimatesMutation.fetcher = (
  variables: Types.RevertSiteToEstimatesMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.RevertSiteToEstimatesMutation,
    Types.RevertSiteToEstimatesMutationVariables
  >(RevertSiteToEstimatesDocument, variables, options)

export const PromoteTheFinmodelAnywayDocument = `
    mutation PromoteTheFinmodelAnyway($finmodelId: Int!) {
  promoteTheFinmodelAnyway(finmodelId: $finmodelId)
}
    `

export const usePromoteTheFinmodelAnywayMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.PromoteTheFinmodelAnywayMutation,
    TError,
    Types.PromoteTheFinmodelAnywayMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.PromoteTheFinmodelAnywayMutation,
    TError,
    Types.PromoteTheFinmodelAnywayMutationVariables,
    TContext
  >({
    mutationKey: ["PromoteTheFinmodelAnyway"],
    mutationFn: (variables?: Types.PromoteTheFinmodelAnywayMutationVariables) =>
      adminFetcher<
        Types.PromoteTheFinmodelAnywayMutation,
        Types.PromoteTheFinmodelAnywayMutationVariables
      >(PromoteTheFinmodelAnywayDocument, variables)(),
    ...options,
  })
}

usePromoteTheFinmodelAnywayMutation.fetcher = (
  variables: Types.PromoteTheFinmodelAnywayMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.PromoteTheFinmodelAnywayMutation,
    Types.PromoteTheFinmodelAnywayMutationVariables
  >(PromoteTheFinmodelAnywayDocument, variables, options)

export const FindBuildingFootprintsDocument = `
    mutation FindBuildingFootprints($sitePid: String!, $strategy: FootprintStrategy!) {
  site {
    findBuildingFootprints(sitePid: $sitePid, strategy: $strategy)
  }
}
    `

export const useFindBuildingFootprintsMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.FindBuildingFootprintsMutation,
    TError,
    Types.FindBuildingFootprintsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.FindBuildingFootprintsMutation,
    TError,
    Types.FindBuildingFootprintsMutationVariables,
    TContext
  >({
    mutationKey: ["FindBuildingFootprints"],
    mutationFn: (variables?: Types.FindBuildingFootprintsMutationVariables) =>
      adminFetcher<
        Types.FindBuildingFootprintsMutation,
        Types.FindBuildingFootprintsMutationVariables
      >(FindBuildingFootprintsDocument, variables)(),
    ...options,
  })
}

useFindBuildingFootprintsMutation.fetcher = (
  variables: Types.FindBuildingFootprintsMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.FindBuildingFootprintsMutation,
    Types.FindBuildingFootprintsMutationVariables
  >(FindBuildingFootprintsDocument, variables, options)

export const CreateHostingCapacityMapDocument = `
    mutation CreateHostingCapacityMap($utilityPid: String!, $hostingCapacityMap: HostingCapacityMapInputs!) {
  hostingCapacityMaps {
    create(utilityPid: $utilityPid, inputs: $hostingCapacityMap)
  }
}
    `

export const useCreateHostingCapacityMapMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.CreateHostingCapacityMapMutation,
    TError,
    Types.CreateHostingCapacityMapMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateHostingCapacityMapMutation,
    TError,
    Types.CreateHostingCapacityMapMutationVariables,
    TContext
  >({
    mutationKey: ["CreateHostingCapacityMap"],
    mutationFn: (variables?: Types.CreateHostingCapacityMapMutationVariables) =>
      adminFetcher<
        Types.CreateHostingCapacityMapMutation,
        Types.CreateHostingCapacityMapMutationVariables
      >(CreateHostingCapacityMapDocument, variables)(),
    ...options,
  })
}

useCreateHostingCapacityMapMutation.fetcher = (
  variables: Types.CreateHostingCapacityMapMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.CreateHostingCapacityMapMutation,
    Types.CreateHostingCapacityMapMutationVariables
  >(CreateHostingCapacityMapDocument, variables, options)

export const UpdateHostingCapacityMapDocument = `
    mutation UpdateHostingCapacityMap($mapId: Int!, $hostingCapacityMap: HostingCapacityMapInputs!) {
  hostingCapacityMaps {
    update(mapId: $mapId, inputs: $hostingCapacityMap)
  }
}
    `

export const useUpdateHostingCapacityMapMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.UpdateHostingCapacityMapMutation,
    TError,
    Types.UpdateHostingCapacityMapMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UpdateHostingCapacityMapMutation,
    TError,
    Types.UpdateHostingCapacityMapMutationVariables,
    TContext
  >({
    mutationKey: ["UpdateHostingCapacityMap"],
    mutationFn: (variables?: Types.UpdateHostingCapacityMapMutationVariables) =>
      adminFetcher<
        Types.UpdateHostingCapacityMapMutation,
        Types.UpdateHostingCapacityMapMutationVariables
      >(UpdateHostingCapacityMapDocument, variables)(),
    ...options,
  })
}

useUpdateHostingCapacityMapMutation.fetcher = (
  variables: Types.UpdateHostingCapacityMapMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.UpdateHostingCapacityMapMutation,
    Types.UpdateHostingCapacityMapMutationVariables
  >(UpdateHostingCapacityMapDocument, variables, options)

export const DeleteHostingCapacityMapDocument = `
    mutation DeleteHostingCapacityMap($mapId: Int!) {
  hostingCapacityMaps {
    delete(mapId: $mapId)
  }
}
    `

export const useDeleteHostingCapacityMapMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.DeleteHostingCapacityMapMutation,
    TError,
    Types.DeleteHostingCapacityMapMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.DeleteHostingCapacityMapMutation,
    TError,
    Types.DeleteHostingCapacityMapMutationVariables,
    TContext
  >({
    mutationKey: ["DeleteHostingCapacityMap"],
    mutationFn: (variables?: Types.DeleteHostingCapacityMapMutationVariables) =>
      adminFetcher<
        Types.DeleteHostingCapacityMapMutation,
        Types.DeleteHostingCapacityMapMutationVariables
      >(DeleteHostingCapacityMapDocument, variables)(),
    ...options,
  })
}

useDeleteHostingCapacityMapMutation.fetcher = (
  variables: Types.DeleteHostingCapacityMapMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.DeleteHostingCapacityMapMutation,
    Types.DeleteHostingCapacityMapMutationVariables
  >(DeleteHostingCapacityMapDocument, variables, options)

export const CreateEmailDraftDocument = `
    mutation CreateEmailDraft($draftParams: CreateDraftInput!) {
  googleApi {
    createDraft(draftParams: $draftParams)
  }
}
    `

export const useCreateEmailDraftMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CreateEmailDraftMutation,
    TError,
    Types.CreateEmailDraftMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateEmailDraftMutation,
    TError,
    Types.CreateEmailDraftMutationVariables,
    TContext
  >({
    mutationKey: ["CreateEmailDraft"],
    mutationFn: (variables?: Types.CreateEmailDraftMutationVariables) =>
      adminFetcher<
        Types.CreateEmailDraftMutation,
        Types.CreateEmailDraftMutationVariables
      >(CreateEmailDraftDocument, variables)(),
    ...options,
  })
}

useCreateEmailDraftMutation.fetcher = (
  variables: Types.CreateEmailDraftMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.CreateEmailDraftMutation, Types.CreateEmailDraftMutationVariables>(
    CreateEmailDraftDocument,
    variables,
    options,
  )

export const UpdateProjectDocument = `
    mutation UpdateProject($projectId: Int!, $inputs: MarketplaceRowInputs!) {
  marketplace {
    updateProject(projectId: $projectId, inputs: $inputs) {
      __typename
      ... on EnergyProject {
        id
      }
      ... on Error {
        message
      }
    }
  }
}
    `

export const useUpdateProjectMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UpdateProjectMutation,
    TError,
    Types.UpdateProjectMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UpdateProjectMutation,
    TError,
    Types.UpdateProjectMutationVariables,
    TContext
  >({
    mutationKey: ["UpdateProject"],
    mutationFn: (variables?: Types.UpdateProjectMutationVariables) =>
      adminFetcher<Types.UpdateProjectMutation, Types.UpdateProjectMutationVariables>(
        UpdateProjectDocument,
        variables,
      )(),
    ...options,
  })
}

useUpdateProjectMutation.fetcher = (
  variables: Types.UpdateProjectMutationVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.UpdateProjectMutation, Types.UpdateProjectMutationVariables>(
    UpdateProjectDocument,
    variables,
    options,
  )

export const FinmodelVersionsDocument = `
    query FinmodelVersions {
  finmodelVersions {
    id
    createdAt
    ppaFmv
    loanFmv
    selfFmv
    leaseFmv
  }
}
    `

export const useFinmodelVersionsQuery = <
  TData = Types.FinmodelVersionsQuery,
  TError = unknown,
>(
  variables?: Types.FinmodelVersionsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.FinmodelVersionsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.FinmodelVersionsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.FinmodelVersionsQuery, TError, TData>({
    queryKey:
      variables === undefined ? ["FinmodelVersions"] : ["FinmodelVersions", variables],
    queryFn: adminFetcher<
      Types.FinmodelVersionsQuery,
      Types.FinmodelVersionsQueryVariables
    >(FinmodelVersionsDocument, variables),
    ...options,
  })
}

useFinmodelVersionsQuery.getKey = (variables?: Types.FinmodelVersionsQueryVariables) =>
  variables === undefined ? ["FinmodelVersions"] : ["FinmodelVersions", variables]

useFinmodelVersionsQuery.rootKey = "FinmodelVersions"

export const useSuspenseFinmodelVersionsQuery = <
  TData = Types.FinmodelVersionsQuery,
  TError = unknown,
>(
  variables?: Types.FinmodelVersionsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.FinmodelVersionsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.FinmodelVersionsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.FinmodelVersionsQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["FinmodelVersionsSuspense"]
        : ["FinmodelVersionsSuspense", variables],
    queryFn: adminFetcher<
      Types.FinmodelVersionsQuery,
      Types.FinmodelVersionsQueryVariables
    >(FinmodelVersionsDocument, variables),
    ...options,
  })
}

useSuspenseFinmodelVersionsQuery.getKey = (
  variables?: Types.FinmodelVersionsQueryVariables,
) =>
  variables === undefined
    ? ["FinmodelVersionsSuspense"]
    : ["FinmodelVersionsSuspense", variables]

useSuspenseFinmodelVersionsQuery.rootKey = "FinmodelVersions"

export const useInfiniteFinmodelVersionsQuery = <
  TData = InfiniteData<Types.FinmodelVersionsQuery>,
  TError = unknown,
>(
  variables: Types.FinmodelVersionsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.FinmodelVersionsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.FinmodelVersionsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.FinmodelVersionsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["FinmodelVersions.infinite"]
            : ["FinmodelVersions.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.FinmodelVersionsQuery,
            Types.FinmodelVersionsQueryVariables
          >(FinmodelVersionsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteFinmodelVersionsQuery.getKey = (
  variables?: Types.FinmodelVersionsQueryVariables,
) =>
  variables === undefined
    ? ["FinmodelVersions.infinite"]
    : ["FinmodelVersions.infinite", variables]

useInfiniteFinmodelVersionsQuery.rootKey = "FinmodelVersions.infinite"

export const useSuspenseInfiniteFinmodelVersionsQuery = <
  TData = InfiniteData<Types.FinmodelVersionsQuery>,
  TError = unknown,
>(
  variables: Types.FinmodelVersionsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.FinmodelVersionsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.FinmodelVersionsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.FinmodelVersionsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["FinmodelVersions.infiniteSuspense"]
            : ["FinmodelVersions.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.FinmodelVersionsQuery,
            Types.FinmodelVersionsQueryVariables
          >(FinmodelVersionsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteFinmodelVersionsQuery.getKey = (
  variables?: Types.FinmodelVersionsQueryVariables,
) =>
  variables === undefined
    ? ["FinmodelVersions.infiniteSuspense"]
    : ["FinmodelVersions.infiniteSuspense", variables]

useSuspenseInfiniteFinmodelVersionsQuery.rootKey = "FinmodelVersions.infinite"

useFinmodelVersionsQuery.fetcher = (
  variables?: Types.FinmodelVersionsQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.FinmodelVersionsQuery, Types.FinmodelVersionsQueryVariables>(
    FinmodelVersionsDocument,
    variables,
    options,
  )

export const FinmodelFailureIndexDocument = `
    query FinmodelFailureIndex {
  finmodelFailureIndex {
    fmv
    name
    failureCount
  }
}
    `

export const useFinmodelFailureIndexQuery = <
  TData = Types.FinmodelFailureIndexQuery,
  TError = unknown,
>(
  variables?: Types.FinmodelFailureIndexQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.FinmodelFailureIndexQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.FinmodelFailureIndexQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.FinmodelFailureIndexQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["FinmodelFailureIndex"]
        : ["FinmodelFailureIndex", variables],
    queryFn: adminFetcher<
      Types.FinmodelFailureIndexQuery,
      Types.FinmodelFailureIndexQueryVariables
    >(FinmodelFailureIndexDocument, variables),
    ...options,
  })
}

useFinmodelFailureIndexQuery.getKey = (
  variables?: Types.FinmodelFailureIndexQueryVariables,
) =>
  variables === undefined
    ? ["FinmodelFailureIndex"]
    : ["FinmodelFailureIndex", variables]

useFinmodelFailureIndexQuery.rootKey = "FinmodelFailureIndex"

export const useSuspenseFinmodelFailureIndexQuery = <
  TData = Types.FinmodelFailureIndexQuery,
  TError = unknown,
>(
  variables?: Types.FinmodelFailureIndexQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.FinmodelFailureIndexQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.FinmodelFailureIndexQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.FinmodelFailureIndexQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["FinmodelFailureIndexSuspense"]
        : ["FinmodelFailureIndexSuspense", variables],
    queryFn: adminFetcher<
      Types.FinmodelFailureIndexQuery,
      Types.FinmodelFailureIndexQueryVariables
    >(FinmodelFailureIndexDocument, variables),
    ...options,
  })
}

useSuspenseFinmodelFailureIndexQuery.getKey = (
  variables?: Types.FinmodelFailureIndexQueryVariables,
) =>
  variables === undefined
    ? ["FinmodelFailureIndexSuspense"]
    : ["FinmodelFailureIndexSuspense", variables]

useSuspenseFinmodelFailureIndexQuery.rootKey = "FinmodelFailureIndex"

export const useInfiniteFinmodelFailureIndexQuery = <
  TData = InfiniteData<Types.FinmodelFailureIndexQuery>,
  TError = unknown,
>(
  variables: Types.FinmodelFailureIndexQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.FinmodelFailureIndexQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.FinmodelFailureIndexQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.FinmodelFailureIndexQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["FinmodelFailureIndex.infinite"]
            : ["FinmodelFailureIndex.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.FinmodelFailureIndexQuery,
            Types.FinmodelFailureIndexQueryVariables
          >(FinmodelFailureIndexDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteFinmodelFailureIndexQuery.getKey = (
  variables?: Types.FinmodelFailureIndexQueryVariables,
) =>
  variables === undefined
    ? ["FinmodelFailureIndex.infinite"]
    : ["FinmodelFailureIndex.infinite", variables]

useInfiniteFinmodelFailureIndexQuery.rootKey = "FinmodelFailureIndex.infinite"

export const useSuspenseInfiniteFinmodelFailureIndexQuery = <
  TData = InfiniteData<Types.FinmodelFailureIndexQuery>,
  TError = unknown,
>(
  variables: Types.FinmodelFailureIndexQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.FinmodelFailureIndexQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.FinmodelFailureIndexQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.FinmodelFailureIndexQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["FinmodelFailureIndex.infiniteSuspense"]
            : ["FinmodelFailureIndex.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.FinmodelFailureIndexQuery,
            Types.FinmodelFailureIndexQueryVariables
          >(FinmodelFailureIndexDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteFinmodelFailureIndexQuery.getKey = (
  variables?: Types.FinmodelFailureIndexQueryVariables,
) =>
  variables === undefined
    ? ["FinmodelFailureIndex.infiniteSuspense"]
    : ["FinmodelFailureIndex.infiniteSuspense", variables]

useSuspenseInfiniteFinmodelFailureIndexQuery.rootKey = "FinmodelFailureIndex.infinite"

useFinmodelFailureIndexQuery.fetcher = (
  variables?: Types.FinmodelFailureIndexQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.FinmodelFailureIndexQuery,
    Types.FinmodelFailureIndexQueryVariables
  >(FinmodelFailureIndexDocument, variables, options)

export const FinmodelFailureViewDocument = `
    query FinmodelFailureView($filterBy: FinmodelFailureTableFilter!, $sortBy: [FinmodelFailureTableSort!]) {
  finmodelFailures {
    view(filterBy: $filterBy, sortBy: $sortBy) {
      count
      keys
    }
  }
}
    `

export const useFinmodelFailureViewQuery = <
  TData = Types.FinmodelFailureViewQuery,
  TError = unknown,
>(
  variables: Types.FinmodelFailureViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.FinmodelFailureViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.FinmodelFailureViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.FinmodelFailureViewQuery, TError, TData>({
    queryKey: ["FinmodelFailureView", variables],
    queryFn: adminFetcher<
      Types.FinmodelFailureViewQuery,
      Types.FinmodelFailureViewQueryVariables
    >(FinmodelFailureViewDocument, variables),
    ...options,
  })
}

useFinmodelFailureViewQuery.getKey = (
  variables: Types.FinmodelFailureViewQueryVariables,
) => ["FinmodelFailureView", variables]

useFinmodelFailureViewQuery.rootKey = "FinmodelFailureView"

export const useSuspenseFinmodelFailureViewQuery = <
  TData = Types.FinmodelFailureViewQuery,
  TError = unknown,
>(
  variables: Types.FinmodelFailureViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.FinmodelFailureViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.FinmodelFailureViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.FinmodelFailureViewQuery, TError, TData>({
    queryKey: ["FinmodelFailureViewSuspense", variables],
    queryFn: adminFetcher<
      Types.FinmodelFailureViewQuery,
      Types.FinmodelFailureViewQueryVariables
    >(FinmodelFailureViewDocument, variables),
    ...options,
  })
}

useSuspenseFinmodelFailureViewQuery.getKey = (
  variables: Types.FinmodelFailureViewQueryVariables,
) => ["FinmodelFailureViewSuspense", variables]

useSuspenseFinmodelFailureViewQuery.rootKey = "FinmodelFailureView"

export const useInfiniteFinmodelFailureViewQuery = <
  TData = InfiniteData<Types.FinmodelFailureViewQuery>,
  TError = unknown,
>(
  variables: Types.FinmodelFailureViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.FinmodelFailureViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.FinmodelFailureViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.FinmodelFailureViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["FinmodelFailureView.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.FinmodelFailureViewQuery,
            Types.FinmodelFailureViewQueryVariables
          >(FinmodelFailureViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteFinmodelFailureViewQuery.getKey = (
  variables: Types.FinmodelFailureViewQueryVariables,
) => ["FinmodelFailureView.infinite", variables]

useInfiniteFinmodelFailureViewQuery.rootKey = "FinmodelFailureView.infinite"

export const useSuspenseInfiniteFinmodelFailureViewQuery = <
  TData = InfiniteData<Types.FinmodelFailureViewQuery>,
  TError = unknown,
>(
  variables: Types.FinmodelFailureViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.FinmodelFailureViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.FinmodelFailureViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.FinmodelFailureViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "FinmodelFailureView.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          adminFetcher<
            Types.FinmodelFailureViewQuery,
            Types.FinmodelFailureViewQueryVariables
          >(FinmodelFailureViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteFinmodelFailureViewQuery.getKey = (
  variables: Types.FinmodelFailureViewQueryVariables,
) => ["FinmodelFailureView.infiniteSuspense", variables]

useSuspenseInfiniteFinmodelFailureViewQuery.rootKey = "FinmodelFailureView.infinite"

useFinmodelFailureViewQuery.fetcher = (
  variables: Types.FinmodelFailureViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.FinmodelFailureViewQuery, Types.FinmodelFailureViewQueryVariables>(
    FinmodelFailureViewDocument,
    variables,
    options,
  )

export const FinmodelFailureRowDocument = `
    query FinmodelFailureRow($keys: [Int!]!) {
  finmodelFailures {
    rows(keys: $keys) {
      id
      createdAt
      fmv
      name
      message
    }
  }
}
    `

export const useFinmodelFailureRowQuery = <
  TData = Types.FinmodelFailureRowQuery,
  TError = unknown,
>(
  variables: Types.FinmodelFailureRowQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.FinmodelFailureRowQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.FinmodelFailureRowQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.FinmodelFailureRowQuery, TError, TData>({
    queryKey: ["FinmodelFailureRow", variables],
    queryFn: adminFetcher<
      Types.FinmodelFailureRowQuery,
      Types.FinmodelFailureRowQueryVariables
    >(FinmodelFailureRowDocument, variables),
    ...options,
  })
}

useFinmodelFailureRowQuery.getKey = (
  variables: Types.FinmodelFailureRowQueryVariables,
) => ["FinmodelFailureRow", variables]

useFinmodelFailureRowQuery.rootKey = "FinmodelFailureRow"

export const useSuspenseFinmodelFailureRowQuery = <
  TData = Types.FinmodelFailureRowQuery,
  TError = unknown,
>(
  variables: Types.FinmodelFailureRowQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.FinmodelFailureRowQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.FinmodelFailureRowQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.FinmodelFailureRowQuery, TError, TData>({
    queryKey: ["FinmodelFailureRowSuspense", variables],
    queryFn: adminFetcher<
      Types.FinmodelFailureRowQuery,
      Types.FinmodelFailureRowQueryVariables
    >(FinmodelFailureRowDocument, variables),
    ...options,
  })
}

useSuspenseFinmodelFailureRowQuery.getKey = (
  variables: Types.FinmodelFailureRowQueryVariables,
) => ["FinmodelFailureRowSuspense", variables]

useSuspenseFinmodelFailureRowQuery.rootKey = "FinmodelFailureRow"

export const useInfiniteFinmodelFailureRowQuery = <
  TData = InfiniteData<Types.FinmodelFailureRowQuery>,
  TError = unknown,
>(
  variables: Types.FinmodelFailureRowQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.FinmodelFailureRowQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.FinmodelFailureRowQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.FinmodelFailureRowQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["FinmodelFailureRow.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.FinmodelFailureRowQuery,
            Types.FinmodelFailureRowQueryVariables
          >(FinmodelFailureRowDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteFinmodelFailureRowQuery.getKey = (
  variables: Types.FinmodelFailureRowQueryVariables,
) => ["FinmodelFailureRow.infinite", variables]

useInfiniteFinmodelFailureRowQuery.rootKey = "FinmodelFailureRow.infinite"

export const useSuspenseInfiniteFinmodelFailureRowQuery = <
  TData = InfiniteData<Types.FinmodelFailureRowQuery>,
  TError = unknown,
>(
  variables: Types.FinmodelFailureRowQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.FinmodelFailureRowQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.FinmodelFailureRowQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.FinmodelFailureRowQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["FinmodelFailureRow.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.FinmodelFailureRowQuery,
            Types.FinmodelFailureRowQueryVariables
          >(FinmodelFailureRowDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteFinmodelFailureRowQuery.getKey = (
  variables: Types.FinmodelFailureRowQueryVariables,
) => ["FinmodelFailureRow.infiniteSuspense", variables]

useSuspenseInfiniteFinmodelFailureRowQuery.rootKey = "FinmodelFailureRow.infinite"

useFinmodelFailureRowQuery.fetcher = (
  variables: Types.FinmodelFailureRowQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.FinmodelFailureRowQuery, Types.FinmodelFailureRowQueryVariables>(
    FinmodelFailureRowDocument,
    variables,
    options,
  )

export const FinmodelFailureDetailDocument = `
    query FinmodelFailureDetail($id: Int!) {
  finmodelFailure(id: $id) {
    ...FinmodelFailureDetail
    finmodel {
      id
      fmvStr
    }
  }
}
    ${FinmodelFailureDetailFragmentDoc}
${SiteRefFragmentDoc}
${ScenarioRefFragmentDoc}
${FinmodelSourceFragmentFragmentDoc}`

export const useFinmodelFailureDetailQuery = <
  TData = Types.FinmodelFailureDetailQuery,
  TError = unknown,
>(
  variables: Types.FinmodelFailureDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.FinmodelFailureDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.FinmodelFailureDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.FinmodelFailureDetailQuery, TError, TData>({
    queryKey: ["FinmodelFailureDetail", variables],
    queryFn: adminFetcher<
      Types.FinmodelFailureDetailQuery,
      Types.FinmodelFailureDetailQueryVariables
    >(FinmodelFailureDetailDocument, variables),
    ...options,
  })
}

useFinmodelFailureDetailQuery.getKey = (
  variables: Types.FinmodelFailureDetailQueryVariables,
) => ["FinmodelFailureDetail", variables]

useFinmodelFailureDetailQuery.rootKey = "FinmodelFailureDetail"

export const useSuspenseFinmodelFailureDetailQuery = <
  TData = Types.FinmodelFailureDetailQuery,
  TError = unknown,
>(
  variables: Types.FinmodelFailureDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.FinmodelFailureDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.FinmodelFailureDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.FinmodelFailureDetailQuery, TError, TData>({
    queryKey: ["FinmodelFailureDetailSuspense", variables],
    queryFn: adminFetcher<
      Types.FinmodelFailureDetailQuery,
      Types.FinmodelFailureDetailQueryVariables
    >(FinmodelFailureDetailDocument, variables),
    ...options,
  })
}

useSuspenseFinmodelFailureDetailQuery.getKey = (
  variables: Types.FinmodelFailureDetailQueryVariables,
) => ["FinmodelFailureDetailSuspense", variables]

useSuspenseFinmodelFailureDetailQuery.rootKey = "FinmodelFailureDetail"

export const useInfiniteFinmodelFailureDetailQuery = <
  TData = InfiniteData<Types.FinmodelFailureDetailQuery>,
  TError = unknown,
>(
  variables: Types.FinmodelFailureDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.FinmodelFailureDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.FinmodelFailureDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.FinmodelFailureDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["FinmodelFailureDetail.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.FinmodelFailureDetailQuery,
            Types.FinmodelFailureDetailQueryVariables
          >(FinmodelFailureDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteFinmodelFailureDetailQuery.getKey = (
  variables: Types.FinmodelFailureDetailQueryVariables,
) => ["FinmodelFailureDetail.infinite", variables]

useInfiniteFinmodelFailureDetailQuery.rootKey = "FinmodelFailureDetail.infinite"

export const useSuspenseInfiniteFinmodelFailureDetailQuery = <
  TData = InfiniteData<Types.FinmodelFailureDetailQuery>,
  TError = unknown,
>(
  variables: Types.FinmodelFailureDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.FinmodelFailureDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.FinmodelFailureDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.FinmodelFailureDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "FinmodelFailureDetail.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          adminFetcher<
            Types.FinmodelFailureDetailQuery,
            Types.FinmodelFailureDetailQueryVariables
          >(FinmodelFailureDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteFinmodelFailureDetailQuery.getKey = (
  variables: Types.FinmodelFailureDetailQueryVariables,
) => ["FinmodelFailureDetail.infiniteSuspense", variables]

useSuspenseInfiniteFinmodelFailureDetailQuery.rootKey = "FinmodelFailureDetail.infinite"

useFinmodelFailureDetailQuery.fetcher = (
  variables: Types.FinmodelFailureDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.FinmodelFailureDetailQuery,
    Types.FinmodelFailureDetailQueryVariables
  >(FinmodelFailureDetailDocument, variables, options)

export const MultiMeterDetailDocument = `
    query MultiMeterDetail($id: Int!) {
  bauMeter(id: $id) {
    id
    formattedBill {
      ...FormattedBill
    }
    formattedPostBill {
      ...FormattedBill
    }
    tariff {
      name
      code
    }
    tariffUsdPerKwh
    postTariff {
      name
      code
    }
  }
}
    ${FormattedBillFragmentDoc}
${BillSectionFragmentDoc}
${DecodedRateFragmentDoc}
${CostOrTaxItemFragmentDoc}`

export const useMultiMeterDetailQuery = <
  TData = Types.MultiMeterDetailQuery,
  TError = unknown,
>(
  variables: Types.MultiMeterDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.MultiMeterDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.MultiMeterDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.MultiMeterDetailQuery, TError, TData>({
    queryKey: ["MultiMeterDetail", variables],
    queryFn: adminFetcher<
      Types.MultiMeterDetailQuery,
      Types.MultiMeterDetailQueryVariables
    >(MultiMeterDetailDocument, variables),
    ...options,
  })
}

useMultiMeterDetailQuery.getKey = (variables: Types.MultiMeterDetailQueryVariables) => [
  "MultiMeterDetail",
  variables,
]

useMultiMeterDetailQuery.rootKey = "MultiMeterDetail"

export const useSuspenseMultiMeterDetailQuery = <
  TData = Types.MultiMeterDetailQuery,
  TError = unknown,
>(
  variables: Types.MultiMeterDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MultiMeterDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.MultiMeterDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MultiMeterDetailQuery, TError, TData>({
    queryKey: ["MultiMeterDetailSuspense", variables],
    queryFn: adminFetcher<
      Types.MultiMeterDetailQuery,
      Types.MultiMeterDetailQueryVariables
    >(MultiMeterDetailDocument, variables),
    ...options,
  })
}

useSuspenseMultiMeterDetailQuery.getKey = (
  variables: Types.MultiMeterDetailQueryVariables,
) => ["MultiMeterDetailSuspense", variables]

useSuspenseMultiMeterDetailQuery.rootKey = "MultiMeterDetail"

export const useInfiniteMultiMeterDetailQuery = <
  TData = InfiniteData<Types.MultiMeterDetailQuery>,
  TError = unknown,
>(
  variables: Types.MultiMeterDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MultiMeterDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.MultiMeterDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MultiMeterDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MultiMeterDetail.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.MultiMeterDetailQuery,
            Types.MultiMeterDetailQueryVariables
          >(MultiMeterDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMultiMeterDetailQuery.getKey = (
  variables: Types.MultiMeterDetailQueryVariables,
) => ["MultiMeterDetail.infinite", variables]

useInfiniteMultiMeterDetailQuery.rootKey = "MultiMeterDetail.infinite"

export const useSuspenseInfiniteMultiMeterDetailQuery = <
  TData = InfiniteData<Types.MultiMeterDetailQuery>,
  TError = unknown,
>(
  variables: Types.MultiMeterDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.MultiMeterDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MultiMeterDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.MultiMeterDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MultiMeterDetail.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.MultiMeterDetailQuery,
            Types.MultiMeterDetailQueryVariables
          >(MultiMeterDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMultiMeterDetailQuery.getKey = (
  variables: Types.MultiMeterDetailQueryVariables,
) => ["MultiMeterDetail.infiniteSuspense", variables]

useSuspenseInfiniteMultiMeterDetailQuery.rootKey = "MultiMeterDetail.infinite"

useMultiMeterDetailQuery.fetcher = (
  variables: Types.MultiMeterDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.MultiMeterDetailQuery, Types.MultiMeterDetailQueryVariables>(
    MultiMeterDetailDocument,
    variables,
    options,
  )

export const BauMeterNameThatTariffDetailDocument = `
    query BauMeterNameThatTariffDetail($id: Int!, $tariffId: Int!, $documentBillingMonthId: Int!) {
  bauMeter(id: $id) {
    nameThatTariffDetail(
      tariffId: $tariffId
      documentBillingMonthId: $documentBillingMonthId
    ) {
      tariff {
        id
        masterTariffId
        code
        name
      }
      result {
        ...NameThatTariffResult
      }
      groups {
        group
        items {
          cost
          usage
          name
          rate
        }
      }
    }
  }
}
    ${NameThatTariffResultFragmentDoc}
${DeltaFragmentDoc}`

export const useBauMeterNameThatTariffDetailQuery = <
  TData = Types.BauMeterNameThatTariffDetailQuery,
  TError = unknown,
>(
  variables: Types.BauMeterNameThatTariffDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.BauMeterNameThatTariffDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.BauMeterNameThatTariffDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.BauMeterNameThatTariffDetailQuery, TError, TData>({
    queryKey: ["BauMeterNameThatTariffDetail", variables],
    queryFn: adminFetcher<
      Types.BauMeterNameThatTariffDetailQuery,
      Types.BauMeterNameThatTariffDetailQueryVariables
    >(BauMeterNameThatTariffDetailDocument, variables),
    ...options,
  })
}

useBauMeterNameThatTariffDetailQuery.getKey = (
  variables: Types.BauMeterNameThatTariffDetailQueryVariables,
) => ["BauMeterNameThatTariffDetail", variables]

useBauMeterNameThatTariffDetailQuery.rootKey = "BauMeterNameThatTariffDetail"

export const useSuspenseBauMeterNameThatTariffDetailQuery = <
  TData = Types.BauMeterNameThatTariffDetailQuery,
  TError = unknown,
>(
  variables: Types.BauMeterNameThatTariffDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.BauMeterNameThatTariffDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.BauMeterNameThatTariffDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.BauMeterNameThatTariffDetailQuery, TError, TData>({
    queryKey: ["BauMeterNameThatTariffDetailSuspense", variables],
    queryFn: adminFetcher<
      Types.BauMeterNameThatTariffDetailQuery,
      Types.BauMeterNameThatTariffDetailQueryVariables
    >(BauMeterNameThatTariffDetailDocument, variables),
    ...options,
  })
}

useSuspenseBauMeterNameThatTariffDetailQuery.getKey = (
  variables: Types.BauMeterNameThatTariffDetailQueryVariables,
) => ["BauMeterNameThatTariffDetailSuspense", variables]

useSuspenseBauMeterNameThatTariffDetailQuery.rootKey = "BauMeterNameThatTariffDetail"

export const useInfiniteBauMeterNameThatTariffDetailQuery = <
  TData = InfiniteData<Types.BauMeterNameThatTariffDetailQuery>,
  TError = unknown,
>(
  variables: Types.BauMeterNameThatTariffDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.BauMeterNameThatTariffDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.BauMeterNameThatTariffDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.BauMeterNameThatTariffDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "BauMeterNameThatTariffDetail.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          adminFetcher<
            Types.BauMeterNameThatTariffDetailQuery,
            Types.BauMeterNameThatTariffDetailQueryVariables
          >(BauMeterNameThatTariffDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteBauMeterNameThatTariffDetailQuery.getKey = (
  variables: Types.BauMeterNameThatTariffDetailQueryVariables,
) => ["BauMeterNameThatTariffDetail.infinite", variables]

useInfiniteBauMeterNameThatTariffDetailQuery.rootKey =
  "BauMeterNameThatTariffDetail.infinite"

export const useSuspenseInfiniteBauMeterNameThatTariffDetailQuery = <
  TData = InfiniteData<Types.BauMeterNameThatTariffDetailQuery>,
  TError = unknown,
>(
  variables: Types.BauMeterNameThatTariffDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.BauMeterNameThatTariffDetailQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.BauMeterNameThatTariffDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.BauMeterNameThatTariffDetailQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "BauMeterNameThatTariffDetail.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          adminFetcher<
            Types.BauMeterNameThatTariffDetailQuery,
            Types.BauMeterNameThatTariffDetailQueryVariables
          >(BauMeterNameThatTariffDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteBauMeterNameThatTariffDetailQuery.getKey = (
  variables: Types.BauMeterNameThatTariffDetailQueryVariables,
) => ["BauMeterNameThatTariffDetail.infiniteSuspense", variables]

useSuspenseInfiniteBauMeterNameThatTariffDetailQuery.rootKey =
  "BauMeterNameThatTariffDetail.infinite"

useBauMeterNameThatTariffDetailQuery.fetcher = (
  variables: Types.BauMeterNameThatTariffDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.BauMeterNameThatTariffDetailQuery,
    Types.BauMeterNameThatTariffDetailQueryVariables
  >(BauMeterNameThatTariffDetailDocument, variables, options)

export const Xl2RenderDocument = `
    query Xl2Render($flavor: XlFlavor) {
  xl2(flavor: $flavor)
}
    `

export const useXl2RenderQuery = <TData = Types.Xl2RenderQuery, TError = unknown>(
  variables?: Types.Xl2RenderQueryVariables,
  options?: Omit<UseQueryOptions<Types.Xl2RenderQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.Xl2RenderQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.Xl2RenderQuery, TError, TData>({
    queryKey: variables === undefined ? ["Xl2Render"] : ["Xl2Render", variables],
    queryFn: adminFetcher<Types.Xl2RenderQuery, Types.Xl2RenderQueryVariables>(
      Xl2RenderDocument,
      variables,
    ),
    ...options,
  })
}

useXl2RenderQuery.getKey = (variables?: Types.Xl2RenderQueryVariables) =>
  variables === undefined ? ["Xl2Render"] : ["Xl2Render", variables]

useXl2RenderQuery.rootKey = "Xl2Render"

export const useSuspenseXl2RenderQuery = <
  TData = Types.Xl2RenderQuery,
  TError = unknown,
>(
  variables?: Types.Xl2RenderQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.Xl2RenderQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.Xl2RenderQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.Xl2RenderQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["Xl2RenderSuspense"]
        : ["Xl2RenderSuspense", variables],
    queryFn: adminFetcher<Types.Xl2RenderQuery, Types.Xl2RenderQueryVariables>(
      Xl2RenderDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseXl2RenderQuery.getKey = (variables?: Types.Xl2RenderQueryVariables) =>
  variables === undefined ? ["Xl2RenderSuspense"] : ["Xl2RenderSuspense", variables]

useSuspenseXl2RenderQuery.rootKey = "Xl2Render"

export const useInfiniteXl2RenderQuery = <
  TData = InfiniteData<Types.Xl2RenderQuery>,
  TError = unknown,
>(
  variables: Types.Xl2RenderQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.Xl2RenderQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.Xl2RenderQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.Xl2RenderQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["Xl2Render.infinite"]
            : ["Xl2Render.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.Xl2RenderQuery, Types.Xl2RenderQueryVariables>(
            Xl2RenderDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteXl2RenderQuery.getKey = (variables?: Types.Xl2RenderQueryVariables) =>
  variables === undefined ? ["Xl2Render.infinite"] : ["Xl2Render.infinite", variables]

useInfiniteXl2RenderQuery.rootKey = "Xl2Render.infinite"

export const useSuspenseInfiniteXl2RenderQuery = <
  TData = InfiniteData<Types.Xl2RenderQuery>,
  TError = unknown,
>(
  variables: Types.Xl2RenderQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.Xl2RenderQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.Xl2RenderQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.Xl2RenderQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["Xl2Render.infiniteSuspense"]
            : ["Xl2Render.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.Xl2RenderQuery, Types.Xl2RenderQueryVariables>(
            Xl2RenderDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteXl2RenderQuery.getKey = (
  variables?: Types.Xl2RenderQueryVariables,
) =>
  variables === undefined
    ? ["Xl2Render.infiniteSuspense"]
    : ["Xl2Render.infiniteSuspense", variables]

useSuspenseInfiniteXl2RenderQuery.rootKey = "Xl2Render.infinite"

useXl2RenderQuery.fetcher = (
  variables?: Types.Xl2RenderQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.Xl2RenderQuery, Types.Xl2RenderQueryVariables>(
    Xl2RenderDocument,
    variables,
    options,
  )

export const Xl2FlavorsDocument = `
    query Xl2Flavors {
  xl2Flavors
}
    `

export const useXl2FlavorsQuery = <TData = Types.Xl2FlavorsQuery, TError = unknown>(
  variables?: Types.Xl2FlavorsQueryVariables,
  options?: Omit<UseQueryOptions<Types.Xl2FlavorsQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.Xl2FlavorsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.Xl2FlavorsQuery, TError, TData>({
    queryKey: variables === undefined ? ["Xl2Flavors"] : ["Xl2Flavors", variables],
    queryFn: adminFetcher<Types.Xl2FlavorsQuery, Types.Xl2FlavorsQueryVariables>(
      Xl2FlavorsDocument,
      variables,
    ),
    ...options,
  })
}

useXl2FlavorsQuery.getKey = (variables?: Types.Xl2FlavorsQueryVariables) =>
  variables === undefined ? ["Xl2Flavors"] : ["Xl2Flavors", variables]

useXl2FlavorsQuery.rootKey = "Xl2Flavors"

export const useSuspenseXl2FlavorsQuery = <
  TData = Types.Xl2FlavorsQuery,
  TError = unknown,
>(
  variables?: Types.Xl2FlavorsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.Xl2FlavorsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.Xl2FlavorsQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.Xl2FlavorsQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["Xl2FlavorsSuspense"]
        : ["Xl2FlavorsSuspense", variables],
    queryFn: adminFetcher<Types.Xl2FlavorsQuery, Types.Xl2FlavorsQueryVariables>(
      Xl2FlavorsDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseXl2FlavorsQuery.getKey = (variables?: Types.Xl2FlavorsQueryVariables) =>
  variables === undefined ? ["Xl2FlavorsSuspense"] : ["Xl2FlavorsSuspense", variables]

useSuspenseXl2FlavorsQuery.rootKey = "Xl2Flavors"

export const useInfiniteXl2FlavorsQuery = <
  TData = InfiniteData<Types.Xl2FlavorsQuery>,
  TError = unknown,
>(
  variables: Types.Xl2FlavorsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.Xl2FlavorsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.Xl2FlavorsQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.Xl2FlavorsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["Xl2Flavors.infinite"]
            : ["Xl2Flavors.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.Xl2FlavorsQuery, Types.Xl2FlavorsQueryVariables>(
            Xl2FlavorsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteXl2FlavorsQuery.getKey = (variables?: Types.Xl2FlavorsQueryVariables) =>
  variables === undefined ? ["Xl2Flavors.infinite"] : ["Xl2Flavors.infinite", variables]

useInfiniteXl2FlavorsQuery.rootKey = "Xl2Flavors.infinite"

export const useSuspenseInfiniteXl2FlavorsQuery = <
  TData = InfiniteData<Types.Xl2FlavorsQuery>,
  TError = unknown,
>(
  variables: Types.Xl2FlavorsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.Xl2FlavorsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.Xl2FlavorsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.Xl2FlavorsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["Xl2Flavors.infiniteSuspense"]
            : ["Xl2Flavors.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.Xl2FlavorsQuery, Types.Xl2FlavorsQueryVariables>(
            Xl2FlavorsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteXl2FlavorsQuery.getKey = (
  variables?: Types.Xl2FlavorsQueryVariables,
) =>
  variables === undefined
    ? ["Xl2Flavors.infiniteSuspense"]
    : ["Xl2Flavors.infiniteSuspense", variables]

useSuspenseInfiniteXl2FlavorsQuery.rootKey = "Xl2Flavors.infinite"

useXl2FlavorsQuery.fetcher = (
  variables?: Types.Xl2FlavorsQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.Xl2FlavorsQuery, Types.Xl2FlavorsQueryVariables>(
    Xl2FlavorsDocument,
    variables,
    options,
  )

export const TaskCountsDocument = `
    query TaskCounts($orgPid: String, $sitePid: String) {
  taskCounts(orgPid: $orgPid, sitePid: $sitePid) {
    name
    runningCount
    pendingCount
    successCount
    failureCount
    canceledCount
  }
}
    `

export const useTaskCountsQuery = <TData = Types.TaskCountsQuery, TError = unknown>(
  variables?: Types.TaskCountsQueryVariables,
  options?: Omit<UseQueryOptions<Types.TaskCountsQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.TaskCountsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.TaskCountsQuery, TError, TData>({
    queryKey: variables === undefined ? ["TaskCounts"] : ["TaskCounts", variables],
    queryFn: adminFetcher<Types.TaskCountsQuery, Types.TaskCountsQueryVariables>(
      TaskCountsDocument,
      variables,
    ),
    ...options,
  })
}

useTaskCountsQuery.getKey = (variables?: Types.TaskCountsQueryVariables) =>
  variables === undefined ? ["TaskCounts"] : ["TaskCounts", variables]

useTaskCountsQuery.rootKey = "TaskCounts"

export const useSuspenseTaskCountsQuery = <
  TData = Types.TaskCountsQuery,
  TError = unknown,
>(
  variables?: Types.TaskCountsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.TaskCountsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.TaskCountsQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.TaskCountsQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["TaskCountsSuspense"]
        : ["TaskCountsSuspense", variables],
    queryFn: adminFetcher<Types.TaskCountsQuery, Types.TaskCountsQueryVariables>(
      TaskCountsDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseTaskCountsQuery.getKey = (variables?: Types.TaskCountsQueryVariables) =>
  variables === undefined ? ["TaskCountsSuspense"] : ["TaskCountsSuspense", variables]

useSuspenseTaskCountsQuery.rootKey = "TaskCounts"

export const useInfiniteTaskCountsQuery = <
  TData = InfiniteData<Types.TaskCountsQuery>,
  TError = unknown,
>(
  variables: Types.TaskCountsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.TaskCountsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.TaskCountsQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.TaskCountsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["TaskCounts.infinite"]
            : ["TaskCounts.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.TaskCountsQuery, Types.TaskCountsQueryVariables>(
            TaskCountsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteTaskCountsQuery.getKey = (variables?: Types.TaskCountsQueryVariables) =>
  variables === undefined ? ["TaskCounts.infinite"] : ["TaskCounts.infinite", variables]

useInfiniteTaskCountsQuery.rootKey = "TaskCounts.infinite"

export const useSuspenseInfiniteTaskCountsQuery = <
  TData = InfiniteData<Types.TaskCountsQuery>,
  TError = unknown,
>(
  variables: Types.TaskCountsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.TaskCountsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.TaskCountsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.TaskCountsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["TaskCounts.infiniteSuspense"]
            : ["TaskCounts.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.TaskCountsQuery, Types.TaskCountsQueryVariables>(
            TaskCountsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteTaskCountsQuery.getKey = (
  variables?: Types.TaskCountsQueryVariables,
) =>
  variables === undefined
    ? ["TaskCounts.infiniteSuspense"]
    : ["TaskCounts.infiniteSuspense", variables]

useSuspenseInfiniteTaskCountsQuery.rootKey = "TaskCounts.infinite"

useTaskCountsQuery.fetcher = (
  variables?: Types.TaskCountsQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.TaskCountsQuery, Types.TaskCountsQueryVariables>(
    TaskCountsDocument,
    variables,
    options,
  )

export const OplogCountsDocument = `
    query OplogCounts {
  oplogCounts {
    name
    runningCount
    pendingCount
    successCount
    failureCount
    canceledCount
  }
}
    `

export const useOplogCountsQuery = <TData = Types.OplogCountsQuery, TError = unknown>(
  variables?: Types.OplogCountsQueryVariables,
  options?: Omit<UseQueryOptions<Types.OplogCountsQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.OplogCountsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.OplogCountsQuery, TError, TData>({
    queryKey: variables === undefined ? ["OplogCounts"] : ["OplogCounts", variables],
    queryFn: adminFetcher<Types.OplogCountsQuery, Types.OplogCountsQueryVariables>(
      OplogCountsDocument,
      variables,
    ),
    ...options,
  })
}

useOplogCountsQuery.getKey = (variables?: Types.OplogCountsQueryVariables) =>
  variables === undefined ? ["OplogCounts"] : ["OplogCounts", variables]

useOplogCountsQuery.rootKey = "OplogCounts"

export const useSuspenseOplogCountsQuery = <
  TData = Types.OplogCountsQuery,
  TError = unknown,
>(
  variables?: Types.OplogCountsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OplogCountsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.OplogCountsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OplogCountsQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["OplogCountsSuspense"]
        : ["OplogCountsSuspense", variables],
    queryFn: adminFetcher<Types.OplogCountsQuery, Types.OplogCountsQueryVariables>(
      OplogCountsDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseOplogCountsQuery.getKey = (variables?: Types.OplogCountsQueryVariables) =>
  variables === undefined ? ["OplogCountsSuspense"] : ["OplogCountsSuspense", variables]

useSuspenseOplogCountsQuery.rootKey = "OplogCounts"

export const useInfiniteOplogCountsQuery = <
  TData = InfiniteData<Types.OplogCountsQuery>,
  TError = unknown,
>(
  variables: Types.OplogCountsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OplogCountsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.OplogCountsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OplogCountsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["OplogCounts.infinite"]
            : ["OplogCounts.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.OplogCountsQuery, Types.OplogCountsQueryVariables>(
            OplogCountsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOplogCountsQuery.getKey = (variables?: Types.OplogCountsQueryVariables) =>
  variables === undefined
    ? ["OplogCounts.infinite"]
    : ["OplogCounts.infinite", variables]

useInfiniteOplogCountsQuery.rootKey = "OplogCounts.infinite"

export const useSuspenseInfiniteOplogCountsQuery = <
  TData = InfiniteData<Types.OplogCountsQuery>,
  TError = unknown,
>(
  variables: Types.OplogCountsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OplogCountsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OplogCountsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OplogCountsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["OplogCounts.infiniteSuspense"]
            : ["OplogCounts.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.OplogCountsQuery, Types.OplogCountsQueryVariables>(
            OplogCountsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOplogCountsQuery.getKey = (
  variables?: Types.OplogCountsQueryVariables,
) =>
  variables === undefined
    ? ["OplogCounts.infiniteSuspense"]
    : ["OplogCounts.infiniteSuspense", variables]

useSuspenseInfiniteOplogCountsQuery.rootKey = "OplogCounts.infinite"

useOplogCountsQuery.fetcher = (
  variables?: Types.OplogCountsQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.OplogCountsQuery, Types.OplogCountsQueryVariables>(
    OplogCountsDocument,
    variables,
    options,
  )

export const ActiveBoostsDocument = `
    query ActiveBoosts($orgPid: String!) {
  activeBoosts(orgPid: $orgPid)
}
    `

export const useActiveBoostsQuery = <TData = Types.ActiveBoostsQuery, TError = unknown>(
  variables: Types.ActiveBoostsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ActiveBoostsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.ActiveBoostsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ActiveBoostsQuery, TError, TData>({
    queryKey: ["ActiveBoosts", variables],
    queryFn: adminFetcher<Types.ActiveBoostsQuery, Types.ActiveBoostsQueryVariables>(
      ActiveBoostsDocument,
      variables,
    ),
    ...options,
  })
}

useActiveBoostsQuery.getKey = (variables: Types.ActiveBoostsQueryVariables) => [
  "ActiveBoosts",
  variables,
]

useActiveBoostsQuery.rootKey = "ActiveBoosts"

export const useSuspenseActiveBoostsQuery = <
  TData = Types.ActiveBoostsQuery,
  TError = unknown,
>(
  variables: Types.ActiveBoostsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ActiveBoostsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ActiveBoostsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ActiveBoostsQuery, TError, TData>({
    queryKey: ["ActiveBoostsSuspense", variables],
    queryFn: adminFetcher<Types.ActiveBoostsQuery, Types.ActiveBoostsQueryVariables>(
      ActiveBoostsDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseActiveBoostsQuery.getKey = (variables: Types.ActiveBoostsQueryVariables) => [
  "ActiveBoostsSuspense",
  variables,
]

useSuspenseActiveBoostsQuery.rootKey = "ActiveBoosts"

export const useInfiniteActiveBoostsQuery = <
  TData = InfiniteData<Types.ActiveBoostsQuery>,
  TError = unknown,
>(
  variables: Types.ActiveBoostsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ActiveBoostsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ActiveBoostsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ActiveBoostsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ActiveBoosts.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.ActiveBoostsQuery, Types.ActiveBoostsQueryVariables>(
            ActiveBoostsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteActiveBoostsQuery.getKey = (variables: Types.ActiveBoostsQueryVariables) => [
  "ActiveBoosts.infinite",
  variables,
]

useInfiniteActiveBoostsQuery.rootKey = "ActiveBoosts.infinite"

export const useSuspenseInfiniteActiveBoostsQuery = <
  TData = InfiniteData<Types.ActiveBoostsQuery>,
  TError = unknown,
>(
  variables: Types.ActiveBoostsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ActiveBoostsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ActiveBoostsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ActiveBoostsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ActiveBoosts.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.ActiveBoostsQuery, Types.ActiveBoostsQueryVariables>(
            ActiveBoostsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteActiveBoostsQuery.getKey = (
  variables: Types.ActiveBoostsQueryVariables,
) => ["ActiveBoosts.infiniteSuspense", variables]

useSuspenseInfiniteActiveBoostsQuery.rootKey = "ActiveBoosts.infinite"

useActiveBoostsQuery.fetcher = (
  variables: Types.ActiveBoostsQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.ActiveBoostsQuery, Types.ActiveBoostsQueryVariables>(
    ActiveBoostsDocument,
    variables,
    options,
  )

export const OrgPriorityDocument = `
    query OrgPriority($orgPid: String!) {
  orgPriority(orgPid: $orgPid)
}
    `

export const useOrgPriorityQuery = <TData = Types.OrgPriorityQuery, TError = unknown>(
  variables: Types.OrgPriorityQueryVariables,
  options?: Omit<UseQueryOptions<Types.OrgPriorityQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.OrgPriorityQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.OrgPriorityQuery, TError, TData>({
    queryKey: ["OrgPriority", variables],
    queryFn: adminFetcher<Types.OrgPriorityQuery, Types.OrgPriorityQueryVariables>(
      OrgPriorityDocument,
      variables,
    ),
    ...options,
  })
}

useOrgPriorityQuery.getKey = (variables: Types.OrgPriorityQueryVariables) => [
  "OrgPriority",
  variables,
]

useOrgPriorityQuery.rootKey = "OrgPriority"

export const useSuspenseOrgPriorityQuery = <
  TData = Types.OrgPriorityQuery,
  TError = unknown,
>(
  variables: Types.OrgPriorityQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OrgPriorityQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.OrgPriorityQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OrgPriorityQuery, TError, TData>({
    queryKey: ["OrgPrioritySuspense", variables],
    queryFn: adminFetcher<Types.OrgPriorityQuery, Types.OrgPriorityQueryVariables>(
      OrgPriorityDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseOrgPriorityQuery.getKey = (variables: Types.OrgPriorityQueryVariables) => [
  "OrgPrioritySuspense",
  variables,
]

useSuspenseOrgPriorityQuery.rootKey = "OrgPriority"

export const useInfiniteOrgPriorityQuery = <
  TData = InfiniteData<Types.OrgPriorityQuery>,
  TError = unknown,
>(
  variables: Types.OrgPriorityQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OrgPriorityQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.OrgPriorityQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OrgPriorityQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgPriority.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.OrgPriorityQuery, Types.OrgPriorityQueryVariables>(
            OrgPriorityDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOrgPriorityQuery.getKey = (variables: Types.OrgPriorityQueryVariables) => [
  "OrgPriority.infinite",
  variables,
]

useInfiniteOrgPriorityQuery.rootKey = "OrgPriority.infinite"

export const useSuspenseInfiniteOrgPriorityQuery = <
  TData = InfiniteData<Types.OrgPriorityQuery>,
  TError = unknown,
>(
  variables: Types.OrgPriorityQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OrgPriorityQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OrgPriorityQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OrgPriorityQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgPriority.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.OrgPriorityQuery, Types.OrgPriorityQueryVariables>(
            OrgPriorityDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOrgPriorityQuery.getKey = (
  variables: Types.OrgPriorityQueryVariables,
) => ["OrgPriority.infiniteSuspense", variables]

useSuspenseInfiniteOrgPriorityQuery.rootKey = "OrgPriority.infinite"

useOrgPriorityQuery.fetcher = (
  variables: Types.OrgPriorityQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.OrgPriorityQuery, Types.OrgPriorityQueryVariables>(
    OrgPriorityDocument,
    variables,
    options,
  )

export const TaskHistoryDocument = `
    query TaskHistory {
  taskHistory {
    timestamps
    taskNames
    series {
      taskName
      counts
    }
  }
}
    `

export const useTaskHistoryQuery = <TData = Types.TaskHistoryQuery, TError = unknown>(
  variables?: Types.TaskHistoryQueryVariables,
  options?: Omit<UseQueryOptions<Types.TaskHistoryQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.TaskHistoryQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.TaskHistoryQuery, TError, TData>({
    queryKey: variables === undefined ? ["TaskHistory"] : ["TaskHistory", variables],
    queryFn: adminFetcher<Types.TaskHistoryQuery, Types.TaskHistoryQueryVariables>(
      TaskHistoryDocument,
      variables,
    ),
    ...options,
  })
}

useTaskHistoryQuery.getKey = (variables?: Types.TaskHistoryQueryVariables) =>
  variables === undefined ? ["TaskHistory"] : ["TaskHistory", variables]

useTaskHistoryQuery.rootKey = "TaskHistory"

export const useSuspenseTaskHistoryQuery = <
  TData = Types.TaskHistoryQuery,
  TError = unknown,
>(
  variables?: Types.TaskHistoryQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.TaskHistoryQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.TaskHistoryQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.TaskHistoryQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["TaskHistorySuspense"]
        : ["TaskHistorySuspense", variables],
    queryFn: adminFetcher<Types.TaskHistoryQuery, Types.TaskHistoryQueryVariables>(
      TaskHistoryDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseTaskHistoryQuery.getKey = (variables?: Types.TaskHistoryQueryVariables) =>
  variables === undefined ? ["TaskHistorySuspense"] : ["TaskHistorySuspense", variables]

useSuspenseTaskHistoryQuery.rootKey = "TaskHistory"

export const useInfiniteTaskHistoryQuery = <
  TData = InfiniteData<Types.TaskHistoryQuery>,
  TError = unknown,
>(
  variables: Types.TaskHistoryQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.TaskHistoryQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.TaskHistoryQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.TaskHistoryQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["TaskHistory.infinite"]
            : ["TaskHistory.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.TaskHistoryQuery, Types.TaskHistoryQueryVariables>(
            TaskHistoryDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteTaskHistoryQuery.getKey = (variables?: Types.TaskHistoryQueryVariables) =>
  variables === undefined
    ? ["TaskHistory.infinite"]
    : ["TaskHistory.infinite", variables]

useInfiniteTaskHistoryQuery.rootKey = "TaskHistory.infinite"

export const useSuspenseInfiniteTaskHistoryQuery = <
  TData = InfiniteData<Types.TaskHistoryQuery>,
  TError = unknown,
>(
  variables: Types.TaskHistoryQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.TaskHistoryQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.TaskHistoryQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.TaskHistoryQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["TaskHistory.infiniteSuspense"]
            : ["TaskHistory.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.TaskHistoryQuery, Types.TaskHistoryQueryVariables>(
            TaskHistoryDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteTaskHistoryQuery.getKey = (
  variables?: Types.TaskHistoryQueryVariables,
) =>
  variables === undefined
    ? ["TaskHistory.infiniteSuspense"]
    : ["TaskHistory.infiniteSuspense", variables]

useSuspenseInfiniteTaskHistoryQuery.rootKey = "TaskHistory.infinite"

useTaskHistoryQuery.fetcher = (
  variables?: Types.TaskHistoryQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.TaskHistoryQuery, Types.TaskHistoryQueryVariables>(
    TaskHistoryDocument,
    variables,
    options,
  )

export const BauMeterBillComparisonDocument = `
    query BauMeterBillComparison($sitePid: String!, $meterId: Int!) {
  site(pid: $sitePid) {
    pid
    isMultifamily
    customTariffs {
      id
      name
      code
      parent {
        id
      }
    }
  }
  bauMeter(id: $meterId) {
    loadCategory
    tariff {
      id
      code
      name
    }
    documentBillingMonths {
      id
      start
      end
    }
  }
}
    `

export const useBauMeterBillComparisonQuery = <
  TData = Types.BauMeterBillComparisonQuery,
  TError = unknown,
>(
  variables: Types.BauMeterBillComparisonQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.BauMeterBillComparisonQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.BauMeterBillComparisonQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.BauMeterBillComparisonQuery, TError, TData>({
    queryKey: ["BauMeterBillComparison", variables],
    queryFn: adminFetcher<
      Types.BauMeterBillComparisonQuery,
      Types.BauMeterBillComparisonQueryVariables
    >(BauMeterBillComparisonDocument, variables),
    ...options,
  })
}

useBauMeterBillComparisonQuery.getKey = (
  variables: Types.BauMeterBillComparisonQueryVariables,
) => ["BauMeterBillComparison", variables]

useBauMeterBillComparisonQuery.rootKey = "BauMeterBillComparison"

export const useSuspenseBauMeterBillComparisonQuery = <
  TData = Types.BauMeterBillComparisonQuery,
  TError = unknown,
>(
  variables: Types.BauMeterBillComparisonQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.BauMeterBillComparisonQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.BauMeterBillComparisonQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.BauMeterBillComparisonQuery, TError, TData>({
    queryKey: ["BauMeterBillComparisonSuspense", variables],
    queryFn: adminFetcher<
      Types.BauMeterBillComparisonQuery,
      Types.BauMeterBillComparisonQueryVariables
    >(BauMeterBillComparisonDocument, variables),
    ...options,
  })
}

useSuspenseBauMeterBillComparisonQuery.getKey = (
  variables: Types.BauMeterBillComparisonQueryVariables,
) => ["BauMeterBillComparisonSuspense", variables]

useSuspenseBauMeterBillComparisonQuery.rootKey = "BauMeterBillComparison"

export const useInfiniteBauMeterBillComparisonQuery = <
  TData = InfiniteData<Types.BauMeterBillComparisonQuery>,
  TError = unknown,
>(
  variables: Types.BauMeterBillComparisonQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.BauMeterBillComparisonQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.BauMeterBillComparisonQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.BauMeterBillComparisonQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BauMeterBillComparison.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.BauMeterBillComparisonQuery,
            Types.BauMeterBillComparisonQueryVariables
          >(BauMeterBillComparisonDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteBauMeterBillComparisonQuery.getKey = (
  variables: Types.BauMeterBillComparisonQueryVariables,
) => ["BauMeterBillComparison.infinite", variables]

useInfiniteBauMeterBillComparisonQuery.rootKey = "BauMeterBillComparison.infinite"

export const useSuspenseInfiniteBauMeterBillComparisonQuery = <
  TData = InfiniteData<Types.BauMeterBillComparisonQuery>,
  TError = unknown,
>(
  variables: Types.BauMeterBillComparisonQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.BauMeterBillComparisonQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.BauMeterBillComparisonQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.BauMeterBillComparisonQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "BauMeterBillComparison.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          adminFetcher<
            Types.BauMeterBillComparisonQuery,
            Types.BauMeterBillComparisonQueryVariables
          >(BauMeterBillComparisonDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteBauMeterBillComparisonQuery.getKey = (
  variables: Types.BauMeterBillComparisonQueryVariables,
) => ["BauMeterBillComparison.infiniteSuspense", variables]

useSuspenseInfiniteBauMeterBillComparisonQuery.rootKey =
  "BauMeterBillComparison.infinite"

useBauMeterBillComparisonQuery.fetcher = (
  variables: Types.BauMeterBillComparisonQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.BauMeterBillComparisonQuery,
    Types.BauMeterBillComparisonQueryVariables
  >(BauMeterBillComparisonDocument, variables, options)

export const AdminDocumentDocument = `
    query AdminDocument($pid: String!) {
  document(pid: $pid) {
    ...Document
    isAnalyzable
    latestAnalysisRequest {
      ...DocumentAnalysisRequest
    }
    aiExtractionResults {
      ...AIExtractionResult
    }
  }
}
    ${DocumentFragmentDoc}
${DocumentAnalysisRequestFragmentDoc}
${AiExtractionResultFragmentDoc}`

export const useAdminDocumentQuery = <
  TData = Types.AdminDocumentQuery,
  TError = unknown,
>(
  variables: Types.AdminDocumentQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminDocumentQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.AdminDocumentQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.AdminDocumentQuery, TError, TData>({
    queryKey: ["AdminDocument", variables],
    queryFn: adminFetcher<Types.AdminDocumentQuery, Types.AdminDocumentQueryVariables>(
      AdminDocumentDocument,
      variables,
    ),
    ...options,
  })
}

useAdminDocumentQuery.getKey = (variables: Types.AdminDocumentQueryVariables) => [
  "AdminDocument",
  variables,
]

useAdminDocumentQuery.rootKey = "AdminDocument"

export const useSuspenseAdminDocumentQuery = <
  TData = Types.AdminDocumentQuery,
  TError = unknown,
>(
  variables: Types.AdminDocumentQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminDocumentQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminDocumentQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminDocumentQuery, TError, TData>({
    queryKey: ["AdminDocumentSuspense", variables],
    queryFn: adminFetcher<Types.AdminDocumentQuery, Types.AdminDocumentQueryVariables>(
      AdminDocumentDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseAdminDocumentQuery.getKey = (
  variables: Types.AdminDocumentQueryVariables,
) => ["AdminDocumentSuspense", variables]

useSuspenseAdminDocumentQuery.rootKey = "AdminDocument"

export const useInfiniteAdminDocumentQuery = <
  TData = InfiniteData<Types.AdminDocumentQuery>,
  TError = unknown,
>(
  variables: Types.AdminDocumentQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminDocumentQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminDocumentQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminDocumentQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminDocument.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.AdminDocumentQuery, Types.AdminDocumentQueryVariables>(
            AdminDocumentDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminDocumentQuery.getKey = (
  variables: Types.AdminDocumentQueryVariables,
) => ["AdminDocument.infinite", variables]

useInfiniteAdminDocumentQuery.rootKey = "AdminDocument.infinite"

export const useSuspenseInfiniteAdminDocumentQuery = <
  TData = InfiniteData<Types.AdminDocumentQuery>,
  TError = unknown,
>(
  variables: Types.AdminDocumentQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminDocumentQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminDocumentQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminDocumentQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminDocument.infiniteSuspense", variables],
        queryFn: (metaData) =>
          adminFetcher<Types.AdminDocumentQuery, Types.AdminDocumentQueryVariables>(
            AdminDocumentDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminDocumentQuery.getKey = (
  variables: Types.AdminDocumentQueryVariables,
) => ["AdminDocument.infiniteSuspense", variables]

useSuspenseInfiniteAdminDocumentQuery.rootKey = "AdminDocument.infinite"

useAdminDocumentQuery.fetcher = (
  variables: Types.AdminDocumentQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<Types.AdminDocumentQuery, Types.AdminDocumentQueryVariables>(
    AdminDocumentDocument,
    variables,
    options,
  )

export const AdminSiteBillingMetersDocument = `
    query AdminSiteBillingMeters($sitePid: String!) {
  site(pid: $sitePid) {
    pid
    billingMeters {
      id
      pid
      label
      createdAt
      archivedAt
      archivedReason
      archivedBy {
        pid
        email
      }
      disabledAt
      disabledReason
      disabledBy {
        pid
        email
      }
      utilityMeterId
      supplyUtilityMeterId
      config {
        bauMeter {
          id
        }
      }
      view {
        name
        isArchived
        isDisabled
        loadCategory
        buildingType
        totalAnnualKwh {
          number
          source
        }
        peakAnnualKw {
          number
          source
        }
        numBills
        numIntervals
        utility {
          pid
          name
          shortName
        }
        tariff {
          id
          pid
          name
          code
        }
      }
    }
  }
}
    `

export const useAdminSiteBillingMetersQuery = <
  TData = Types.AdminSiteBillingMetersQuery,
  TError = unknown,
>(
  variables: Types.AdminSiteBillingMetersQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminSiteBillingMetersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminSiteBillingMetersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminSiteBillingMetersQuery, TError, TData>({
    queryKey: ["AdminSiteBillingMeters", variables],
    queryFn: adminFetcher<
      Types.AdminSiteBillingMetersQuery,
      Types.AdminSiteBillingMetersQueryVariables
    >(AdminSiteBillingMetersDocument, variables),
    ...options,
  })
}

useAdminSiteBillingMetersQuery.getKey = (
  variables: Types.AdminSiteBillingMetersQueryVariables,
) => ["AdminSiteBillingMeters", variables]

useAdminSiteBillingMetersQuery.rootKey = "AdminSiteBillingMeters"

export const useSuspenseAdminSiteBillingMetersQuery = <
  TData = Types.AdminSiteBillingMetersQuery,
  TError = unknown,
>(
  variables: Types.AdminSiteBillingMetersQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminSiteBillingMetersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminSiteBillingMetersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminSiteBillingMetersQuery, TError, TData>({
    queryKey: ["AdminSiteBillingMetersSuspense", variables],
    queryFn: adminFetcher<
      Types.AdminSiteBillingMetersQuery,
      Types.AdminSiteBillingMetersQueryVariables
    >(AdminSiteBillingMetersDocument, variables),
    ...options,
  })
}

useSuspenseAdminSiteBillingMetersQuery.getKey = (
  variables: Types.AdminSiteBillingMetersQueryVariables,
) => ["AdminSiteBillingMetersSuspense", variables]

useSuspenseAdminSiteBillingMetersQuery.rootKey = "AdminSiteBillingMeters"

export const useInfiniteAdminSiteBillingMetersQuery = <
  TData = InfiniteData<Types.AdminSiteBillingMetersQuery>,
  TError = unknown,
>(
  variables: Types.AdminSiteBillingMetersQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminSiteBillingMetersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminSiteBillingMetersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminSiteBillingMetersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminSiteBillingMeters.infinite", variables],
        queryFn: (metaData) =>
          adminFetcher<
            Types.AdminSiteBillingMetersQuery,
            Types.AdminSiteBillingMetersQueryVariables
          >(AdminSiteBillingMetersDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminSiteBillingMetersQuery.getKey = (
  variables: Types.AdminSiteBillingMetersQueryVariables,
) => ["AdminSiteBillingMeters.infinite", variables]

useInfiniteAdminSiteBillingMetersQuery.rootKey = "AdminSiteBillingMeters.infinite"

export const useSuspenseInfiniteAdminSiteBillingMetersQuery = <
  TData = InfiniteData<Types.AdminSiteBillingMetersQuery>,
  TError = unknown,
>(
  variables: Types.AdminSiteBillingMetersQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminSiteBillingMetersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminSiteBillingMetersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminSiteBillingMetersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "AdminSiteBillingMeters.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          adminFetcher<
            Types.AdminSiteBillingMetersQuery,
            Types.AdminSiteBillingMetersQueryVariables
          >(AdminSiteBillingMetersDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminSiteBillingMetersQuery.getKey = (
  variables: Types.AdminSiteBillingMetersQueryVariables,
) => ["AdminSiteBillingMeters.infiniteSuspense", variables]

useSuspenseInfiniteAdminSiteBillingMetersQuery.rootKey =
  "AdminSiteBillingMeters.infinite"

useAdminSiteBillingMetersQuery.fetcher = (
  variables: Types.AdminSiteBillingMetersQueryVariables,
  options?: RequestInit["headers"],
) =>
  adminFetcher<
    Types.AdminSiteBillingMetersQuery,
    Types.AdminSiteBillingMetersQueryVariables
  >(AdminSiteBillingMetersDocument, variables, options)
